import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFeatureFlags } from '../contexts/FeatureFlagsContext';
import { openImpersonationWindow } from '../utils/impersonation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { MobileSectionMenu } from '../components/shared/MobileSectionMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { PersonAvatar } from '../components/people/PersonAvatar';
import { LogbookQuickRange } from '../components/fleet/LogbookQuickRange';
import { ManagePeopleGroupsModal } from '../components/people/ManagePeopleGroupsModal';
import { UnmatchedScansModal } from '../components/people/UnmatchedScansModal';
import { resolveGroupTextColor } from '../components/shared/groupColors';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { PaginationFooter, usePersistedPageSize } from '../components/shared/PaginationFooter';
import { useUrlPage, useUrlPageSize, useSetUrlParams, useDebouncedValue, useSkipFirstEffect } from '../components/shared/useUrlPagination';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { formatDT, formatDuration, formatDistance, getFileTimestamp, formatPlainDate } from '../utils/timezone';
import { logReportExport } from '../utils/auditExport';
import { OCCUPANCY_BADGE_CLS } from '../utils/occupancy';
import { fmtCoords, tripLocMapsUrl, roleLabel } from '../utils/tripExport';
import { exportEntryCsv, exportEntryPdf, exportAllEntriesCsv, exportAllEntriesPdf, buildAllEntriesPdf } from '../utils/personLogExport';
import { exportObsPointCsv, exportObsPointPdf, exportAllObsCsv, exportAllObsPdf, buildAllObsPdf } from '../utils/beaconHistoryExport';
import { EmailReportModal } from '../components/EmailReportModal';
import { DriverCorrectionsTab } from '../components/people/DriverCorrectionsTab';
import {
  Plus,
  Users,
  User,
  UserCircle,
  UserPlus,
  UserMinus,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Phone,
  Mail,
  MapPin,
  Car,
  Pencil,
  Trash2,
  Upload,
  Download,
  Search,
  Shield,
  Loader2,
  TrendingUp,
  TrendingDown,
  Activity,
  Clock,
  BookOpen,
  AlertTriangle,
  CheckCircle,
  IdCard,
  Briefcase,
  UserCheck,
  RefreshCw,
  ScanLine,
  Copy,
  MessageSquare,
  KeyRound,
  CreditCard,
  ChevronDown,
  ChevronRight,
  Layers,
  Link2,
  FileText,
  FileSpreadsheet,
  X,
  Bluetooth,
  ExternalLink,
  Route,
  Ban,
  UserRound,
  Calendar as CalendarIcon,
  Paperclip,
  Settings2,
  MoreVertical,
  Camera,
  QrCode,
  Sparkles,
  Archive,
} from 'lucide-react';
import { PersonAttachments, AttachmentsViewerDialog, worstAttachmentExpiry } from '../components/shared/Attachments';
import { PhotoCropDialog } from '../components/fleet/PhotoCropDialog';
import { useEmailAvailability, EmailAvailabilityHint } from '../components/shared/EmailAvailability';
import { PersonProfileDrawer, licenseExpiryInfo, behaviourScoreCls } from '../components/people/PersonProfileDrawer';
import { useIsMobileOrWebview } from '../hooks/useIsMobileOrWebview';
import PeopleImportWizard, { downloadPeopleTemplateXlsx, downloadPeopleTemplateCsv } from '../components/wizards/PeopleImportWizard';
import { PersonInviteQrModal } from '../components/people/PersonInviteQrModal';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';

// Status colors
const statusColors = {
  active: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
  inactive: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/30 dark:text-slate-400',
  suspended: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
};

const rfidStatusColors = {
  available: 'bg-slate-100 text-slate-700 border-slate-200 dark:bg-slate-800/30 dark:text-slate-400',
  assigned: 'bg-green-100 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-400',
  disabled: 'bg-red-100 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-400',
};

// API path segment per person type (note: staff is /staff, not /staffs).
const PERSON_PATH = { driver: 'drivers', staff: 'staff', passenger: 'passengers' };

// Person Log Book: entries shown per page (matches the Vehicle Log Book).
const ENTRIES_PER_PAGE = 10;
const SIGNAL_BADGE_CLS = {
  strong: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  medium: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  low: 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};
const signalLabel = (s) => (s === 'strong' ? 'Strong' : s === 'medium' ? 'Medium' : s === 'low' ? 'Low' : '');
const logEntryKey = (e, i) => e?.id || `entry-${i}`;
const obsPointKey = (p, i) => `${p?.timestamp || 'obs'}-${i}`;
// Distinct colours per BLE confidence so High / Medium / Low read at a glance.
const confidenceBadgeClass = (conf) => {
  const c = String(conf || 'low').toLowerCase();
  if (c === 'high') return 'bg-green-100 text-green-700 border-green-300 dark:bg-green-950/40 dark:text-green-300 dark:border-green-800';
  if (c === 'medium') return 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-800';
  return 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-800';
};

// Clickable, direction-toggling column header used across the People tables.
const SortHeader = ({ label, sortKey, sort, onSort, testid }) => (
  <button
    type="button"
    onClick={() => onSort(sortKey)}
    className="flex items-center gap-1 -ml-0.5 hover:text-foreground transition-colors"
    data-testid={testid}
  >
    {label}
    {sort.key === sortKey
      ? (sort.dir === 'asc' ? <ArrowUp className="w-3.5 h-3.5" /> : <ArrowDown className="w-3.5 h-3.5" />)
      : <ArrowUpDown className="w-3.5 h-3.5 opacity-40" />}
  </button>
);

const riskColors = {
  low: 'bg-green-100 text-green-700 border-green-200',
  medium: 'bg-amber-100 text-amber-700 border-amber-200',
  high: 'bg-red-100 text-red-700 border-red-200',
};

const beaconDisplayName = (beacon) => beacon?.asset_name || beacon?.name || beacon?.imei || 'Personal ID Beacon';

// License expiry health for driver rows comes from PersonProfileDrawer (shared export).

// Person status selector (Active / Inactive / Suspended) shared by the three dialogs.
const PersonStatusField = ({ value, onChange, testPrefix }) => (
  <div className="space-y-2">
    <Label>Status</Label>
    <Select value={value || 'active'} onValueChange={onChange}>
      <SelectTrigger data-testid={`${testPrefix}-status-select`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="active" data-testid={`${testPrefix}-status-option-active`}>Active</SelectItem>
        <SelectItem value="inactive" data-testid={`${testPrefix}-status-option-inactive`}>Inactive</SelectItem>
        <SelectItem value="suspended" data-testid={`${testPrefix}-status-option-suspended`}>Suspended</SelectItem>
      </SelectContent>
    </Select>
  </div>
);

// Optional link between a person and a REAL user account (end user / sub account).
// Linked people use their existing login — invite/chat-login controls are hidden.
const LinkedAccountField = ({ options, value, onChange, testPrefix }) => (
  <div className="space-y-2">
    <Label>Link to account (optional)</Label>
    <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
      <SelectTrigger data-testid={`${testPrefix}-linked-user-select`}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value="none" data-testid={`${testPrefix}-linked-user-option-none`}>Not linked</SelectItem>
        {options.map((u) => (
          <SelectItem key={u.id} value={u.id} data-testid={`${testPrefix}-linked-user-option-${u.id}`}>
            {(u.full_name || u.email)}{u.email ? ` (${u.email})` : ''}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
    <p className="text-xs text-muted-foreground">Links this person to a real account — they use their existing login, no invite needed.</p>
  </div>
);


// Paperclip button turns red/amber when any document is expired / expiring ≤30d.
const attachmentAlertCls = (attachments) => {
  const w = worstAttachmentExpiry(attachments);
  if (w === 'expired') return 'border-red-300 text-red-600 dark:border-red-800 dark:text-red-400';
  if (w === 'expiring') return 'border-amber-300 text-amber-600 dark:border-amber-800 dark:text-amber-400';
  return '';
};
const attachmentAlertTitle = (attachments) => {
  const w = worstAttachmentExpiry(attachments);
  if (w === 'expired') return 'Documents — a document has expired';
  if (w === 'expiring') return 'Documents — a document expires within 30 days';
  return 'Documents';
};

// Photo field inside the Edit modal (just before Notes) — mirrors the Vehicle Edit
// modal's Vehicle Photo section incl. the drag-and-zoom square crop. Only shown when
// editing an existing person.
const PersonPhotoField = ({ api, personType, person, onChange }) => {
  const [busy, setBusy] = useState(false);
  const [url, setUrl] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const photo = person?.photo || null;
  useEffect(() => {
    if (!photo?.url) { setUrl(null); return undefined; }
    let cancelled = false; let obj = null;
    api.get(photo.url.replace(/^\/api/, ''), { responseType: 'blob' })
      .then((r) => { if (!cancelled) { obj = URL.createObjectURL(r.data); setUrl(obj); } })
      .catch(() => { if (!cancelled) setUrl(null); });
    return () => { cancelled = true; if (obj) URL.revokeObjectURL(obj); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo?.url]);
  if (!person?.id) return null;
  const closeCrop = () => { if (cropSrc) URL.revokeObjectURL(cropSrc); setCropSrc(null); };
  const handleCropped = async (blob) => {
    const fd = new FormData();
    fd.append('file', new File([blob], 'person-photo.jpg', { type: 'image/jpeg' }));
    setBusy(true);
    try {
      const r = await api.post(`/people/${personType}/${person.id}/photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onChange(r.data.photo);
      toast.success('Photo updated');
      closeCrop();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Photo upload failed');
    } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete(`/people/${personType}/${person.id}/photo`);
      onChange(null);
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not remove photo');
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid={`${personType}-photo-field`}>
      <Label>Photo</Label>
      <div className="flex items-center gap-3">
        {url
          ? <img src={url} alt={person.full_name} className="w-16 h-16 rounded-lg object-cover border" data-testid={`${personType}-photo-thumb`} />
          : <div className="w-16 h-16 rounded-lg bg-muted flex items-center justify-center text-muted-foreground" data-testid={`${personType}-photo-empty`}><Camera className="w-5 h-5 opacity-40" /></div>}
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) setCropSrc(URL.createObjectURL(f)); }} data-testid={`${personType}-photo-input`} />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => fileRef.current?.click()} data-testid={`${personType}-photo-upload-button`}>
          <Camera className="w-3.5 h-3.5 mr-1.5" /> {busy ? 'Working…' : (photo ? 'Change Photo' : 'Upload Photo')}
        </Button>
        {photo && (
          <Button type="button" size="sm" variant="ghost" className="text-red-600" disabled={busy} onClick={remove} data-testid={`${personType}-photo-remove-button`}>Remove</Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">Saved immediately — shown on the person's dashboard and in lists.</p>
      <PhotoCropDialog open={!!cropSrc} src={cropSrc} busy={busy} onCancel={closeCrop} onCropped={handleCropped} />
    </div>
  );
};

// Clickable paperclip chip (Status column + mobile card) — opens the Documents
// viewer directly. Red/amber when a document is expired / expiring ≤30d.
const DocumentsChip = ({ person, prefix, large, onClick }) => {
  const count = (person.attachments || []).length;
  if (!count) return null;
  const alert = attachmentAlertCls(person.attachments);
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={attachmentAlertTitle(person.attachments)}
      className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-medium transition-colors hover:bg-accent ${large ? 'min-h-[36px] px-2.5 text-sm' : 'px-1.5 py-0.5 text-xs'} ${alert || 'text-muted-foreground border-slate-300 dark:border-slate-600'}`}
      data-testid={`${prefix}-documents-chip-${person.id}`}
    >
      <Paperclip className={large ? 'w-4 h-4' : 'w-3.5 h-3.5'} />
      {count}
    </button>
  );
};

// Driving Behaviour score chip (30d) — green 85+, amber 60–84, red <60.
// Exposure gating: grey "Not rated" under the minimum distance; dashed border = low confidence.
const BehaviourScoreBadge = ({ person, map, prefix, large }) => {
  const b = map?.[person.id];
  if (!b || !b.trips) return null;
  if (b.rating === 'not_rated') {
    return (
      <Badge
        variant="outline"
        className={`${large ? 'text-xs' : 'text-[10px]'} shrink-0 bg-slate-100 text-slate-600 dark:bg-slate-800/50 dark:text-slate-400`}
        title={`Not yet rated — only ${b.km} km driven in 30 days (below the minimum rated distance set in Settings)`}
        data-testid={`${prefix}-behaviour-badge-${person.id}`}
      >
        Not rated
      </Badge>
    );
  }
  return (
    <Badge
      variant="outline"
      className={`${large ? 'text-sm' : 'text-[10px]'} font-bold shrink-0 ${b.rating === 'low_confidence' ? 'border-dashed ' : ''}${behaviourScoreCls(b.score)}`}
      title={`Driving Behaviour score (30 days): ${b.total} harsh event${b.total === 1 ? '' : 's'} over ${b.km} km across ${b.trips} allocated trip${b.trips === 1 ? '' : 's'}${b.rating === 'low_confidence' ? ' — low confidence (limited distance driven)' : ''}`}
      data-testid={`${prefix}-behaviour-badge-${person.id}`}
    >
      {b.score}
    </Badge>
  );
};

// Tap-friendly tooltip (mobile WebView has no hover): the icon is a Popover
// trigger that opens on tap/click; `title` kept for desktop hover too.
const TapTip = ({ content, title, testid, className, large, children }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className={`inline-flex shrink-0 items-center cursor-pointer ${large ? 'p-1.5 -m-1.5' : ''} ${className || ''}`}
        title={title}
        data-testid={testid}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </button>
    </PopoverTrigger>
    <PopoverContent
      side="top"
      align="start"
      className={`w-auto max-w-[280px] px-3 py-2 ${large ? 'text-sm' : 'text-xs'} z-[10000] shadow-lg border-slate-700 bg-slate-900 text-slate-50 dark:border-slate-300 dark:bg-slate-100 dark:text-slate-900`}
    >
      {content}
    </PopoverContent>
  </Popover>
);

// Row-level expiry warnings (license + documents) collapsed into one small
// amber/red icon beside the person's name with a tooltip listing the issues.
const personRowWarnings = (person) => {
  const issues = [];
  let level = null;
  const lic = licenseExpiryInfo(person.license_expiry);
  if (lic) { issues.push(lic.label); level = /expired/i.test(lic.label) ? 'red' : 'amber'; }
  const doc = worstAttachmentExpiry(person.attachments || []);
  if (doc === 'expired') { issues.push('A document has expired'); level = 'red'; }
  else if (doc === 'expiring') { issues.push('A document expires within 30 days'); level = level || 'amber'; }
  return issues.length ? { issues, level } : null;
};
const RowWarningIcon = ({ person, prefix, large }) => {
  const w = personRowWarnings(person);
  if (!w) return null;
  return (
    <TapTip
      content={<div className="space-y-1">{w.issues.map((i, idx) => <p key={idx}>{i}</p>)}</div>}
      title={w.issues.join(' • ')}
      testid={`${prefix}-row-warning-${person.id}`}
      className={w.level === 'red' ? 'text-red-500' : 'text-amber-500'}
      large={large}
    >
      <AlertTriangle className={large ? 'w-5 h-5' : 'w-4 h-4'} />
    </TapTip>
  );
};

// Compact secondary account state shown INLINE next to the status pill
// (icon + tap/hover tooltip) instead of a second stacked badge.
const LinkedAccountIcon = ({ testid, large }) => (
  <TapTip content="Linked account — uses their existing login" title="Linked account — uses their existing login" testid={testid} large={large}>
    <Link2 className={`${large ? 'w-5 h-5' : 'w-3.5 h-3.5'} text-sky-600 dark:text-sky-400`} />
  </TapTip>
);
const CHAT_STATUS_TIP = {
  pending_invitation: "Sign-up invitation sent — they haven't completed their login setup yet",
  active: 'Signed up — they completed setup and can log in',
  deactivated: "Login disabled — they can't sign in until you reactivate it",
};
const ChatStatusIcon = ({ status, testid, large }) => {
  const tip = CHAT_STATUS_TIP[status];
  if (!tip) return null;
  const cls = large ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <TapTip content={tip} title={tip} testid={testid} large={large}>
      {status === 'pending_invitation' && <Mail className={`${cls} text-amber-600 dark:text-amber-400`} />}
      {status === 'active' && <CheckCircle className={`${cls} text-green-600 dark:text-green-400`} />}
      {status === 'deactivated' && <Ban className={`${cls} text-slate-500 dark:text-slate-400`} />}
    </TapTip>
  );
};

// Avatar fallback colours per person type (shared by table rows + mobile cards).
const PERSON_AVATAR_CLS = {
  driver: 'bg-[#2563eb] text-white',
  staff: 'bg-emerald-500 text-white',
  passenger: 'bg-purple-500 text-white',
};

// Staff role badge (shared by the table row + mobile card).
const StaffRoleBadges = ({ member, large }) => (
  <div className="flex items-center gap-1.5">
    <Badge variant="outline" className={`${large ? 'text-sm' : 'text-xs'} capitalize whitespace-nowrap`}>{member.role || '-'}</Badge>
  </div>
);

// "Driver eligible" chip — lives in the Status icon column, after the signed-up icon.
const DriverEligibleIcon = ({ person, large }) => {
  if (!person.driver_eligible) return null;
  return (
    <TapTip
      content="Driver eligible — can be assigned as a vehicle driver"
      title="Driver eligible"
      testid={`staff-driver-eligible-badge-${person.id}`}
      large={large}
      className={`justify-center rounded-full border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800 ${large ? 'w-7 h-7 !p-0 !m-0' : 'w-5 h-5'}`}
    >
      <Car className={large ? 'w-4 h-4' : 'w-3 h-3'} />
    </TapTip>
  );
};

// Person group selector shared by the three dialogs. Hidden when the tab has no groups.
const PersonGroupField = ({ options, value, onChange, testPrefix }) => {
  if (!options?.length) return null;
  return (
    <div className="space-y-2">
      <Label>Group</Label>
      <Select value={value || 'none'} onValueChange={(v) => onChange(v === 'none' ? '' : v)}>
        <SelectTrigger data-testid={`${testPrefix}-group-select`}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none" data-testid={`${testPrefix}-group-option-none`}>No group</SelectItem>
          {options.map((g) => (
            <SelectItem key={g.id} value={g.id} data-testid={`${testPrefix}-group-option-${g.id}`}>{g.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

// CSV export helpers — richer columns shared by the three people exports.
const personAccountStatus = (p) => p.chat_invite_status || (p.account_enabled ? 'enabled' : '');
const PERSON_EXPORT_EXTRAS = {
  // Name + [IMEI] / label + [card value] so imports can reliably match the asset back.
  personal_beacon: (p) => {
    if (!p.personal_beacon) return '';
    const name = beaconDisplayName(p.personal_beacon);
    const imei = String(p.personal_beacon.imei || '').trim();
    return imei && imei !== name ? `${name} [${imei}]` : name;
  },
  rfid_ibutton_card: (p) => {
    if (!p.rfid_ibutton_card) return '';
    const { label, card_value } = p.rfid_ibutton_card;
    return label && card_value && label !== card_value ? `${label} [${card_value}]` : (card_value || label || '');
  },
  account_status: personAccountStatus,
  group: (p) => p.person_group_name || '',
  // Calendar dates exported in the user's configured date format (General Settings).
  license_expiry: (p) => (p.license_expiry ? formatPlainDate(p.license_expiry) : ''),
  date_of_birth: (p) => (p.date_of_birth ? formatPlainDate(p.date_of_birth) : ''),
  date_started: (p) => (p.date_started ? formatPlainDate(p.date_started) : ''),
};
const personExportValue = (p, h) => (PERSON_EXPORT_EXTRAS[h] ? PERSON_EXPORT_EXTRAS[h](p) : (p[h] ?? ''));
// Phone-like columns must survive Excel as text (keeps the leading "+" / zeros).
// Import dropdown — consistent with Maintenance > Schedules > Import (wizard + templates).
const PeopleImportDropdown = ({ type, onOpenWizard }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button variant="outline" size="sm" data-testid={`${type}-import-btn`}>
        <Download className="w-4 h-4 mr-1" /> Import <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-56">
      <DropdownMenuLabel className="text-xs">Import options</DropdownMenuLabel>
      <DropdownMenuItem onSelect={onOpenWizard} data-testid={`${type}-import-open-wizard`}>
        <Sparkles className="w-4 h-4 mr-2 text-[#2563eb]" />
        <div className="flex-1 min-w-0"><p className="text-sm font-medium leading-tight">Import Wizard</p><p className="text-[11px] text-muted-foreground leading-tight">Preview, edit &amp; validate before import</p></div>
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      <DropdownMenuLabel className="text-[10px] text-muted-foreground pt-1 pb-0.5">Templates</DropdownMenuLabel>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); downloadPeopleTemplateXlsx(type); }} data-testid={`${type}-import-template-xlsx`}>
        <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" /> Excel-safe template (.xlsx)
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={(e) => { e.preventDefault(); downloadPeopleTemplateCsv(type); }} data-testid={`${type}-import-template-csv`}>
        <FileSpreadsheet className="w-4 h-4 mr-2" /> CSV template (.csv)
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);

const PEOPLE_EXPORT_TEXT_COLS = ['phone', 'emergency_contact_phone'];
const peopleCsvCell = (p, h) => {
  const s = `${personExportValue(p, h)}`;
  if (PEOPLE_EXPORT_TEXT_COLS.includes(h) && s) return `="${s.replace(/"/g, '""')}"`;
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};
const personExportRow = (p, headers) => headers.map((h) => peopleCsvCell(p, h)).join(',');
const PEOPLE_EXPORT_HEADERS = {
  drivers: ['employee_id', 'full_name', 'email', 'phone', 'license_number', 'license_expiry', 'address', 'date_of_birth', 'status', 'group',
    'date_started', 'emergency_contact_name', 'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
  staff: ['employee_id', 'full_name', 'email', 'phone', 'department', 'role', 'driver_eligible', 'license_number', 'license_expiry',
    'address', 'date_of_birth', 'status', 'group', 'date_started', 'emergency_contact_name', 'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
  passengers: ['passenger_id', 'full_name', 'email', 'phone', 'address', 'date_of_birth', 'status', 'group',
    'emergency_contact_name', 'emergency_contact_phone', 'notes', 'personal_beacon', 'rfid_ibutton_card', 'account_status'],
};
// Excel-safe export: real .xlsx with phone columns forced to text (mirrors the Users export).
const buildPeopleWorkbook = (rows, headers, sheetName) => {
  const aoa = [headers, ...rows.map((p) => headers.map((h) => `${personExportValue(p, h)}`))];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = headers.map((h) => ({ wch: Math.min(Math.max(h.length + 2, 12), 30) }));
  const textIdx = headers.map((h, i) => (PEOPLE_EXPORT_TEXT_COLS.includes(h) ? i : -1)).filter((i) => i >= 0);
  for (let r = 1; r < aoa.length; r++) {
    textIdx.forEach((c) => {
      const addr = XLSX.utils.encode_cell({ r, c });
      if (ws[addr]) { ws[addr].t = 's'; ws[addr].z = '@'; ws[addr].v = String(ws[addr].v ?? ''); }
    });
  }
  ws['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return wb;
};

const buildPeopleXlsx = (rows, headers, sheetName, fname) => {
  XLSX.writeFile(buildPeopleWorkbook(rows, headers, sheetName), fname);
};

// Required-field validation shared by the create AND edit handlers.
const driverFormError = (f) => {
  if (!f.full_name || !f.email || !f.license_number || !f.license_expiry) return 'Please fill in required fields (Name, Email, License Number, License Expiry)';
  return null;
};
const staffFormError = (f) => {
  if (!f.full_name || !f.email || !f.department) return 'Please fill in required fields (Name, Email, Department)';
  if (f.driver_eligible && (!f.license_number || !f.license_expiry)) return 'License Number and License Expiry are required when "Eligible to drive" is ticked';
  return null;
};
const passengerFormError = (f) => {
  if (!f.full_name || !f.phone) return 'Please fill in required fields (Name, Phone)';
  return null;
};

// Solid "Driving / In vehicle" pill — same style as the Person Dashboard's
// Current vehicle badge. Rendered after the beacon/RFID identity tags.
// Clicking it opens that vehicle's dashboard (/fleet?qv=<deviceId>).
const CurrentVehiclePill = ({ person, prefix, large, inline }) => {
  const navigate = useNavigate();
  const b = person.current_binding;
  if (!b) return null;
  const btn = (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); if (b.vehicle_device_id) navigate(`/fleet?qv=${b.vehicle_device_id}`); }}
      className={`inline-flex w-fit items-center gap-1.5 whitespace-nowrap rounded-full ${inline ? 'px-2 py-0.5' : 'px-3 py-1'} ${large ? 'text-xs' : 'text-[11px]'} font-semibold text-white transition-colors ${b.role === 'driving' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-emerald-600 hover:bg-emerald-700'}`}
      title={`${b.role === 'driving' ? 'Driving' : 'In'} ${b.vehicle_name || 'vehicle'} since ${b.since ? new Date(b.since).toLocaleString() : 'unknown'}${b.vehicle_device_id ? ' — open Vehicle Dashboard' : ''}`}
      data-testid={`${prefix}-currently-in-${person.id}`}
    >
      <Car className="w-3 h-3" /> {b.role === 'driving' ? 'Driving' : 'In'} {b.vehicle_name || 'vehicle'}
    </button>
  );
  return inline ? btn : <div className="mt-1">{btn}</div>;
};

// Shared identity tags rendered under a person's name on the Drivers / Staff /
// Passengers tables. Shows the assigned BLE Personal ID Beacon and/or the
// assigned RFID / iButton card (both when a person has both).
const PersonIdentityTags = ({ person, prefix, compact, large }) => (
  compact ? (
    <>
      {(person.personal_beacon || person.rfid_ibutton_card || person.current_binding) && (
        <p className={`${large ? 'text-sm' : 'text-xs'} flex items-center gap-1 flex-wrap`}>
          {person.personal_beacon && (
            <span className="text-teal-700 dark:text-teal-300 inline-flex items-center" data-testid={`${prefix}-personal-beacon-${person.id}`}>
              <Bluetooth className={`${large ? 'w-4 h-4' : 'w-3 h-3'} inline mr-1 -mt-px`} />{beaconDisplayName(person.personal_beacon)}
            </span>
          )}
          {person.personal_beacon?.low_battery === true && (
            <TapTip
              content={`Beacon battery low${person.personal_beacon.battery_voltage_mv != null ? ` (${person.personal_beacon.battery_voltage_mv} mV)` : ''}`}
              title={`Beacon battery low${person.personal_beacon.battery_voltage_mv != null ? ` (${person.personal_beacon.battery_voltage_mv} mV)` : ''}`}
              testid={`${prefix}-beacon-low-battery-${person.id}`}
              className="text-amber-600 dark:text-amber-400"
              large={large}
            >
              <AlertTriangle className={large ? 'w-4 h-4' : 'w-3 h-3'} />
            </TapTip>
          )}
          {person.personal_beacon && person.rfid_ibutton_card && <span className="text-muted-foreground">·</span>}
          {person.rfid_ibutton_card && (
            <span className="text-[#2563eb] dark:text-blue-300 inline-flex items-center" data-testid={`${prefix}-rfid-card-${person.id}`}>
              <CreditCard className={`${large ? 'w-4 h-4' : 'w-3 h-3'} inline mr-1 -mt-px`} />{person.rfid_ibutton_card.label || person.rfid_ibutton_card.card_value}
            </span>
          )}
          <CurrentVehiclePill person={person} prefix={prefix} large={large} inline />
        </p>
      )}
    </>
  ) : (
  <>
    {person.personal_beacon && (
      <p className="text-xs text-teal-700 dark:text-teal-300" data-testid={`${prefix}-personal-beacon-${person.id}`}>
        <Bluetooth className="w-3 h-3 inline mr-1 -mt-px" />{beaconDisplayName(person.personal_beacon)}
      </p>
    )}
    {person.personal_beacon?.low_battery === true && (
      <p className="text-xs text-amber-600 dark:text-amber-400" data-testid={`${prefix}-beacon-low-battery-${person.id}`}>
        <AlertTriangle className="w-3 h-3 inline mr-1 -mt-px" />Beacon battery low{person.personal_beacon.battery_voltage_mv != null ? ` (${person.personal_beacon.battery_voltage_mv} mV)` : ''}
      </p>
    )}
    {person.rfid_ibutton_card && (
      <p className="text-xs text-[#2563eb] dark:text-blue-300" data-testid={`${prefix}-rfid-card-${person.id}`}>
        <CreditCard className="w-3 h-3 inline mr-1 -mt-px" />{person.rfid_ibutton_card.label || person.rfid_ibutton_card.card_value}
      </p>
    )}
    <CurrentVehiclePill person={person} prefix={prefix} />
  </>
  )
);

// Searchable person dropdown — same style as the Event Videos device selector:
// a click-to-open popover with a search box on top (plain-text filtering) and
// checkbox-style rows. z-[10000] keeps it above the Dialog overlay.
// Selection controls shown in each tab header once rows are ticked — mirrors
// the Driver Corrections pattern: count + server-wide "Select All" + Clear.
const PeopleSelectionBar = ({ count, total, busy, onToggleAll, onClear, prefix }) => {
  // Always visible: shows "0 selected" when nothing is ticked; Select All /
  // Clear appear once at least one row is selected (app-wide standard).
  return (
    <div className="flex items-center gap-3 text-sm text-muted-foreground px-1 h-6 -my-1.5 overflow-hidden" data-testid={`${prefix}-selection-bar`}>
      <span data-testid={`${prefix}-selection-count`}>{count} selected</span>
      {count > 0 && (<>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" disabled={busy} onClick={onToggleAll} data-testid={`${prefix}-select-all-toggle`}>
        {busy ? 'Selecting…' : (count >= total ? 'Deselect All' : 'Select All')}
      </Button>
      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={onClear} data-testid={`${prefix}-selection-clear`}>Clear</Button>
      </>)}
    </div>
  );
};

const PersonPicker = ({ people, value, onChange, emptyLabel = 'No people found', placeholder = 'Select a person', testidPrefix }) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const inputRef = React.useRef(null);
  const selected = people.find((p) => p.id === value);
  const q = query.trim().toLowerCase();
  const filtered = !q ? people : people.filter((p) => (p.full_name || p.name || '').toLowerCase().includes(q));
  return (
    <Popover modal={true} open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQuery(''); }}>
      <div className="flex gap-2">
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            className="h-8 min-w-0 flex-1 justify-between px-3 text-left font-normal"
            data-testid={`${testidPrefix}-trigger`}
          >
            <span className={selected ? 'min-w-0 truncate' : 'min-w-0 truncate text-muted-foreground'}>
              {selected ? (selected.full_name || selected.name) : placeholder}
            </span>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        {selected && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="h-9 w-9 shrink-0"
            onClick={() => onChange('')}
            title="Clear selection"
            aria-label="Clear selection"
            data-testid={`${testidPrefix}-clear`}
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
      <PopoverContent className="z-[10000] w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <div className="border-b bg-popover p-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people..."
              className="h-8 pl-7 pr-7 text-xs"
              data-testid={`${testidPrefix}-search`}
            />
            {query && (
              <button
                type="button"
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setQuery(''); inputRef.current?.focus(); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                data-testid={`${testidPrefix}-search-clear`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          <div className="mt-1 text-[10px] text-muted-foreground" data-testid={`${testidPrefix}-count`}>
            {filtered.length}/{people.length}
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1" data-testid={`${testidPrefix}-results`}>
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-center text-xs text-muted-foreground" data-testid={`${testidPrefix}-empty`}>
              {emptyLabel}
            </div>
          ) : filtered.map((p) => {
            const label = p.full_name || p.name || '';
            const isSel = value === p.id;
            return (
              <button
                key={p.id}
                type="button"
                role="checkbox"
                aria-checked={isSel}
                onClick={() => { onChange(isSel ? '' : p.id); setOpen(false); setQuery(''); }}
                className={`flex w-full min-w-0 items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground ${isSel ? 'bg-accent text-accent-foreground' : ''}`}
                data-testid={`${testidPrefix}-option-${p.id}`}
              >
                <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border text-[10px] ${isSel ? 'border-blue-600 bg-blue-600 text-white' : 'border-muted-foreground/40'}`}>{isSel ? '✓' : ''}</span>
                <span className="min-w-0 truncate">{label}</span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
};

const formatObservedTime = (value) => {
  if (!value) return 'Not seen';
  const formatted = formatDT(value);
  return formatted === '-' ? 'Not seen' : formatted;
};

const formatBeaconBattery = (item) => {
  const mv = item?.battery_voltage_mv;
  const low = item?.low_battery;
  if (mv == null && low == null) return 'Not reported';
  return `${mv != null ? `${mv} mV` : 'Voltage unknown'}${low === true ? ' · Low' : low === false ? ' · OK' : ''}`;
};

const freshnessLabel = (value) => {
  if (!value) return 'Not seen';
  const diffMs = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diffMs)) return 'Seen';
  const minutes = diffMs / 60000;
  if (minutes < 5) return 'Seen just now';
  if (minutes < 60) return `Seen ${Math.round(minutes)} min ago`;
  if (minutes < 24 * 60) return 'Seen today';
  return 'Stale';
};

const buildHistoryQuery = (filters) => {
  const params = new URLSearchParams();
  params.set('limit', String(filters.limit || 200));
  if (filters.start_time) params.set('start_time', new Date(filters.start_time).toISOString());
  if (filters.end_time) params.set('end_time', new Date(filters.end_time).toISOString());
  if (filters.detector_imei) params.set('detector_imei', filters.detector_imei);
  if (filters.confidence && filters.confidence !== 'all') params.set('confidence', filters.confidence);
  if (filters.search) params.set('search', filters.search);
  if (filters.min_rssi) params.set('min_rssi', filters.min_rssi);
  if (filters.max_rssi) params.set('max_rssi', filters.max_rssi);
  return params.toString();
};

const buildLogbookQuery = (filters, exportType = '') => {
  const params = new URLSearchParams();
  params.set('limit', String(filters.limit || 200));
  if (filters.start_time) params.set('start_time', new Date(filters.start_time).toISOString());
  if (filters.end_time) params.set('end_time', new Date(filters.end_time).toISOString());
  if (filters.source && filters.source !== 'all') params.set('source', filters.source);
  if (filters.event_type && filters.event_type !== 'all') params.set('event_type', filters.event_type);
  if (filters.signal && filters.signal !== 'all') params.set('signal', filters.signal);
  if (filters.person_type && filters.person_type !== 'all') params.set('person_type', filters.person_type);
  if (filters.search) params.set('search', filters.search);
  if (exportType) params.set('export', exportType);
  return params.toString();
};

const PersonalBeaconField = ({ value, onChange, beacons, currentPersonId, personType, testPrefix }) => {
  const options = (beacons || []).filter((beacon) => {
    if (!beacon) return false;
    if (!beacon.personal_beacon_assigned_to) return true;
    return beacon.personal_beacon_assigned_to.type === personType && beacon.personal_beacon_assigned_to.id === currentPersonId;
  });
  return (
    <div className="space-y-2 rounded-lg border border-teal-200 bg-teal-50/60 p-3 dark:border-teal-900 dark:bg-teal-950/20" data-testid={`${testPrefix}-personal-beacon-field`}>
      <Label className="flex items-center gap-2"><Bluetooth className="w-4 h-4 text-teal-600" /> Personal ID Beacon</Label>
      <Select value={value || '__none'} onValueChange={(v) => onChange(v === '__none' ? '' : v)}>
        <SelectTrigger data-testid={`${testPrefix}-personal-beacon-select`}>
          <SelectValue placeholder="Select allocated Personal ID Beacon" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__none" data-testid={`${testPrefix}-personal-beacon-option-none`}>No Personal ID Beacon</SelectItem>
          {options.map((beacon) => (
            <SelectItem key={beacon.id} value={beacon.id} data-testid={`${testPrefix}-personal-beacon-option-${beacon.id}`}>
              {beaconDisplayName(beacon)} · {beacon.beacon_type || 'BLE'}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <p className="text-[11px] text-muted-foreground">
        Only BLE Beacons allocated to this account and marked as Personal ID Beacon are listed.
      </p>
      {options.length === 0 && (
        <p className="text-[11px] text-amber-700" data-testid={`${testPrefix}-personal-beacon-empty`}>
          No unassigned Personal ID Beacons are available.
        </p>
      )}
    </div>
  );
};

const CredentialFields = ({ form, setForm, testPrefix, cards = [] }) => (
  <div className="rounded-lg border p-3 space-y-3" data-testid={`${testPrefix}-credential-fields`}>
    <Label className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-[#2563eb]" /> RFID / iButton Card</Label>
    <div className="space-y-1">
      <Label>Assigned Card</Label>
      <Select value={form.rfid_ibutton_card_id || 'none'} onValueChange={(v) => setForm(prev => ({ ...prev, rfid_ibutton_card_id: v === 'none' ? '' : v }))}>
        <SelectTrigger data-testid={`${testPrefix}-rfid-card-select`}><SelectValue placeholder="Select RFID / iButton card" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">No card assigned</SelectItem>
          {cards.map(card => (
            <SelectItem key={card.id} value={card.id}>
              {(card.label || card.card_value)} · {card.card_value}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <p className="text-[11px] text-muted-foreground">
      Cards are managed in the RFID / iButton Cards tab and are tenant isolated.
    </p>
  </div>
);

// RFID / iButton Cards now render inline in the People page, consistent with the
// Drivers / Staff / Passengers subpages — see the `activeTab === 'rfid-cards'` block.

// Shown inside the "Create Account" row. Enabling it sends the SAME activation
// invitation used for Admin-created users: the person sets their own password and
// accepts the Terms & Privacy, then lands on their contact page. The invitation is
// sent when you Save (Create/Update). Operators pick delivery via the Email / SMS
// tick-boxes (both OFF by default — leave both off to just get a copyable link); a
// "Resend invitation" button re-mints the link for a still-pending invite.
const ChatInviteField = ({ form, prefix, inviteStatus, isNotificationChannelActive, emailChecked, smsChecked, onToggleEmail, onToggleSms, onResend, resending, onShowQr, onActiveToggle, statusBusy, pushEnabled, onPushToggle, pushBusy }) => {
  const hasEmail = !!((form.email || '').trim());
  const hasPhone = !!((form.phone || '').trim());
  const hasContact = hasEmail || hasPhone;
  const emailActive = hasEmail && isNotificationChannelActive('notification_email');
  const smsActive = hasPhone && isNotificationChannelActive('notification_sms');
  const isActive = inviteStatus === 'active';
  const isDeactivated = inviteStatus === 'deactivated';
  const hasAccount = isActive || isDeactivated;

  return (
    <div className="mt-2 space-y-2" data-testid={`${prefix}-chat-invite-block`}>
      {inviteStatus && (
        <div className="flex items-center gap-2 text-xs">
          <span className="text-muted-foreground">Account login:</span>
          <span
            className={`px-2 py-0.5 rounded-full font-medium ${isActive ? 'bg-green-100 text-green-700' : isDeactivated ? 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400' : 'bg-amber-100 text-amber-700'}`}
            data-testid={`${prefix}-chat-invite-status`}
          >
            {isActive ? 'Active' : isDeactivated ? 'Deactivated' : 'Invitation pending'}
          </span>
        </div>
      )}

      {/* "Account active" switch — activate/deactivate the login in place, without
          having to untick "Create Account" (which removes the whole account). */}
      {hasAccount && onActiveToggle && (
        <div className="space-y-1" data-testid={`${prefix}-account-active-row`}>
          <div className="flex items-center gap-2">
            <Switch
              checked={isActive}
              disabled={statusBusy}
              onCheckedChange={(v) => onActiveToggle(!!v)}
              data-testid={`${prefix}-account-active-switch`}
            />
            <span className="text-xs font-medium" data-testid={`${prefix}-account-active-label`}>
              {isActive ? 'Account active' : 'Account deactivated'}
            </span>
            {statusBusy && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {isActive
              ? 'Turn off to block sign-in without removing the account.'
              : 'Turn on to reactivate — their password is kept, no new invitation is sent.'}
          </p>
        </div>
      )}

      {/* "Push Notifications" switch — the per-person layer-2 privilege that
          `/api/push/subscribe` gates on. Turning this OFF makes the person's
          browser/phone unable to register for pushes (returns 403); ON restores it.
          Their personal preference toggle in their own Settings still applies. */}
      {hasAccount && onPushToggle && (
        <div className="space-y-1" data-testid={`${prefix}-account-push-row`}>
          <div className="flex items-center gap-2">
            <Switch
              checked={!!pushEnabled}
              disabled={pushBusy || !isActive}
              onCheckedChange={(v) => onPushToggle(!!v)}
              data-testid={`${prefix}-account-push-switch`}
            />
            <span className="text-xs font-medium" data-testid={`${prefix}-account-push-label`}>
              {pushEnabled ? 'Push Notifications: On' : 'Push Notifications: Off'}
            </span>
            {pushBusy && <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />}
          </div>
          <p className="text-[11px] text-muted-foreground">
            {!isActive
              ? 'Reactivate the account to change this.'
              : pushEnabled
                ? 'This person can register their phone/browser to receive push alerts.'
                : 'Turn on to let this person subscribe their phone/browser to push alerts.'}
          </p>
        </div>
      )}

      {!hasContact && !hasAccount && (
        <p className="text-[11px] text-amber-600" data-testid={`${prefix}-chat-invite-nocontact`}>
          Add an email or mobile number above so we can send the activation invitation.
        </p>
      )}

      {/* Delivery channels — only while no account exists yet (fresh or pending invite). Each is
          disabled unless that channel can actually deliver (contact on file + channel on). */}
      {!hasAccount && (
        <div className="space-y-1.5" data-testid={`${prefix}-chat-invite-channels`}>
          <span className="block text-[11px] text-muted-foreground">Send invitation via (optional):</span>
          <div className="flex items-center gap-4">
            <label className={`flex items-center gap-1.5 text-xs ${emailActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
              title={emailActive ? '' : 'Add an email above and turn on your Email notification channel in Settings'}>
              <Checkbox checked={emailChecked && emailActive} disabled={!emailActive}
                onCheckedChange={(v) => onToggleEmail(!!v)} data-testid={`${prefix}-chat-invite-channel-email`} />
              Email
            </label>
            <label className={`flex items-center gap-1.5 text-xs ${smsActive ? 'cursor-pointer' : 'opacity-40 cursor-not-allowed'}`}
              title={smsActive ? '' : 'Add a mobile number above and turn on your SMS notification channel in Settings'}>
              <Checkbox checked={smsChecked && smsActive} disabled={!smsActive}
                onCheckedChange={(v) => onToggleSms(!!v)} data-testid={`${prefix}-chat-invite-channel-sms`} />
              SMS
            </label>
          </div>
        </div>
      )}

      {isActive ? (
        <p className="text-[11px] text-muted-foreground">This person has activated their login.</p>
      ) : isDeactivated ? (
        !onActiveToggle && (
          <p className="text-[11px] text-muted-foreground" data-testid={`${prefix}-chat-invite-help`}>
            This person&apos;s login is deactivated. Saving with &quot;Create Account&quot; ticked reactivates it — their password is kept, no new invitation is sent.
          </p>
        )
      ) : (
        <p className="text-[11px] text-muted-foreground" data-testid={`${prefix}-chat-invite-help`}>
          {inviteStatus
            ? 'An invitation is pending. Use "Resend invitation" to re-send (tick a channel) or just copy the link.'
            : "On save we'll create the invitation. Tick a channel to deliver it, or leave both off and copy the link."}
        </p>
      )}

      {/* Resend invitation — only for a still-pending invite; never greyed by channel
          availability so the link can always be copied from the result dialog. */}
      {inviteStatus === 'pending_invitation' && onResend && (
        <Button
          type="button" size="sm" variant="outline" className="h-7 text-xs mt-1"
          onClick={onResend} disabled={resending}
          data-testid={`${prefix}-resend-invite-btn`}
        >
          {resending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Resend invitation
        </Button>
      )}
      {/* Activation QR — viewable any time until the account is activated. */}
      {inviteStatus === 'pending_invitation' && onShowQr && (
        <Button
          type="button" size="sm" variant="outline" className="h-7 text-xs mt-1 ml-2"
          onClick={onShowQr}
          data-testid={`${prefix}-invite-qr-btn`}
        >
          <QrCode className="w-3 h-3 mr-1" />
          Activation QR
        </Button>
      )}
    </div>
  );
};


// "Create Account" (login lifecycle) + "Allow Chat" (Chat page privilege) section
// shared by the Driver / Staff / Passenger dialogs.
const AccountAccessSection = ({ form, prefix, personLabel, inviteStatus,
  onAccountToggle, onChatToggle, onResend, resending, onShowQr, isNotificationChannelActive,
  emailChecked, smsChecked, onToggleEmail, onToggleSms, onActiveToggle, statusBusy,
  pushEnabled, onPushToggle, pushBusy }) => (
  <div className="rounded-lg border p-3 bg-muted/30 space-y-3" data-testid={`${prefix}-account-section`}>
    <div className="flex items-start gap-3" data-testid={`${prefix}-create-account-row`}>
      <Checkbox
        id={`${prefix}-create-account`}
        checked={!!form.account_enabled}
        onCheckedChange={onAccountToggle}
        className="mt-0.5"
        data-testid={`${prefix}-create-account-checkbox`}
      />
      <div className="space-y-1 flex-1">
        <Label htmlFor={`${prefix}-create-account`} className="cursor-pointer">Create Account</Label>
        <p className="text-xs text-muted-foreground">
          Sends this {personLabel} an invitation to set up their own login (they choose their password and accept the Terms &amp; Privacy). They get their contact page plus any pages you allow below.
        </p>
        {form.account_enabled && (
          <ChatInviteField
            form={form}
            prefix={prefix}
            inviteStatus={inviteStatus}
            isNotificationChannelActive={isNotificationChannelActive}
            emailChecked={emailChecked}
            smsChecked={smsChecked}
            onToggleEmail={onToggleEmail}
            onToggleSms={onToggleSms}
            onResend={onResend}
            resending={resending}
            onShowQr={onShowQr}
            onActiveToggle={onActiveToggle}
            statusBusy={statusBusy}
            pushEnabled={pushEnabled}
            onPushToggle={onPushToggle}
            pushBusy={pushBusy}
          />
        )}
      </div>
    </div>
    <div className="flex items-start gap-3 pt-3 border-t" data-testid={`${prefix}-allow-chat-row`}>
      <Checkbox
        id={`${prefix}-allow-chat`}
        checked={!!form.messaging_enabled}
        disabled={!form.account_enabled}
        onCheckedChange={(v) => onChatToggle(!!v)}
        className="mt-0.5"
        data-testid={`${prefix}-allow-chat-checkbox`}
      />
      <div className="space-y-1">
        <Label htmlFor={`${prefix}-allow-chat`} className={form.account_enabled ? 'cursor-pointer' : 'opacity-50'}>Allow Chat</Label>
        <p className="text-xs text-muted-foreground">
          Gives this {personLabel} access to the Chat page. Only your account can chat with them.{!form.account_enabled ? ' Requires "Create Account".' : ''}
        </p>
      </div>
    </div>
  </div>
);

// Clickable header badge that counts pending chat invitations and toggles a
// "show only pending" filter. Count is server-computed (tenant-wide, not page-local).
const PendingInviteFilterBadge = ({ count, active, onToggle, testid }) => {
  const pc = count || 0;
  if (pc === 0 && !active) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${active ? 'bg-amber-500 text-white hover:bg-amber-600' : 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-400 dark:hover:bg-amber-900/50'}`}
      title={active ? 'Showing pending sign-ups — click to clear' : 'Show only people whose sign-up is still pending'}
      data-testid={testid}
    >
      <Mail className="w-3.5 h-3.5" />
      {pc} pending sign-up{pc === 1 ? '' : 's'}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
};


// Clickable header badge counting people with an expired / expiring-in-30-days
// license; toggles the server-side license_expiring filter. Count is server-computed.
const LicenseExpiryFilterBadge = ({ count, active, onToggle, testid }) => {
  const c = count || 0;
  if (c === 0 && !active) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${active ? 'bg-red-500 text-white hover:bg-red-600' : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50'}`}
      title={active ? 'Showing license alerts — click to clear' : 'Show only drivers with an expired or expiring license'}
      data-testid={testid}
    >
      <IdCard className="w-3.5 h-3.5" />
      {active ? 'License alerts' : `${c} license alert${c === 1 ? '' : 's'}`}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
};


// Clickable header badge counting people with a document expired / expiring in
// 30 days; toggles the server-side docs_expiring filter. Count is server-computed.
const DocsExpiryFilterBadge = ({ count, active, onToggle, testid }) => {
  const c = count || 0;
  if (c === 0 && !active) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${active ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-orange-100 text-orange-700 hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-900/50'}`}
      title={active ? 'Showing document alerts — click to clear' : 'Show only people with an expired or expiring document'}
      data-testid={testid}
    >
      <Paperclip className="w-3.5 h-3.5" />
      {active ? 'Document alerts' : `${c} document alert${c === 1 ? '' : 's'}`}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
};


// Clickable header badge counting people (tenant-wide, server-counted) who are
// inside / driving a vehicle right now; toggles the server-side currently_in filter.
const CurrentlyInFilterBadge = ({ count, active, onToggle, testid }) => {
  if (!count && !active) return null;
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`inline-flex items-center gap-1.5 text-xs font-medium rounded-full px-2.5 py-1 transition-colors ${active ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-400 dark:hover:bg-emerald-900/50'}`}
      title={active ? 'Showing people currently in a vehicle — click to clear' : 'Show only people currently in or driving a vehicle'}
      data-testid={testid}
    >
      <Car className="w-3.5 h-3.5" />
      {active ? 'Currently in' : `${count} currently in`}
      {active && <X className="w-3 h-3 ml-0.5" />}
    </button>
  );
};


// Bulk actions menu shown when rows are selected: set status / resend sign-up invites.
const BulkActionsMenu = ({ count, onStatus, onResend, groups = [], onMoveToGroup, testPrefix }) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button size="sm" variant="outline" data-testid={`${testPrefix}-bulk-actions-btn`}>
        Bulk actions ({count})
        <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="w-64">
      <DropdownMenuLabel className="text-xs">Set status for {count} selected</DropdownMenuLabel>
      <DropdownMenuItem onSelect={() => onStatus('active')} data-testid={`${testPrefix}-bulk-set-active`}>
        <CheckCircle className="w-4 h-4 mr-2 text-green-600" /> Set Active
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => onStatus('inactive')} data-testid={`${testPrefix}-bulk-set-inactive`}>
        <Ban className="w-4 h-4 mr-2 text-slate-500" /> Set Inactive
      </DropdownMenuItem>
      <DropdownMenuItem onSelect={() => onStatus('suspended')} data-testid={`${testPrefix}-bulk-set-suspended`}>
        <AlertTriangle className="w-4 h-4 mr-2 text-amber-600" /> Set Suspended
      </DropdownMenuItem>
      <DropdownMenuSeparator />
      {groups.length > 0 && onMoveToGroup && (
        <>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid={`${testPrefix}-bulk-move-to-group`}>
              <Layers className="w-4 h-4 mr-2 text-[#2563eb]" /> Move to group
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56">
              {groups.map((g) => (
                <DropdownMenuItem key={g.id} onSelect={() => onMoveToGroup(g.id)} data-testid={`${testPrefix}-bulk-group-${g.id}`}>
                  <Layers className="w-4 h-4 mr-2" style={{ color: g.color || undefined }} /> {g.name}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => onMoveToGroup(null)} data-testid={`${testPrefix}-bulk-remove-from-group`}>
                <X className="w-4 h-4 mr-2 text-slate-500" /> Remove from group
              </DropdownMenuItem>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
        </>
      )}
      <DropdownMenuItem onSelect={onResend} data-testid={`${testPrefix}-bulk-resend-invites`}>
        <RefreshCw className="w-4 h-4 mr-2 text-[#2563eb]" /> Resend sign-up invitations
      </DropdownMenuItem>
    </DropdownMenuContent>
  </DropdownMenu>
);


// Default sort applied to each People tab until the user picks one.
const PEOPLE_DEFAULT_SORT = { key: 'name', dir: 'asc' };


export const People = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const { isSubpageEnabled, isNotificationChannelActive } = useFeatureFlags();
  const hasGreenDriving = isSubpageEnabled('fleet_green_driving');
  
  // Check which subpages are enabled
  const hasDrivers = isSubpageEnabled('people_drivers');
  const hasStaff = isSubpageEnabled('people_staff');
  const isMobileOrWebview = useIsMobileOrWebview();
  const hasPassengers = isSubpageEnabled('people_passengers');
  const hasRfidCards = isSubpageEnabled('people_rfid_ibutton_cards');
  const hasDriverCorrections = isSubpageEnabled('people_driver_corrections');
  const [personalBeacons, setPersonalBeacons] = useState([]);
  // Full inventories (modal assignment dropdowns + tab gating) — separate from
  // the server-paginated table rows above.
  const [beaconInventory, setBeaconInventory] = useState([]);
  const [cardInventory, setCardInventory] = useState([]);
  const hasPersonalBeacons = beaconInventory.length > 0;
  
  // SECURITY FIX: Return null if no subpages enabled
  const getDefaultTab = useCallback(() => {
    if (hasDrivers) return 'drivers';
    if (hasStaff) return 'staff';
    if (hasPassengers) return 'passengers';
    if (hasRfidCards) return 'rfid-cards';
    if (hasPersonalBeacons) return 'personal-beacons';
    if (hasDriverCorrections) return 'driver-corrections';
    return null;
  }, [hasDrivers, hasStaff, hasPassengers, hasRfidCards, hasPersonalBeacons, hasDriverCorrections]);

  const requestedTab = new URLSearchParams(location.search).get('tab');
  const requestedSearch = new URLSearchParams(location.search).get('search') || '';
  const getRequestedTab = useCallback(() => {
    const allowedTabs = { drivers: hasDrivers, staff: hasStaff, passengers: hasPassengers, 'rfid-cards': hasRfidCards, 'personal-beacons': hasPersonalBeacons, 'driver-corrections': hasDriverCorrections };
    return allowedTabs[requestedTab] ? requestedTab : null;
  }, [hasDrivers, hasStaff, hasPassengers, hasRfidCards, hasPersonalBeacons, hasDriverCorrections, requestedTab]);
  
  const [activeTab, setActiveTab] = useState(() => getRequestedTab() || getPersistedTab() || getDefaultTab());
  const lastAppliedRequestedTabRef = React.useRef(null);

  // Session memory: remember the last sub-tab across navigation & F5 (same sessionStorage-backed
  // approach as Analytics/Media). Only honoured when the remembered tab is still feature-allowed.
  function getPersistedTab() {
    try {
      const t = sessionStorage.getItem('people_subtab');
      const allowedTabs = { drivers: hasDrivers, staff: hasStaff, passengers: hasPassengers, 'rfid-cards': hasRfidCards, 'personal-beacons': hasPersonalBeacons, 'driver-corrections': hasDriverCorrections };
      return t && allowedTabs[t] ? t : null;
    } catch { return null; }
  }
  useEffect(() => {
    if (activeTab) { try { sessionStorage.setItem('people_subtab', activeTab); } catch { /* ignore */ } }
  }, [activeTab]);

  // Apply a URL-requested tab (?tab=...) ONLY when it first appears or actually changes — never
  // on every render. The old logic forced activeTab back to the URL tab whenever they diverged,
  // so a manual tab click was instantly reverted while ?tab= stayed in the URL (the bug that
  // also survived a hard refresh; it only "cleared" when navigating away and back).
  useEffect(() => {
    const requested = getRequestedTab();
    if (requested && requested !== lastAppliedRequestedTabRef.current) {
      lastAppliedRequestedTabRef.current = requested;
      setActiveTab(requested);
    }
  }, [getRequestedTab]);

  // Keep activeTab valid as feature flags load (fall back to the first allowed tab). This does
  // NOT re-apply the URL tab, so the user's manual tab selection is always preserved.
  useEffect(() => {
    const allowedTabs = { drivers: hasDrivers, staff: hasStaff, passengers: hasPassengers, 'rfid-cards': hasRfidCards, 'personal-beacons': hasPersonalBeacons, 'driver-corrections': hasDriverCorrections };
    if (!activeTab || allowedTabs[activeTab] !== true) {
      const defaultTab = getDefaultTab();
      if (defaultTab) {
        setActiveTab(defaultTab);
      }
    }
  }, [activeTab, getDefaultTab, hasDrivers, hasStaff, hasPassengers, hasRfidCards, hasPersonalBeacons, hasDriverCorrections]);
  const [loading, setLoading] = useState(true);
  // Per-tab search terms — each subpage keeps its OWN filter (persisted for the
  // session), so a Drivers search no longer bleeds into Staff/Passengers/etc.
  const [searchByTab, setSearchByTab] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem('people_filters') || '{}').searchByTab;
      return stored && typeof stored === 'object' ? stored : {};
    } catch { return {}; }
  });
  const searchQuery = searchByTab[activeTab] || '';
  const setSearchQuery = useCallback((v) => {
    setSearchByTab((prev) => ({ ...prev, [activeTab]: typeof v === 'function' ? v(prev[activeTab] || '') : v }));
  }, [activeTab]);
  useEffect(() => {
    // Consume-once: ?search seeds the filter then is removed from the URL so a
    // page refresh starts with clean filters.
    if (requestedSearch) {
      setSearchQuery(requestedSearch);
      const p = new URLSearchParams(location.search);
      p.delete('search');
      navigate(`${location.pathname}${p.toString() ? `?${p}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedSearch]);
  const [rfidCards, setRfidCards] = useState([]);
  const [rfidCardForm, setRfidCardForm] = useState({ card_value: '', label: '', status: 'available', notes: '' });
  const [selectedRfidCardIds, setSelectedRfidCardIds] = useState([]);
  const [isRfidDialogOpen, setIsRfidDialogOpen] = useState(false);
  const [unmatchedScans, setUnmatchedScans] = useState([]);
  const [unmatchedModalOpen, setUnmatchedModalOpen] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  useEffect(() => {
    if (!hasDriverCorrections) return;
    api.get('/fleet/driver-candidates?status=pending&limit=1')
      .then((res) => setPendingReviewCount(res.data?.total ?? (res.data?.candidates || []).length))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasDriverCorrections]);
  const [assignCardCredential, setAssignCardCredential] = useState('');
  // Consume-once: ?open_person=<type>:<id> (from document-expiry alerts) opens the
  // person's profile drawer, which shows their Documents section.
  const requestedOpenPerson = new URLSearchParams(location.search).get('open_person') || '';
  useEffect(() => {
    if (!requestedOpenPerson) return;
    const [ptype, pid] = requestedOpenPerson.split(':');
    const path = { driver: 'drivers', staff: 'staff', passenger: 'passengers' }[ptype];
    if (path && pid) {
      api.get(`/${path}/${pid}`)
        .then((res) => setProfileDrawer({ open: true, person: res.data, personType: ptype }))
        .catch(() => toast.error('Person not found'));
    }
    const p = new URLSearchParams(location.search);
    p.delete('open_person');
    navigate(`${location.pathname}${p.toString() ? `?${p}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedOpenPerson]);

  // Consume-once: ?assign_card=<credential> (from scan-alert quick actions) opens the
  // Unmatched Scans modal pre-searched + highlighted on that credential.
  const requestedAssignCard = new URLSearchParams(location.search).get('assign_card') || '';
  useEffect(() => {
    if (requestedAssignCard) {
      setAssignCardCredential(requestedAssignCard);
      setUnmatchedModalOpen(true);
      const p = new URLSearchParams(location.search);
      p.delete('assign_card');
      navigate(`${location.pathname}${p.toString() ? `?${p}` : ''}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedAssignCard]);
  const [selectedRfidCard, setSelectedRfidCard] = useState(null);
  const [assignCardModal, setAssignCardModal] = useState({ open: false, card: null });
  const [assignForm, setAssignForm] = useState({ personType: 'driver', personId: '' });
  // Per-tab sort for the Drivers / Staff / Passengers tables (persisted like search).
  const [sortByTab, setSortByTab] = useState(() => {
    try {
      const stored = JSON.parse(sessionStorage.getItem('people_filters') || '{}').sortByTab;
      return stored && typeof stored === 'object' ? stored : {};
    } catch { return {}; }
  });
  const peopleSort = sortByTab[activeTab] || PEOPLE_DEFAULT_SORT;
  // Persist People per-tab search + sort for the session (restored on return — like Analytics/Media).
  useEffect(() => {
    try { sessionStorage.setItem('people_filters', JSON.stringify({ searchByTab, sortByTab })); } catch { /* ignore */ }
  }, [searchByTab, sortByTab]);
  const [cardSort, setCardSort] = useState({ key: 'label', dir: 'asc' });
  const [selectedBeaconIds, setSelectedBeaconIds] = useState([]);
  const [assignBeaconModal, setAssignBeaconModal] = useState({ open: false, beacon: null });
  const [assignBeaconForm, setAssignBeaconForm] = useState({ personType: 'driver', personId: '' });
  const [beaconSort, setBeaconSort] = useState({ key: 'name', dir: 'asc' });
  const rfidImportRef = React.useRef(null);
  const [lastLocationModal, setLastLocationModal] = useState({ open: false, loading: false, data: null, error: null });
  const [observedHistoryModal, setObservedHistoryModal] = useState({ open: false, loading: false, data: null, error: null });
  const [logbookModal, setLogbookModal] = useState({ open: false, loading: false, data: null, error: null });
  const [logbookTarget, setLogbookTarget] = useState(null);
  const [logbookFilters, setLogbookFilters] = useState({ start_time: null, end_time: null, source: 'all', event_type: 'all', signal: 'all', search: '', limit: 1000 });
  const [selectedLogEntries, setSelectedLogEntries] = useState(new Set());
  const [logEntryPage, setLogEntryPage] = useState(1);
  const [logEntryPageSize, setLogEntryPageSize] = usePersistedPageSize('person-logbook', 10);
  const [logbookTotal, setLogbookTotal] = useState(0);
  const suppressLogbookPageFetchRef = useRef(false);
  const logbookListTopRef = useRef(null);
  const [lbStartOpen, setLbStartOpen] = useState(false);
  const [lbEndOpen, setLbEndOpen] = useState(false);
  const [observedHistoryTarget, setObservedHistoryTarget] = useState(null);
  const [selectedObsPoints, setSelectedObsPoints] = useState(new Set());
  const [obsPage, setObsPage] = useState(1);
  const [obsPageSize, setObsPageSize] = usePersistedPageSize('observed-history', 10);
  const [obsTotal, setObsTotal] = useState(0);
  const suppressObsPageFetchRef = useRef(false);
  const obsListTopRef = useRef(null);
  const [ohStartOpen, setOhStartOpen] = useState(false);
  const [ohEndOpen, setOhEndOpen] = useState(false);
  const [observedHistoryFilters, setObservedHistoryFilters] = useState({
    start_time: null,
    end_time: null,
    detector_imei: '',
    confidence: 'all',
    search: '',
    min_rssi: '',
    max_rssi: '',
    limit: 200,
  });
  
  // Selection states for checkboxes
  const [selectedDriverIds, setSelectedDriverIds] = useState([]);
  const [selectedStaffIds, setSelectedStaffIds] = useState([]);
  const [selectedPassengerIds, setSelectedPassengerIds] = useState([]);
  
  // Drivers state
  const [drivers, setDrivers] = useState([]);
  const [pendingPeopleAction, setPendingPeopleAction] = useState(null);
  const [peopleDeleteConfirmText, setPeopleDeleteConfirmText] = useState('');
  const [showPeopleUnsavedWarning, setShowPeopleUnsavedWarning] = useState(false);
  const [unsavedCloseTarget, setUnsavedCloseTarget] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [attachmentsViewer, setAttachmentsViewer] = useState(null); // { personType, person }
  // Driving Behaviour scores (30d) for driver badges — one batch call.
  const [behaviourMap, setBehaviourMap] = useState({});
  useEffect(() => {
    if (!hasGreenDriving) return undefined;
    let cancelled = false;
    api.get('/people/driving-behaviour-summary?days=30')
      .then((r) => { if (!cancelled) setBehaviourMap(r.data?.persons || {}); })
      .catch(() => {});
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasGreenDriving]);
  // Documents are attached immediately (independent of the form save); keep dialog + table in sync.
  const applyPersonAttachments = (personType, personId, attachments) => {
    const patch = (p) => (p && p.id === personId ? { ...p, attachments } : p);
    if (personType === 'driver') { setSelectedDriver(patch); setDrivers((list) => list.map(patch)); }
    if (personType === 'staff') { setSelectedStaffMember(patch); setStaff((list) => list.map(patch)); }
    if (personType === 'passenger') { setSelectedPassengerRecord(patch); setPassengers((list) => list.map(patch)); }
  };
  // Photo uploads are saved immediately (independent of the form save); keep dialog + table in sync.
  const applyPersonPhoto = (personType, personId, photo) => {
    const patch = (p) => (p && p.id === personId ? { ...p, photo } : p);
    if (personType === 'driver') { setSelectedDriver(patch); setDrivers((list) => list.map(patch)); }
    if (personType === 'staff') { setSelectedStaffMember(patch); setStaff((list) => list.map(patch)); }
    if (personType === 'passenger') { setSelectedPassengerRecord(patch); setPassengers((list) => list.map(patch)); }
  };
  const [isDriverDialogOpen, setIsDriverDialogOpen] = useState(false);
  const [driverForm, setDriverForm] = useState({
    employee_id: '',
    status: 'active',
    full_name: '',
    email: '',
    phone: '',
    address: '',
    license_number: '',
    license_expiry: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    date_started: '',
    notes: '',
    messaging_enabled: false,
    account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
    rfid_ibutton_card_id: '',
    person_group: '',
    linked_user_id: '',
  });
  
  // Staff state
  const [staff, setStaff] = useState([]);
  const [selectedStaffMember, setSelectedStaffMember] = useState(null);
  const [isStaffDialogOpen, setIsStaffDialogOpen] = useState(false);
  const [staffForm, setStaffForm] = useState({
    employee_id: '',
    status: 'active',
    license_number: '',
    license_expiry: '',
    full_name: '',
    email: '',
    phone: '',
    department: 'operations',
    role: 'coordinator',
    address: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    date_started: '',
    notes: '',
    messaging_enabled: false,
    account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
    rfid_ibutton_card_id: '',
    driver_eligible: false,
    person_group: '',
    linked_user_id: '',
  });
  
  // Passengers state
  const [passengers, setPassengers] = useState([]);
  const [selectedPassengerRecord, setSelectedPassengerRecord] = useState(null);
  const [isPassengerDialogOpen, setIsPassengerDialogOpen] = useState(false);
  const [passengerForm, setPassengerForm] = useState({
    passenger_id: '',
    status: 'active',
    full_name: '',
    email: '',
    phone: '',
    address: '',
    date_of_birth: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    special_requirements: '',
    notes: '',
    messaging_enabled: false,
    account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
    rfid_ibutton_card_id: '',
    person_group: '',
    linked_user_id: '',
  });

  // Chat activation invite to surface once after enabling "Allow Chat": delivery
  // status + a copy-able activation link, plus a resend action.
  const [chatInvite, setChatInvite] = useState(null);
  // Read-only person profile drawer (opened by clicking a person's name).
  const [profileDrawer, setProfileDrawer] = useState({ open: false, person: null, personType: null });
  // Set when Log Book / Location is opened FROM the profile modal → reopen it on close.
  const returnToProfileRef = useRef(null);
  const openProfile = (personType, person) => setProfileDrawer({ open: true, person, personType });
  // Live email-availability hints for the three person dialogs (A3).
  const driverEmailAvail = useEmailAvailability(api, driverForm.email, { excludePersonId: selectedDriver?.id, excludeUserId: driverForm.linked_user_id || undefined, enabled: isDriverDialogOpen });
  const staffEmailAvail = useEmailAvailability(api, staffForm.email, { excludePersonId: selectedStaffMember?.id, excludeUserId: staffForm.linked_user_id || undefined, enabled: isStaffDialogOpen });
  const passengerEmailAvail = useEmailAvailability(api, passengerForm.email, { excludePersonId: selectedPassengerRecord?.id, enabled: isPassengerDialogOpen });
  const [resendingInvite, setResendingInvite] = useState(false);
  const [accountStatusBusy, setAccountStatusBusy] = useState(false);
  const [accountPushBusy, setAccountPushBusy] = useState(false);
  // Operator-chosen invite delivery channels — two independent toggles, BOTH default
  // OFF (per request). Leaving both off = create/keep the invite but send nothing;
  // the result dialog still shows the activation link to copy.
  const [inviteEmail, setInviteEmail] = useState(false);
  const [inviteSms, setInviteSms] = useState(false);
  // "Show only pending chat invitations" header filters (per collection).
  const [driverPendingOnly, setDriverPendingOnly] = useState(false);
  // "Show only drivers with an expired / expiring-in-30-days license" header filter.
  const [driverLicenseOnly, setDriverLicenseOnly] = useState(false);
  const [staffPendingOnly, setStaffPendingOnly] = useState(false);
  const [staffLicenseOnly, setStaffLicenseOnly] = useState(false);
  const [driverInOnly, setDriverInOnly] = useState(false);
  const [staffInOnly, setStaffInOnly] = useState(false);
  const [passengerInOnly, setPassengerInOnly] = useState(false);
  const [currentlyInCounts, setCurrentlyInCounts] = useState({ drivers: 0, staff: 0, passengers: 0 });
  const [licenseAlertCounts, setLicenseAlertCounts] = useState({ drivers: 0, staff: 0 });
  const [docsAlertCounts, setDocsAlertCounts] = useState({ drivers: 0, staff: 0, passengers: 0 });
  const [driverDocsOnly, setDriverDocsOnly] = useState(false);
  const [staffDocsOnly, setStaffDocsOnly] = useState(false);
  const [passengerDocsOnly, setPassengerDocsOnly] = useState(false);
  const [pendingInviteCounts, setPendingInviteCounts] = useState({ drivers: 0, staff: 0, passengers: 0 });
  const [groupsModalOpen, setGroupsModalOpen] = useState(false);
  const [linkableUsers, setLinkableUsers] = useState([]);

  useEffect(() => {
    api.get('/people/linkable-users')
      .then((r) => setLinkableUsers(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Selecting an account in ADD mode prefills identity fields; clearing just unlinks.
  const handleLinkChange = (setForm, isEdit) => (uid) => {
    const u = linkableUsers.find((x) => x.id === uid);
    setForm((prev) => ({
      ...prev,
      linked_user_id: uid,
      ...(!isEdit && u ? {
        full_name: u.full_name || prev.full_name,
        email: u.email || prev.email,
        phone: u.mobile || u.phone || prev.phone,
        address: u.address || prev.address,
      } : {}),
      ...(uid ? { account_enabled: false } : {}),
    }));
  };
  const [groupedView, setGroupedView] = useState(() => {
    try { return { drivers: false, staff: false, passengers: false, ...JSON.parse(localStorage.getItem('people-grouped-view') || '{}') }; }
    catch { return { drivers: false, staff: false, passengers: false }; }
  });
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    let raw = {};
    try { raw = JSON.parse(localStorage.getItem('people-collapsed-groups') || '{}'); } catch { raw = {}; }
    return { drivers: new Set(raw.drivers || []), staff: new Set(raw.staff || []), passengers: new Set(raw.passengers || []) };
  });
  const [peopleGroups, setPeopleGroups] = useState({ drivers: [], staff: [], passengers: [] });
  const [groupCounts, setGroupCounts] = useState({ drivers: {}, staff: {}, passengers: {} });

  const toggleGroupedView = (tab) => setGroupedView((prev) => {
    const next = { ...prev, [tab]: !prev[tab] };
    localStorage.setItem('people-grouped-view', JSON.stringify(next));
    return next;
  });

  const toggleCollapsedGroup = (tab, gid) => setCollapsedGroups((prev) => {
    const s = new Set(prev[tab]);
    if (s.has(gid)) s.delete(gid); else s.add(gid);
    const next = { ...prev, [tab]: s };
    localStorage.setItem('people-collapsed-groups', JSON.stringify({ drivers: [...next.drivers], staff: [...next.staff], passengers: [...next.passengers] }));
    return next;
  });
  const [passengerPendingOnly, setPassengerPendingOnly] = useState(false);
  // ONE URL-backed page shared by whichever tab is active (?tab=staff&page=2).
  // Tab switches reset it via changeTab below.
  const [peoplePage, setPeoplePage] = useUrlPage('page');
  const driversPage = peoplePage; const setDriversPage = setPeoplePage;
  const staffPage = peoplePage; const setStaffPage = setPeoplePage;
  const passengersPage = peoplePage; const setPassengersPage = setPeoplePage;
  const rfidPage = peoplePage; const setRfidPage = setPeoplePage;
  const beaconsPage = peoplePage; const setBeaconsPage = setPeoplePage;
  const [peoplePageSize, setPeoplePageSize] = useUrlPageSize('people', 10);
  // Server-side totals: per-tab filtered totals (pager) + unfiltered tab-badge counts.
  const [peopleTotals, setPeopleTotals] = useState({ drivers: 0, staff: 0, passengers: 0, 'rfid-cards': 0, 'personal-beacons': 0 });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [selectAllBusyTab, setSelectAllBusyTab] = useState(null);
  const [peopleCounts, setPeopleCounts] = useState({ drivers: 0, staff: 0, passengers: 0, rfid_cards: 0, personal_beacons: 0 });
  // Per-type creation limits from user_features (-1 = unlimited, 0 = not set).
  const [peopleLimits, setPeopleLimits] = useState({ drivers: -1, staff: -1, passengers: -1 });

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    api.get(`/users/${user.id}/features`)
      .then(res => {
        if (cancelled) return;
        setPeopleLimits({
          drivers: res.data?.max_drivers ?? -1,
          staff: res.data?.max_staff ?? -1,
          passengers: res.data?.max_passengers ?? -1,
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [api, user?.id]);
  const setUrlParams = useSetUrlParams();
  const changeTab = (tab) => {
    setActiveTab(tab);
    setUrlParams({ tab, page: null });
  };
  // Debounce the whole per-tab search map: typing debounces as before, but a tab
  // switch applies the new tab's (already-settled) term instantly — no stale fetch.
  const debouncedSearchByTab = useDebouncedValue(searchByTab, 400);
  const debouncedPeopleSearch = debouncedSearchByTab[activeTab] ?? '';
  // Reset pagination when filters change (skips mount so ?page= deep-links survive)
  useSkipFirstEffect(() => { setPeoplePage(1); }, [debouncedPeopleSearch, driverPendingOnly, staffPendingOnly, passengerPendingOnly, driverLicenseOnly, staffLicenseOnly, driverDocsOnly, staffDocsOnly, passengerDocsOnly, driverInOnly, staffInOnly, passengerInOnly, groupedView, collapsedGroups, peoplePageSize]);

  // Reset both invite-channel toggles to OFF every time a person dialog opens.
  useEffect(() => {
    if (isDriverDialogOpen || isStaffDialogOpen || isPassengerDialogOpen) {
      setInviteEmail(false);
      setInviteSms(false);
    }
  }, [isDriverDialogOpen, isStaffDialogOpen, isPassengerDialogOpen]);

  // Open the invite-result dialog AFTER the (closing) form dialog's transition
  // settles — opening both Radix dialogs in the same tick caused a ~5s overlap.
  const openInviteDialog = (info) => { setTimeout(() => setChatInvite(info), 280); };

  // Surface the invite result returned by create/update when a new invitation was sent.
  const maybeShowInviteResult = (data, personName, personType) => {
    if (data && data.chat_invite && data.chat_invite.invite_status) {
      openInviteDialog({
        ...data.chat_invite,
        person_name: personName,
        person_type: personType,
        person_id: data.id,
        person_email: data.email,
        person_phone: data.phone,
      });
    }
  };

  // Which channels are actually DELIVERABLE for a person right now: they need that
  // contact detail AND the matching notification channel must be active.
  const channelAvailability = (personLike) => ({
    email: !!((personLike.email || '').trim()) && isNotificationChannelActive('notification_email'),
    sms: !!((personLike.phone || '').trim()) && isNotificationChannelActive('notification_sms'),
  });

  // The channels the operator has TICKED, filtered down to the ones that can actually
  // deliver. May be [] (both unticked / unavailable) → send nothing, just mint a link.
  const selectedInviteChannels = (personLike) => {
    const avail = channelAvailability(personLike);
    return [inviteEmail && avail.email && 'email', inviteSms && avail.sms && 'sms'].filter(Boolean);
  };

  // Attach the chosen invite delivery channels to a create/update payload when
  // "Create Account" is on. Always an explicit array ([] = send nothing).
  const withInviteChannels = (form) => {
    if (!form.account_enabled) return form;
    return { ...form, invite_channels: selectedInviteChannels(form) };
  };

  // Confirm before unticking "Create Account" on a person who already has a login,
  // since saving will deactivate their access and sign them out.
  const handleAccountToggle = (checked, setForm, currentInviteStatus, personName) => {
    if (!checked && currentInviteStatus) {
      setPendingPeopleAction({
        title: 'Deactivate account?',
        message: `Unticking "Create Account" will deactivate ${personName || 'this person'}'s login when you save. They'll be signed out and won't be able to sign in until you re-invite them.`,
        confirmLabel: 'Deactivate account',
        action: async () => { setForm((prev) => ({ ...prev, account_enabled: false, messaging_enabled: false, confirm_account_deactivation: true })); },
      });
      return;
    }
    setForm((prev) => ({ ...prev, account_enabled: !!checked, confirm_account_deactivation: false, ...(checked ? {} : { messaging_enabled: false }) }));
  };

  // "Account active" switch — activate/deactivate the person's login in place,
  // keeping the account (no need to untick "Create Account"). Deactivation confirms first.
  const handleAccountActiveToggle = (personType, person, active, applyStatus) => {
    if (!person?.id) return;
    const doToggle = async () => {
      setAccountStatusBusy(true);
      try {
        const res = await api.put(`/people/${personType}/${person.id}/account/status`, { active });
        applyStatus(res.data.chat_invite_status);
        toast.success(active ? 'Account reactivated' : 'Account deactivated');
        fetchData();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Failed to update account status');
      } finally {
        setAccountStatusBusy(false);
      }
    };
    if (!active) {
      setPendingPeopleAction({
        title: 'Deactivate account?',
        message: `${person.full_name || 'This person'} will be signed out and blocked from signing in until you reactivate the account. Their password is kept.`,
        confirmLabel: 'Deactivate',
        action: doToggle,
      });
      return;
    }
    doToggle();
  };

  // "Push Notifications" switch — turn on/off the per-person layer-2 privilege that
  // /api/push/subscribe gates on. This is what fixes the "Request failed with status
  // code 403" when a person tries to subscribe their phone/browser.
  const handleAccountPushToggle = (personType, person, enabled, applyPushState) => {
    if (!person?.id) return;
    const doToggle = async () => {
      setAccountPushBusy(true);
      try {
        const res = await api.put(`/people/${personType}/${person.id}/account/push`, { enabled });
        applyPushState(!!res.data.push_enabled);
        toast.success(enabled ? 'Push notifications enabled' : 'Push notifications disabled');
        fetchData();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Failed to update push notifications');
      } finally {
        setAccountPushBusy(false);
      }
    };
    if (!enabled) {
      setPendingPeopleAction({
        title: 'Disable push notifications?',
        message: `${person.full_name || 'This person'} will no longer be able to register their phone/browser for push alerts, and any currently-subscribed device will be unsubscribed.`,
        confirmLabel: 'Disable',
        action: doToggle,
      });
      return;
    }
    doToggle();
  };

  // Resend (re-mint) the activation link for a still-pending invite over the ticked
  // channel(s). With both unticked it still mints a fresh link the operator can copy
  // from the result dialog (sends nothing).
  const handleResendInvite = async (personType, person) => {
    setResendingInvite(true);
    try {
      const channels = selectedInviteChannels(person);
      const res = await api.post(
        `/people/${personType}/${person.id}/chat-invite/resend`,
        { channels },
      );
      openInviteDialog({
        ...res.data,
        person_name: person.full_name,
        person_type: personType,
        person_id: person.id,
        person_email: person.email,
        person_phone: person.phone,
      });
      toast.success('Invitation re-sent');
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to resend invitation');
    } finally {
      setResendingInvite(false);
    }
  };
  // Per-dialog snapshots taken when each dialog opens — the unsaved-changes
  // warning compares actual form data instead of trusting DOM input events
  // (autofill fired spurious input events; toggles/selects fired none).
  const driverSnapshotRef = useRef('');
  const staffSnapshotRef = useRef('');
  const passengerSnapshotRef = useRef('');
  useEffect(() => {
    if (isDriverDialogOpen) driverSnapshotRef.current = JSON.stringify(driverForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriverDialogOpen]);
  useEffect(() => {
    if (isStaffDialogOpen) staffSnapshotRef.current = JSON.stringify(staffForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaffDialogOpen]);
  useEffect(() => {
    if (isPassengerDialogOpen) passengerSnapshotRef.current = JSON.stringify(passengerForm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPassengerDialogOpen]);
  const peopleFormHasChanges = () => {
    if (isDriverDialogOpen) return JSON.stringify(driverForm) !== driverSnapshotRef.current;
    if (isStaffDialogOpen) return JSON.stringify(staffForm) !== staffSnapshotRef.current;
    if (isPassengerDialogOpen) return JSON.stringify(passengerForm) !== passengerSnapshotRef.current;
    return false;
  };

  const handlePeopleDialogClose = (closeFn, forceClose = false) => {
    if (!forceClose && peopleFormHasChanges()) {
      setUnsavedCloseTarget(() => closeFn);
      setShowPeopleUnsavedWarning(true);
      return;
    }
    closeFn();
    setShowPeopleUnsavedWarning(false);
  };

  // Require a deliverable contact when enabling an account (email OR mobile) so the
  // activation invitation can actually reach the person.
  const chatContactError = (form) => {
    if (form.account_enabled && !((form.email || '').trim() || (form.phone || '').trim())) {
      return 'Add an email or mobile number so we can send the account invitation';
    }
    return null;
  };

  // Add-button gating: blocked when the limit is 0 (Upgrade Required) or reached.
  const addBlocked = {
    drivers: peopleLimits.drivers === 0 || (peopleLimits.drivers > 0 && peopleCounts.drivers >= peopleLimits.drivers),
    staff: peopleLimits.staff === 0 || (peopleLimits.staff > 0 && peopleCounts.staff >= peopleLimits.staff),
    passengers: peopleLimits.passengers === 0 || (peopleLimits.passengers > 0 && peopleCounts.passengers >= peopleLimits.passengers),
  };
  const addBlockedTitle = (label, limit) => (
    limit === 0
      ? `Upgrade required — contact your administrator to add ${label}.`
      : `${label} limit reached (${limit}). Contact your administrator to increase it.`
  );

  const fetchData = useCallback(async (silent) => {
    const isSilent = silent === true;
    if (!isSilent) setLoading(true);
    try {
      // Server-side pagination: fetch only the ACTIVE tab's filtered/sorted page,
      // plus tab-badge counts and the full beacon/card inventories (dropdowns).
      const search = debouncedPeopleSearch.trim();
      const skip = String((peoplePage - 1) * peoplePageSize);
      const limit = String(peoplePageSize);

      const buildPersonParams = (pendingOnly) => {
        const p = new URLSearchParams();
        if (search) p.set('search', search);
        if (pendingOnly) p.set('pending_only', 'true');
        p.set('sort_key', peopleSort.key);
        p.set('sort_dir', peopleSort.dir);
        p.set('skip', skip);
        p.set('limit', limit);
        return p;
      };

      const readTotal = (res, fallbackLen) => {
        const t = parseInt(res.headers?.['x-total-count'], 10);
        return Number.isFinite(t) ? t : fallbackLen;
      };

      // Server-computed header badge counts (tenant-wide, not page-local).
      const fetchHeaderCounts = (url, key, withLicense) => {
        const idsCount = (params, setter) => api.get(`${url}?${params}&ids_only=true`)
          .then((r) => setter((prev) => ({ ...prev, [key]: (r.data?.ids || []).length })))
          .catch(() => {});
        const jobs = [
          idsCount('currently_in=true', setCurrentlyInCounts),
          idsCount('pending_only=true', setPendingInviteCounts),
          idsCount('docs_expiring=true', setDocsAlertCounts),
        ];
        if (withLicense) jobs.push(idsCount('license_expiring=true', setLicenseAlertCounts));
        return Promise.all(jobs);
      };

      // Grouped view (Phase 2): adds group_sort/exclude_groups to the row fetch and fires
      // a parallel request for true per-group counts.
      const fetchTabGroups = (tabKey) => {
        const gtype = { drivers: 'driver', staff: 'staff', passengers: 'passenger' }[tabKey];
        return api.get(`/people-groups?group_type=${gtype}`)
          .then((r) => setPeopleGroups((prev) => ({ ...prev, [tabKey]: Array.isArray(r.data) ? r.data : [] })))
          .catch(() => {});
      };

      const applyGroupParams = (p, tabKey) => {
        if (!groupedView[tabKey]) return null;
        p.set('group_sort', 'true');
        const excl = [...collapsedGroups[tabKey]];
        if (excl.length) p.set('exclude_groups', excl.join(','));
        const cp = new URLSearchParams(p);
        cp.delete('group_sort'); cp.delete('exclude_groups'); cp.delete('skip'); cp.delete('limit');
        cp.set('group_counts', 'true');
        const url = { drivers: '/drivers', staff: '/staff', passengers: '/passengers' }[tabKey];
        return api.get(`${url}?${cp.toString()}`)
          .then((r) => setGroupCounts((prev) => ({ ...prev, [tabKey]: r.data?.counts || {} })))
          .catch(() => {});
      };

      const tabFetch = (async () => {
        if (activeTab === 'drivers') {
          const p = buildPersonParams(driverPendingOnly);
          if (driverLicenseOnly) p.set('license_expiring', 'true');
          if (driverDocsOnly) p.set('docs_expiring', 'true');
          if (driverInOnly) p.set('currently_in', 'true');
          const grpPromise = applyGroupParams(p, 'drivers');
          const [res] = await Promise.all([api.get(`/drivers?${p.toString()}`), fetchHeaderCounts('/drivers', 'drivers', true), fetchTabGroups('drivers'), grpPromise].filter(Boolean));
          setDrivers(res.data || []);
          setPeopleTotals((prev) => ({ ...prev, drivers: readTotal(res, (res.data || []).length) }));
        } else if (activeTab === 'staff') {
          const p = buildPersonParams(staffPendingOnly);
          if (staffLicenseOnly) p.set('license_expiring', 'true');
          if (staffDocsOnly) p.set('docs_expiring', 'true');
          if (staffInOnly) p.set('currently_in', 'true');
          const grpPromise = applyGroupParams(p, 'staff');
          const [res] = await Promise.all([api.get(`/staff?${p.toString()}`), fetchHeaderCounts('/staff', 'staff', true), fetchTabGroups('staff'), grpPromise].filter(Boolean));
          setStaff(res.data || []);
          setPeopleTotals((prev) => ({ ...prev, staff: readTotal(res, (res.data || []).length) }));
        } else if (activeTab === 'passengers') {
          const p = buildPersonParams(passengerPendingOnly);
          if (passengerDocsOnly) p.set('docs_expiring', 'true');
          if (passengerInOnly) p.set('currently_in', 'true');
          const grpPromise = applyGroupParams(p, 'passengers');
          const [res] = await Promise.all([api.get(`/passengers?${p.toString()}`), fetchHeaderCounts('/passengers', 'passengers', false), fetchTabGroups('passengers'), grpPromise].filter(Boolean));
          setPassengers(res.data || []);
          setPeopleTotals((prev) => ({ ...prev, passengers: readTotal(res, (res.data || []).length) }));
        } else if (activeTab === 'rfid-cards') {
          const p = new URLSearchParams();
          if (search) p.set('search', search);
          p.set('sort_key', cardSort.key);
          p.set('sort_dir', cardSort.dir);
          p.set('skip', skip);
          p.set('limit', limit);
          const [res, unRes] = await Promise.all([
            api.get(`/people/rfid-ibutton-cards?${p.toString()}`),
            api.get('/people/rfid-ibutton-cards/unmatched-scans').catch(() => ({ data: [] })),
          ]);
          setRfidCards(res.data || []);
          setUnmatchedScans(unRes.data || []);
          setPeopleTotals((prev) => ({ ...prev, 'rfid-cards': readTotal(res, (res.data || []).length) }));
        } else if (activeTab === 'personal-beacons') {
          const p = new URLSearchParams();
          if (search) p.set('search', search);
          p.set('sort_key', beaconSort.key);
          p.set('sort_dir', beaconSort.dir);
          p.set('skip', skip);
          p.set('limit', limit);
          const res = await api.get(`/people/personal-beacons?${p.toString()}`);
          setPersonalBeacons(res.data || []);
          setPeopleTotals((prev) => ({ ...prev, 'personal-beacons': readTotal(res, (res.data || []).length) }));
        }
      })();

      const [countsRes, beaconsRes, cardsRes] = await Promise.all([
        api.get('/people/counts').catch(() => ({ data: null })),
        api.get('/people/personal-beacons').catch(() => ({ data: [] })),
        api.get('/people/rfid-ibutton-cards').catch(() => ({ data: [] })),
      ]);
      await tabFetch;
      if (countsRes.data) setPeopleCounts(countsRes.data);
      setBeaconInventory(beaconsRes.data || []);
      setCardInventory(cardsRes.data || []);
    } catch (error) {
      console.error('Failed to fetch people data:', error);
      toast.error('Failed to load people data');
      setDrivers([]);
      setStaff([]);
      setPassengers([]);
      setPersonalBeacons([]);
      setRfidCards([]);
    } finally {
      if (!isSilent) setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [api, activeTab, debouncedPeopleSearch, peoplePage, peoplePageSize,
      driverPendingOnly, staffPendingOnly, passengerPendingOnly, driverLicenseOnly, staffLicenseOnly,
      driverDocsOnly, staffDocsOnly, passengerDocsOnly,
      driverInOnly, staffInOnly, passengerInOnly, groupedView, collapsedGroups,
      peopleSort.key, peopleSort.dir, cardSort.key, cardSort.dir, beaconSort.key, beaconSort.dir]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Silent 60s auto-refresh for the people lists (same cadence as the Person/Vehicle dashboards).
  useEffect(() => {
    const iv = setInterval(() => fetchData(true), 60000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const openLastLocation = async (personType, person) => {
    setLastLocationModal({ open: true, loading: true, data: null, error: null });
    try {
      const response = await api.get(`/people/${personType}/${person.id}/last-location`);
      setLastLocationModal({ open: true, loading: false, data: response.data, error: null });
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to load last observed location';
      setLastLocationModal({ open: true, loading: false, data: null, error: message });
      toast.error(message);
    }
  };

  // True server-side pagination: `limit` stays the user-facing observation window filter;
  // `page_size` + `skip` slice it per page. Total (within window) from X-Total-Count.
  const fetchObservedHistoryPage = useCallback(async (personType, person, filters, page, size) => {
    const params = new URLSearchParams(buildHistoryQuery(filters));
    params.set('page_size', String(size));
    params.set('skip', String((page - 1) * size));
    const response = await api.get(`/people/${personType}/${person.id}/observed-history?${params.toString()}`);
    setObsTotal(parseInt(response.headers?.['x-total-count'], 10) || response.data?.count || 0);
    return response;
  }, [api]);

  // Full matching window — used by Select All + exports only.
  const fetchAllObsPoints = useCallback(async () => {
    if (!observedHistoryTarget) return [];
    const response = await api.get(`/people/${observedHistoryTarget.personType}/${observedHistoryTarget.person.id}/observed-history?${buildHistoryQuery(observedHistoryFilters)}`);
    return response.data?.points || [];
  }, [api, observedHistoryTarget, observedHistoryFilters]);

  const openObservedHistory = async (personType, person, filters = observedHistoryFilters) => {
    setObservedHistoryTarget({ personType, person });
    setSelectedObsPoints(new Set());
    setObsPage((prev) => { if (prev !== 1) suppressObsPageFetchRef.current = true; return 1; });
    setObservedHistoryModal({ open: true, loading: true, data: null, error: null });
    try {
      const response = await fetchObservedHistoryPage(personType, person, filters, 1, obsPageSize);
      setObservedHistoryModal({ open: true, loading: false, data: response.data, error: null });
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to load observed history';
      setObservedHistoryModal({ open: true, loading: false, data: null, error: message });
      toast.error(message);
    }
  };

  // Page / rows-per-page changes swap the data in place (no spinner remount) so the
  // scroll-to-first-record behaviour stays smooth.
  useEffect(() => {
    if (suppressObsPageFetchRef.current) { suppressObsPageFetchRef.current = false; return; }
    if (!observedHistoryModal.open || !observedHistoryTarget) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchObservedHistoryPage(observedHistoryTarget.personType, observedHistoryTarget.person, observedHistoryFilters, obsPage, obsPageSize);
        if (!cancelled) setObservedHistoryModal((prev) => (prev.open ? { ...prev, loading: false, data: response.data, error: null } : prev));
      } catch { /* keep showing the existing page */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [obsPage, obsPageSize]);

  const applyObservedHistoryFilters = () => {
    if (!observedHistoryTarget) return;
    openObservedHistory(observedHistoryTarget.personType, observedHistoryTarget.person, observedHistoryFilters);
  };

  const toggleObsSelection = (key) => setSelectedObsPoints((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const logObsExport = (fmt, count) => logReportExport(api, {
    report: 'Observed Beacon History',
    format: fmt,
    scope: observedHistoryModal.data?.person?.full_name || observedHistoryTarget?.person?.full_name || observedHistoryTarget?.personType,
    count,
    entity_id: observedHistoryTarget?.person?.id,
    filters: { person_type: observedHistoryTarget?.personType, beacon: beaconDisplayName(observedHistoryModal.data?.beacon) },
  });

  const exportAllObs = async (fmt) => {
    const pname = observedHistoryModal.data?.person?.full_name || observedHistoryTarget?.person?.full_name;
    const bname = beaconDisplayName(observedHistoryModal.data?.beacon);
    try {
      const all = await fetchAllObsPoints();
      const points = selectedObsPoints.size ? all.filter((p, i) => selectedObsPoints.has(obsPointKey(p, i))) : all;
      if (!points.length) { toast.info('No observations to export'); return; }
      logObsExport(fmt === 'pdf' ? 'pdf' : 'csv', points.length);
      if (fmt === 'pdf') {
        exportAllObsPdf(points, pname, bname).catch(() => toast.error('Could not generate PDF'));
      } else {
        exportAllObsCsv(points, pname, bname);
      }
    } catch { toast.error('Could not load observations for export'); }
  };

  const openEmailObs = () => {
    const pname = observedHistoryModal.data?.person?.full_name || observedHistoryTarget?.person?.full_name;
    const bname = beaconDisplayName(observedHistoryModal.data?.beacon);
    const count = selectedObsPoints.size || obsTotal;
    if (!count) { toast.info('No observations to email'); return; }
    setEmailModal({
      open: true,
      ctx: {
        reportLabel: 'Observed Beacon History',
        scope: [pname, bname].filter(Boolean).join(' \u00b7 ') || 'person',
        count,
        subject: `EnsueLIVETRAK - Observed Beacon History${pname ? ' - ' + pname : ''}`,
        buildPdf: async () => {
          const all = await fetchAllObsPoints();
          const points = selectedObsPoints.size ? all.filter((p, i) => selectedObsPoints.has(obsPointKey(p, i))) : all;
          return buildAllObsPdf(points, pname, bname);
        },
      },
    });
  };

  // True server-side pagination for the Person Log Book (limit = page size, skip = offset).
  const fetchPersonLogbookPage = useCallback(async (personType, person, filters, page, size) => {
    const params = new URLSearchParams(buildLogbookQuery(filters));
    params.set('limit', String(size));
    params.set('skip', String((page - 1) * size));
    const response = await api.get(`/people/${personType}/${person.id}/logbook?${params.toString()}`);
    setLogbookTotal(parseInt(response.headers?.['x-total-count'], 10) || response.data?.count || 0);
    return response;
  }, [api]);

  // Full matching window (up to 1000 entries) — used by Select All + exports only.
  const fetchAllPersonLogEntries = useCallback(async () => {
    if (!logbookTarget) return [];
    const params = new URLSearchParams(buildLogbookQuery(logbookFilters));
    params.set('limit', '1000');
    params.set('skip', '0');
    const response = await api.get(`/people/${logbookTarget.personType}/${logbookTarget.person.id}/logbook?${params.toString()}`);
    return response.data?.entries || [];
  }, [api, logbookTarget, logbookFilters]);

  // Select All: ids-only fetch — skips per-row distance scans, near-instant.
  const fetchAllPersonLogEntryIds = useCallback(async () => {
    if (!logbookTarget) return [];
    const params = new URLSearchParams(buildLogbookQuery(logbookFilters));
    params.set('limit', '1000');
    params.set('skip', '0');
    params.set('ids_only', '1');
    const response = await api.get(`/people/${logbookTarget.personType}/${logbookTarget.person.id}/logbook?${params.toString()}`);
    return response.data?.entry_ids || [];
  }, [api, logbookTarget, logbookFilters]);

  const openPersonLogbook = async (personType, person, filters = logbookFilters) => {
    setLogbookTarget({ personType, person });
    setSelectedLogEntries(new Set());
    setLogEntryPage((prev) => { if (prev !== 1) suppressLogbookPageFetchRef.current = true; return 1; });
    setLogbookModal({ open: true, loading: true, data: null, error: null });
    try {
      const response = await fetchPersonLogbookPage(personType, person, filters, 1, logEntryPageSize);
      setLogbookModal({ open: true, loading: false, data: response.data, error: null });
    } catch (error) {
      const message = error.response?.data?.detail || 'Unable to load log book';
      setLogbookModal({ open: true, loading: false, data: null, error: message });
      toast.error(message);
    }
  };

  // Consume-once: ?open_person_logbook=<type>:<id> (round-trip return from the Vehicle
  // Driver Log Book) re-opens that person's Log Book modal.
  const requestedPersonLogbook = new URLSearchParams(location.search).get('open_person_logbook') || '';
  useEffect(() => {
    if (!requestedPersonLogbook) return;
    const [ptype, pid] = requestedPersonLogbook.split(':');
    const path = { driver: 'drivers', staff: 'staff', passenger: 'passengers' }[ptype];
    if (path && pid) {
      api.get(`/${path}/${pid}`)
        .then((res) => openPersonLogbook(ptype, res.data))
        .catch(() => toast.error('Person not found'));
    }
    const p = new URLSearchParams(location.search);
    p.delete('open_person_logbook');
    navigate(`${location.pathname}${p.toString() ? `?${p}` : ''}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedPersonLogbook]);

  useEffect(() => {
    if (suppressLogbookPageFetchRef.current) { suppressLogbookPageFetchRef.current = false; return; }
    if (!logbookModal.open || !logbookTarget) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchPersonLogbookPage(logbookTarget.personType, logbookTarget.person, logbookFilters, logEntryPage, logEntryPageSize);
        if (!cancelled) setLogbookModal((prev) => (prev.open ? { ...prev, loading: false, data: response.data, error: null } : prev));
      } catch { /* keep showing the existing page */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [logEntryPage, logEntryPageSize]);

  const applyPersonLogbookFilters = () => {
    if (!logbookTarget) return;
    openPersonLogbook(logbookTarget.personType, logbookTarget.person, logbookFilters);
  };

  const openObservedHistoryFromLogbook = () => {
    if (!logbookTarget) return;
    openObservedHistory(logbookTarget.personType, logbookTarget.person, observedHistoryFilters);
  };

  const openVehicleTripFromPersonLogbook = (entry) => {
    if (!entry?.vehicle_device_id) return;
    const params = new URLSearchParams({ tab: 'vehicles', driver_logbook_device: entry.vehicle_device_id });
    if (entry.trip_id) params.set('trip_id', entry.trip_id);
    const person = logbookTarget?.person || logbookModal.data?.person;
    if (person?.id && logbookTarget?.personType) {
      // Round-trip marker: closing the Vehicle Driver Log Book brings the user back here.
      params.set('return_person_logbook', `${logbookTarget.personType}:${person.id}`);
    }
    navigate(`/fleet?${params.toString()}`);
  };

  const toggleLogEntrySelection = (key) => setSelectedLogEntries((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const logPersonExport = (fmt, count) => logReportExport(api, {
    report: 'Person Log Book',
    format: fmt,
    scope: logbookModal.data?.person?.full_name || logbookTarget?.person?.full_name || logbookTarget?.personType,
    count,
    entity_id: logbookTarget?.person?.id,
    filters: { person_type: logbookTarget?.personType },
  });

  const exportAllPersonEntries = async (fmt) => {
    const pname = logbookModal.data?.person?.full_name || logbookTarget?.person?.full_name;
    try {
      const all = await fetchAllPersonLogEntries();
      const entries = selectedLogEntries.size ? all.filter((e, i) => selectedLogEntries.has(logEntryKey(e, i))) : all;
      if (!entries.length) { toast.info('No entries to export'); return; }
      logPersonExport(fmt === 'pdf' ? 'pdf' : 'csv', entries.length);
      if (fmt === 'pdf') {
        exportAllEntriesPdf(entries, pname).catch(() => toast.error('Could not generate PDF'));
      } else {
        exportAllEntriesCsv(entries, pname);
      }
    } catch { toast.error('Could not load entries for export'); }
  };

  const [emailModal, setEmailModal] = useState({ open: false, ctx: null });
  const openEmailPersonEntries = () => {
    const pname = logbookModal.data?.person?.full_name || logbookTarget?.person?.full_name;
    const count = selectedLogEntries.size || logbookTotal;
    if (!count) { toast.info('No entries to email'); return; }
    setEmailModal({
      open: true,
      ctx: {
        reportLabel: 'Person Log Book',
        scope: pname || 'person',
        count,
        subject: `EnsueLIVETRAK - Person Log Book${pname ? ' - ' + pname : ''}`,
        buildPdf: async () => {
          const all = await fetchAllPersonLogEntries();
          const entries = selectedLogEntries.size ? all.filter((e, i) => selectedLogEntries.has(logEntryKey(e, i))) : all;
          return buildAllEntriesPdf(entries, pname);
        },
      },
    });
  };

  // ============ DRIVER HANDLERS ============
  const handleCreateDriver = async () => {
    const formErr = driverFormError(driverForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(driverForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (driverEmailAvail?.available === false) { toast.error(driverEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.post('/drivers', withInviteChannels(driverForm));
      toast.success('Driver created successfully');
      await syncPersonGroup('driver', res.data?.id, driverForm.person_group, null);
      maybeShowInviteResult(res.data, driverForm.full_name, 'driver');
      setIsDriverDialogOpen(false);
      resetDriverForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create driver:', error);
      toast.error(error?.response?.data?.detail || 'Failed to create driver');
    }
  };

  // Login as a person's chat_contact account (same isolated-tab flow as Sub Accounts)
  const personCanLoginAs = (person) =>
    !!(person.account_enabled && person.chat_user_id && !['pending_invitation', 'deactivated', 'expired'].includes(person.chat_invite_status));

  const handleLoginAsPerson = async (person) => {
    try {
      const response = await api.post(`/users/${person.chat_user_id}/impersonate`);
      openImpersonationWindow(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as this person');
    }
  };

  const handleEditDriver = async () => {
    if (!selectedDriver) return;
    const formErr = driverFormError(driverForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(driverForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (driverEmailAvail?.available === false) { toast.error(driverEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.put(`/drivers/${selectedDriver.id}`, withInviteChannels(driverForm));
      toast.success('Driver updated successfully');
      await syncPersonGroup('driver', selectedDriver.id, driverForm.person_group, selectedDriver.driver_group);
      maybeShowInviteResult(res.data, driverForm.full_name, 'driver');
      setIsDriverDialogOpen(false);
      setSelectedDriver(null);
      resetDriverForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update driver:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update driver');
    }
  };

  const handleDeleteDriver = async (driverId) => {
    setPendingPeopleAction({ title: 'Delete Driver?', requireTyped: true, message: 'Are you sure you want to delete this driver?', action: async () => {
      try {
        await api.delete(`/drivers/${driverId}`);
        toast.success('Driver deleted successfully');
        setSelectedDriverIds(prev => prev.filter(id => id !== driverId));
        if (drivers.length === 1 && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete driver'); }
    }});
  };

  const handleDeleteSelectedDrivers = async () => {
    if (selectedDriverIds.length === 0) { toast.error('No drivers selected'); return; }
    setPendingPeopleAction({ title: 'Delete Drivers?', requireTyped: true, message: `Are you sure you want to delete ${selectedDriverIds.length} driver(s)?`, action: async () => {
      try {
        const willEmptyPage = drivers.length > 0 && drivers.every(d => selectedDriverIds.includes(d.id));
        await Promise.all(selectedDriverIds.map(id => api.delete(`/drivers/${id}`)));
        toast.success(`Deleted ${selectedDriverIds.length} driver(s)`);
        setSelectedDriverIds([]);
        if (willEmptyPage && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete drivers'); }
    }});
  };

  const availableRfidCardsFor = (form) => cardInventory.filter(card => !card.assigned_person_id || card.id === form.rfid_ibutton_card_id);

  const handleSaveRfidCard = async () => {
    if (!rfidCardForm.card_value.trim()) { toast.error('Card value is required'); return; }
    try {
      if (selectedRfidCard) {
        await api.put(`/people/rfid-ibutton-cards/${selectedRfidCard.id}`, rfidCardForm);
        toast.success('RFID / iButton card updated');
      } else {
        await api.post('/people/rfid-ibutton-cards', rfidCardForm);
        toast.success('RFID / iButton card added');
      }
      setIsRfidDialogOpen(false);
      setSelectedRfidCard(null);
      setRfidCardForm({ card_value: '', label: '', status: 'available', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to save RFID / iButton card');
    }
  };

  const openAddRfidDialog = () => {
    setSelectedRfidCard(null);
    setRfidCardForm({ card_value: '', label: '', status: 'available', notes: '' });
    setIsRfidDialogOpen(true);
  };

  const openEditRfidDialog = (card) => {
    setSelectedRfidCard(card);
    setRfidCardForm({ card_value: card.card_value || '', label: card.label || '', status: card.status || 'available', notes: card.notes || '' });
    setIsRfidDialogOpen(true);
  };

  const handleToggleRfidSelection = (id) => {
    setSelectedRfidCardIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleToggleAllRfid = () => {
    const pageIds = rfidCards.map(c => c.id).filter(Boolean);
    const allOn = pageIds.length > 0 && pageIds.every(id => selectedRfidCardIds.includes(id));
    setSelectedRfidCardIds(prev => allOn ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])));
  };

  const handleDeleteSelectedRfidCards = async () => {
    if (selectedRfidCardIds.length === 0) { toast.error('No cards selected'); return; }
    const deletable = (cardInventory.length ? cardInventory : rfidCards).filter(c => selectedRfidCardIds.includes(c.id) && !c.assigned_person_id);
    const skipped = selectedRfidCardIds.length - deletable.length;
    setPendingPeopleAction({ title: 'Delete RFID / iButton Cards?', requireTyped: true, message: `Delete ${deletable.length} card(s)?${skipped > 0 ? ` ${skipped} assigned card(s) will be skipped.` : ''}`, action: async () => {
      try {
        const deletedIds = deletable.map(c => c.id);
        const willEmptyPage = rfidCards.length > 0 && rfidCards.every(c => deletedIds.includes(c.id));
        await Promise.all(deletable.map(c => api.delete(`/people/rfid-ibutton-cards/${c.id}`)));
        toast.success(`Deleted ${deletable.length} card(s)`);
        setSelectedRfidCardIds([]);
        if (willEmptyPage && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      } catch (error) { toast.error(error?.response?.data?.detail || 'Failed to delete cards'); }
    }});
  };

  // Assign dialogs must list ALL people, not just the current server page.
  const [personDirectory, setPersonDirectory] = useState({ driver: [], staff: [], passenger: [] });
  const loadPersonDirectory = async () => {
    try {
      const [d, st, pa] = await Promise.all([
        hasDrivers ? api.get('/drivers') : Promise.resolve({ data: [] }),
        hasStaff ? api.get('/staff') : Promise.resolve({ data: [] }),
        hasPassengers ? api.get('/passengers') : Promise.resolve({ data: [] }),
      ]);
      setPersonDirectory({ driver: d.data || [], staff: st.data || [], passenger: pa.data || [] });
    } catch (error) { /* keep previous directory */ }
  };
  const peopleForType = (t) => personDirectory[t] || [];

  const openAssignCardDialog = (card) => {
    const firstType = hasDrivers ? 'driver' : (hasStaff ? 'staff' : 'passenger');
    setAssignForm({ personType: firstType, personId: '' });
    setAssignCardModal({ open: true, card });
    loadPersonDirectory();
  };

  const handleAssignCard = async () => {
    const { card } = assignCardModal;
    if (!card) return;
    if (!assignForm.personId) { toast.error('Select a person to assign'); return; }
    try {
      await api.put(`/${PERSON_PATH[assignForm.personType]}/${assignForm.personId}`, { rfid_ibutton_card_id: card.id });
      toast.success('Card assigned');
      setAssignCardModal({ open: false, card: null });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign card');
    }
  };

  const handleUnassignCard = (card) => {
    if (!card.assigned_person_id || !card.assigned_person_type) return;
    setPendingPeopleAction({ title: 'Unassign Card', tone: 'warning', confirmLabel: 'Unassign', message: `Unassign ${card.label || card.card_value || 'this card'} from ${card.assigned_person_name || 'this person'}?`, action: async () => {
      try {
        await api.put(`/${PERSON_PATH[card.assigned_person_type]}/${card.assigned_person_id}`, { rfid_ibutton_card_id: null });
        toast.success('Card unassigned');
        fetchData();
      } catch (error) { toast.error(error?.response?.data?.detail || 'Failed to unassign card'); }
    }});
  };

  // --- Personal Beacons (assign-only; admins manage the inventory in the Admin Panel) ---
  const toggleBeaconSort = (key) => setBeaconSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });
  const handleToggleBeaconSelection = (id) => setSelectedBeaconIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const handleToggleAllBeacons = () => {
    const pageIds = personalBeacons.map((b) => b.id).filter(Boolean);
    const allOn = pageIds.length > 0 && pageIds.every(id => selectedBeaconIds.includes(id));
    setSelectedBeaconIds(prev => allOn ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])));
  };
  const handleExportBeacons = async () => {
    const headers = ['name', 'imei', 'beacon_type', 'battery_voltage_mv', 'assigned_person_type', 'assigned_person_name'];
    let all = personalBeacons;
    try { const res = await api.get('/people/personal-beacons'); all = res.data || []; }
    catch (error) { toast.error('Failed to fetch beacons for export'); return; }
    const data = selectedBeaconIds.length > 0 ? all.filter((b) => selectedBeaconIds.includes(b.id)) : all;
    const rows = data.map((b) => [beaconDisplayName(b), b.imei || '', b.beacon_type || 'BLE', b.battery_voltage_mv ?? '', b.personal_beacon_assigned_to?.type || '', b.personal_beacon_assigned_to?.name || '']
      .map((v) => `${v}`.replace(/,/g, ' ')).join(','));
    downloadCSV([headers.join(','), ...rows].join('\n'), `personal-beacons_export_${getFileTimestamp()}.csv`);
    toast.success(`Exported ${data.length} beacon(s)`);
  };
  const openAssignBeaconDialog = (beacon) => {
    const firstType = hasDrivers ? 'driver' : (hasStaff ? 'staff' : 'passenger');
    setAssignBeaconForm({ personType: firstType, personId: '' });
    setAssignBeaconModal({ open: true, beacon });
    loadPersonDirectory();
  };
  const handleAssignBeacon = async () => {
    const { beacon } = assignBeaconModal;
    if (!beacon) return;
    if (!assignBeaconForm.personId) { toast.error('Select a person to assign'); return; }
    try {
      await api.put(`/${PERSON_PATH[assignBeaconForm.personType]}/${assignBeaconForm.personId}`, { personal_beacon_id: beacon.id });
      toast.success('Beacon assigned');
      setAssignBeaconModal({ open: false, beacon: null });
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to assign beacon');
    }
  };
  const handleUnassignBeacon = (beacon) => {
    const a = beacon.personal_beacon_assigned_to;
    if (!a?.id || !a?.type) return;
    setPendingPeopleAction({ title: 'Unassign Beacon', tone: 'warning', confirmLabel: 'Unassign', message: `Unassign ${beaconDisplayName(beacon)} from ${a.name || 'this person'}?`, action: async () => {
      try {
        await api.put(`/${PERSON_PATH[a.type]}/${a.id}`, { personal_beacon_id: null });
        toast.success('Beacon unassigned');
        fetchData();
      } catch (error) { toast.error(error?.response?.data?.detail || 'Failed to unassign beacon'); }
    }});
  };

  const handleDeleteRfidCard = async (card) => {
    setPendingPeopleAction({ title: 'Delete RFID / iButton Card?', requireTyped: true, message: `Delete ${card.label || card.card_value}?`, action: async () => {
      try {
        await api.delete(`/people/rfid-ibutton-cards/${card.id}`);
        toast.success('RFID / iButton card deleted');
        setSelectedRfidCardIds(prev => prev.filter(id => id !== card.id));
        if (rfidCards.length === 1 && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error(error?.response?.data?.detail || 'Failed to delete RFID / iButton card'); }
    }});
  };

  const handleImportRfidCards = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/people/rfid-ibutton-cards/import', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { created = 0, skipped = 0, allocated = 0, errors = [] } = res.data || {};
      toast.success(`Imported ${created} card(s)${allocated ? `, ${allocated} allocated` : ''}${skipped ? `, ${skipped} skipped` : ''}`);
      if (errors.length > 0) toast.warning(`${errors.length} row(s) had issues — e.g. ${errors[0]?.error}`);
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to import cards');
    } finally {
      event.target.value = '';
    }
  };

  const handleExportRfidCards = async () => {
    // Unified, import-ready format (same as the Unmatched Scans export):
    // assigned_person carries Employee/Passenger ID (fallback email) so the file
    // can be re-imported with allocations intact. Notes last.
    const headers = ['card_value', 'label', 'status', 'assigned_type', 'assigned_person', 'notes'];
    let all = rfidCards;
    try { const res = await api.get('/people/rfid-ibutton-cards'); all = res.data || []; }
    catch (error) { toast.error('Failed to fetch cards for export'); return; }
    const dataToExport = selectedRfidCardIds.length > 0
      ? all.filter(c => selectedRfidCardIds.includes(c.id))
      : all;
    const personIdent = {};
    if (dataToExport.some(c => c.assigned_person_id)) {
      try {
        const [dRes, sRes, pRes] = await Promise.all([
          api.get('/drivers').catch(() => ({ data: [] })),
          api.get('/staff').catch(() => ({ data: [] })),
          api.get('/passengers').catch(() => ({ data: [] })),
        ]);
        [...(dRes.data || []), ...(sRes.data || []), ...(pRes.data || [])].forEach((p) => {
          personIdent[p.id] = p.employee_id || p.passenger_id || p.email || p.full_name || '';
        });
      } catch { /* fall back to stored name below */ }
    }
    const csvContent = [
      headers.join(','),
      ...dataToExport.map(c => {
        const row = {
          card_value: c.card_value,
          label: c.label,
          status: c.status,
          assigned_type: c.assigned_person_type || '',
          assigned_person: c.assigned_person_id ? (personIdent[c.assigned_person_id] || c.assigned_person_name || '') : '',
          notes: c.notes,
        };
        return headers.map(h => `${row[h] ?? ''}`.replace(/,/g, ' ')).join(',');
      })
    ].join('\n');
    downloadCSV(csvContent, `rfid-ibutton-cards_export_${getFileTimestamp()}.csv`);
    toast.success(`Exported ${dataToExport.length} card(s)`);
  };

  const openEditDriverDialog = (driver) => {
    setSelectedDriver(driver);
    setDriverForm({
      employee_id: driver.employee_id || '',
      full_name: driver.full_name || '',
      email: driver.email || '',
      phone: driver.phone || '',
      address: driver.address || '',
      license_number: driver.license_number || '',
      license_expiry: driver.license_expiry || '',
      date_of_birth: driver.date_of_birth || '',
      emergency_contact_name: driver.emergency_contact_name || '',
      emergency_contact_phone: driver.emergency_contact_phone || '',
      date_started: driver.date_started || '',
      notes: driver.notes || '',
      status: driver.status || 'active',
      messaging_enabled: driver.messaging_enabled || false,
      account_enabled: driver.account_enabled || false,
      chat_password_mode: 'auto',
      chat_password: '',
      personal_beacon_id: driver.personal_beacon_id || '',
      credential_number: driver.credential_number || '',
      rfid_ibutton_card_id: driver.rfid_ibutton_card_id || '',
      person_group: driver.driver_group || '',
      linked_user_id: driver.linked_user_id || '',
    });
    setIsDriverDialogOpen(true);
  };

  const resetDriverForm = () => {
    setDriverForm({
      employee_id: '',
      status: 'active',
      full_name: '',
      email: '',
      phone: '',
      address: '',
      license_number: '',
      license_expiry: '',
      date_of_birth: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      date_started: '',
      notes: '',
      messaging_enabled: false,
      account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
    rfid_ibutton_card_id: '',
    person_group: '',
    linked_user_id: '',
    });
  };

  // ============ STAFF HANDLERS ============
  const handleCreateStaff = async () => {
    const formErr = staffFormError(staffForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(staffForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (staffEmailAvail?.available === false) { toast.error(staffEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.post('/staff', withInviteChannels(staffForm));
      toast.success('Staff member created successfully');
      await syncPersonGroup('staff', res.data?.id, staffForm.person_group, null);
      maybeShowInviteResult(res.data, staffForm.full_name, 'staff');
      setIsStaffDialogOpen(false);
      resetStaffForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create staff:', error);
      toast.error(error?.response?.data?.detail || 'Failed to create staff member');
    }
  };

  const handleEditStaff = async () => {
    if (!selectedStaffMember) return;
    const formErr = staffFormError(staffForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(staffForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (staffEmailAvail?.available === false) { toast.error(staffEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.put(`/staff/${selectedStaffMember.id}`, withInviteChannels(staffForm));
      toast.success('Staff member updated successfully');
      await syncPersonGroup('staff', selectedStaffMember.id, staffForm.person_group, selectedStaffMember.staff_group);
      maybeShowInviteResult(res.data, staffForm.full_name, 'staff');
      setIsStaffDialogOpen(false);
      setSelectedStaffMember(null);
      resetStaffForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update staff:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update staff member');
    }
  };

  const handleDeleteStaff = async (staffId) => {
    setPendingPeopleAction({ title: 'Delete Staff Member?', requireTyped: true, message: 'Are you sure you want to delete this staff member?', action: async () => {
      try {
        await api.delete(`/staff/${staffId}`);
        toast.success('Staff member deleted successfully');
        setSelectedStaffIds(prev => prev.filter(id => id !== staffId));
        if (staff.length === 1 && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete staff member'); }
    }});
  };

  // ============ BULK ACTIONS (status change / invite resend) ============
  const handleBulkStatus = async (personType, ids, status) => {
    try {
      const res = await api.post(`/people/${personType}/bulk-status`, { ids, status });
      toast.success(`${res.data.updated} set to ${status}${res.data.skipped ? `, ${res.data.skipped} skipped` : ''}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk status change failed');
    }
  };

  const handleBulkResendInvites = async (personType, ids) => {
    try {
      const res = await api.post(`/people/${personType}/bulk-resend-invites`, { ids });
      const { sent, skipped, errors } = res.data;
      if (sent) toast.success(`${sent} invitation${sent === 1 ? '' : 's'} sent${skipped ? `, ${skipped} skipped` : ''}`);
      else toast.info('No pending sign-ups in the selection — nothing sent');
      if (errors?.length) toast.error(`${errors.length} failed (first: ${errors[0].name || errors[0].id})`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk resend failed');
    }
  };

  const handleBulkMoveToGroup = async (personType, ids, groupId) => {
    try {
      if (groupId) {
        const res = await api.post(`/people-groups/${groupId}/add-items`, { item_ids: ids });
        toast.success(`${res.data.added_count} moved to group`);
      } else {
        const res = await api.post('/people-groups/clear-items', { group_type: personType, item_ids: ids });
        toast.success(`${res.data.cleared_count} removed from group`);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Group change failed');
    }
  };

  // Keeps a person's group in sync after dialog save (group is stored via the
  // group endpoints, not the person create/update payload).
  const syncPersonGroup = async (personType, personId, desiredGid, currentGid) => {
    const want = desiredGid || null;
    if (!personId || want === (currentGid || null)) return;
    try {
      if (want) await api.post(`/people-groups/${want}/add-items`, { item_ids: [personId] });
      else await api.post('/people-groups/clear-items', { group_type: personType, item_ids: [personId] });
    } catch { toast.error('Saved, but the group assignment failed'); }
  };

  const handleDeleteSelectedStaff = async () => {
    if (selectedStaffIds.length === 0) { toast.error('No staff members selected'); return; }
    setPendingPeopleAction({ title: 'Delete Staff Members?', requireTyped: true, message: `Are you sure you want to delete ${selectedStaffIds.length} staff member(s)?`, action: async () => {
      try {
        const willEmptyPage = staff.length > 0 && staff.every(s => selectedStaffIds.includes(s.id));
        await Promise.all(selectedStaffIds.map(id => api.delete(`/staff/${id}`)));
        toast.success(`Deleted ${selectedStaffIds.length} staff member(s)`);
        setSelectedStaffIds([]);
        if (willEmptyPage && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete staff members'); }
    }});
  };

  const openEditStaffDialog = (member) => {
    setSelectedStaffMember(member);
    setStaffForm({
      employee_id: member.employee_id || '',
      full_name: member.full_name || '',
      email: member.email || '',
      phone: member.phone || '',
      department: member.department || 'operations',
      role: member.role || 'coordinator',
      address: member.address || '',
      date_of_birth: member.date_of_birth || '',
      emergency_contact_name: member.emergency_contact_name || '',
      emergency_contact_phone: member.emergency_contact_phone || '',
      date_started: member.date_started || '',
      notes: member.notes || '',
      status: member.status || 'active',
      license_number: member.license_number || '',
      license_expiry: member.license_expiry || '',
      messaging_enabled: member.messaging_enabled || false,
      account_enabled: member.account_enabled || false,
      personal_beacon_id: member.personal_beacon_id || '',
      credential_number: member.credential_number || '',
      rfid_ibutton_card_id: member.rfid_ibutton_card_id || '',
      driver_eligible: member.driver_eligible || false,
      person_group: member.staff_group || '',
      linked_user_id: member.linked_user_id || '',
    });
    setIsStaffDialogOpen(true);
  };

  const resetStaffForm = () => {
    setStaffForm({
      employee_id: '',
      status: 'active',
      license_number: '',
      license_expiry: '',
      full_name: '',
      email: '',
      phone: '',
      department: 'operations',
      role: 'coordinator',
      address: '',
      date_of_birth: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      date_started: '',
      notes: '',
      messaging_enabled: false,
      account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
      rfid_ibutton_card_id: '',
    driver_eligible: false,
    person_group: '',
    linked_user_id: '',
    });
  };

  // ============ PASSENGER HANDLERS ============
  const handleCreatePassenger = async () => {
    const formErr = passengerFormError(passengerForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(passengerForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (passengerEmailAvail?.available === false) { toast.error(passengerEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.post('/passengers', withInviteChannels(passengerForm));
      toast.success('Passenger created successfully');
      await syncPersonGroup('passenger', res.data?.id, passengerForm.person_group, null);
      maybeShowInviteResult(res.data, passengerForm.full_name, 'passenger');
      setIsPassengerDialogOpen(false);
      resetPassengerForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create passenger:', error);
      toast.error(error?.response?.data?.detail || 'Failed to create passenger');
    }
  };

  const handleEditPassenger = async () => {
    if (!selectedPassengerRecord) return;
    const formErr = passengerFormError(passengerForm);
    if (formErr) { toast.error(formErr); return; }
    const chatErr = chatContactError(passengerForm);
    if (chatErr) { toast.error(chatErr); return; }
    if (passengerEmailAvail?.available === false) { toast.error(passengerEmailAvail.reason || 'This email is already in use'); return; }
    try {
      const res = await api.put(`/passengers/${selectedPassengerRecord.id}`, withInviteChannels(passengerForm));
      toast.success('Passenger updated successfully');
      await syncPersonGroup('passenger', selectedPassengerRecord.id, passengerForm.person_group, selectedPassengerRecord.passenger_group);
      maybeShowInviteResult(res.data, passengerForm.full_name, 'passenger');
      setIsPassengerDialogOpen(false);
      setSelectedPassengerRecord(null);
      resetPassengerForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update passenger:', error);
      toast.error(error?.response?.data?.detail || 'Failed to update passenger');
    }
  };

  const handleDeletePassenger = async (passengerId) => {
    setPendingPeopleAction({ title: 'Delete Passenger?', requireTyped: true, message: 'Are you sure you want to delete this passenger?', action: async () => {
      try {
        await api.delete(`/passengers/${passengerId}`);
        toast.success('Passenger deleted successfully');
        setSelectedPassengerIds(prev => prev.filter(id => id !== passengerId));
        if (passengers.length === 1 && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete passenger'); }
    }});
  };

  const handleDeleteSelectedPassengers = async () => {
    if (selectedPassengerIds.length === 0) { toast.error('No passengers selected'); return; }
    setPendingPeopleAction({ title: 'Delete Passengers?', requireTyped: true, message: `Are you sure you want to delete ${selectedPassengerIds.length} passenger(s)?`, action: async () => {
      try {
        const willEmptyPage = passengers.length > 0 && passengers.every(p => selectedPassengerIds.includes(p.id));
        await Promise.all(selectedPassengerIds.map(id => api.delete(`/passengers/${id}`)));
        toast.success(`Deleted ${selectedPassengerIds.length} passenger(s)`);
        setSelectedPassengerIds([]);
        if (willEmptyPage && peoplePage > 1) setPeoplePage(peoplePage - 1);
        else fetchData();
      }
      catch (error) { toast.error('Failed to delete passengers'); }
    }});
  };

  const openEditPassengerDialog = (passenger) => {
    setSelectedPassengerRecord(passenger);
    setPassengerForm({
      passenger_id: passenger.passenger_id || '',
      full_name: passenger.full_name || '',
      email: passenger.email || '',
      phone: passenger.phone || '',
      address: passenger.address || '',
      date_of_birth: passenger.date_of_birth || '',
      emergency_contact_name: passenger.emergency_contact_name || '',
      emergency_contact_phone: passenger.emergency_contact_phone || '',
      special_requirements: passenger.special_requirements || '',
      notes: passenger.notes || '',
      status: passenger.status || 'active',
      messaging_enabled: passenger.messaging_enabled || false,
      account_enabled: passenger.account_enabled || false,
      personal_beacon_id: passenger.personal_beacon_id || '',
      credential_number: passenger.credential_number || '',
      rfid_ibutton_card_id: passenger.rfid_ibutton_card_id || '',
      person_group: passenger.passenger_group || '',
    });
    setIsPassengerDialogOpen(true);
  };

  const resetPassengerForm = () => {
    setPassengerForm({
      passenger_id: '',
      status: 'active',
      full_name: '',
      email: '',
      phone: '',
      address: '',
      date_of_birth: '',
      emergency_contact_name: '',
      emergency_contact_phone: '',
      special_requirements: '',
      notes: '',
      messaging_enabled: false,
      account_enabled: false,
    chat_password_mode: 'auto',
    chat_password: '',
    personal_beacon_id: '',
    credential_number: '',
      rfid_ibutton_card_id: '',
    person_group: '',
    linked_user_id: '',
    });
  };

  // ============ SELECTION HANDLERS ============
  const handleToggleDriverSelection = (driverId) => {
    setSelectedDriverIds(prev => 
      prev.includes(driverId) ? prev.filter(id => id !== driverId) : [...prev, driverId]
    );
  };

  const handleToggleAllDrivers = () => {
    const pageIds = drivers.map(d => d.id).filter(Boolean);
    const allOn = pageIds.length > 0 && pageIds.every(id => selectedDriverIds.includes(id));
    setSelectedDriverIds(prev => allOn ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])));
  };

  const handleToggleStaffSelection = (staffId) => {
    setSelectedStaffIds(prev => 
      prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]
    );
  };

  const handleToggleAllStaff = () => {
    const pageIds = staff.map(s => s.id).filter(Boolean);
    const allOn = pageIds.length > 0 && pageIds.every(id => selectedStaffIds.includes(id));
    setSelectedStaffIds(prev => allOn ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])));
  };

  const handleTogglePassengerSelection = (passengerId) => {
    setSelectedPassengerIds(prev => 
      prev.includes(passengerId) ? prev.filter(id => id !== passengerId) : [...prev, passengerId]
    );
  };

  const handleToggleAllPassengers = () => {
    const pageIds = passengers.map(p => p.id).filter(Boolean);
    const allOn = pageIds.length > 0 && pageIds.every(id => selectedPassengerIds.includes(id));
    setSelectedPassengerIds(prev => allOn ? prev.filter(id => !pageIds.includes(id)) : Array.from(new Set([...prev, ...pageIds])));
  };

  // "Select All" (selection bar) — selects EVERY row matching the current
  // search/pending filters across ALL pages via a server ids_only fetch.
  // Mirrors the Driver Corrections tab pattern exactly.
  const handleSelectAllMatching = async (tab) => {
    const cfg = {
      drivers: { url: '/drivers', pending: driverPendingOnly, licenseOnly: driverLicenseOnly, docsOnly: driverDocsOnly, inOnly: driverInOnly, setter: setSelectedDriverIds, total: peopleTotals.drivers, selected: selectedDriverIds },
      staff: { url: '/staff', pending: staffPendingOnly, licenseOnly: staffLicenseOnly, docsOnly: staffDocsOnly, inOnly: staffInOnly, setter: setSelectedStaffIds, total: peopleTotals.staff, selected: selectedStaffIds },
      passengers: { url: '/passengers', pending: passengerPendingOnly, docsOnly: passengerDocsOnly, inOnly: passengerInOnly, setter: setSelectedPassengerIds, total: peopleTotals.passengers, selected: selectedPassengerIds },
      'rfid-cards': { url: '/people/rfid-ibutton-cards', setter: setSelectedRfidCardIds, total: peopleTotals['rfid-cards'], selected: selectedRfidCardIds },
      'personal-beacons': { url: '/people/personal-beacons', setter: setSelectedBeaconIds, total: peopleTotals['personal-beacons'], selected: selectedBeaconIds },
    }[tab];
    if (!cfg) return;
    if (cfg.selected.length >= cfg.total) { cfg.setter([]); return; }
    setSelectAllBusyTab(tab);
    try {
      const params = new URLSearchParams();
      const search = debouncedPeopleSearch.trim();
      if (search) params.set('search', search);
      if (cfg.pending) params.set('pending_only', 'true');
      if (cfg.licenseOnly) params.set('license_expiring', 'true');
      if (cfg.docsOnly) params.set('docs_expiring', 'true');
      if (cfg.inOnly) params.set('currently_in', 'true');
      if (groupedView[activeTab] && collapsedGroups[activeTab]?.size) params.set('exclude_groups', [...collapsedGroups[activeTab]].join(','));
      params.set('ids_only', 'true');
      const res = await api.get(`${cfg.url}?${params.toString()}`);
      cfg.setter(Array.isArray(res.data?.ids) ? res.data.ids : []);
    } catch (error) {
      toast.error('Failed to select all rows');
    } finally {
      setSelectAllBusyTab(null);
    }
  };

  // ============ IMPORT/EXPORT HANDLERS ============
  // People import — wizard per tab (drivers / staff / passengers).
  const [importWizardType, setImportWizardType] = useState(null);
  // Activation QR — viewable while an invite is pending.
  const [qrPerson, setQrPerson] = useState(null); // { person, type }

  // Shared fetch for exports — full filtered set, narrowed to selection when present.
  const fetchPeopleExportRows = async (url, current, selectedIds, label) => {
    let all = current;
    try { const res = await api.get(url); all = res.data || []; }
    catch (error) { toast.error(`Failed to fetch ${label} for export`); return null; }
    const rows = selectedIds.length > 0 ? all.filter((x) => selectedIds.includes(x.id)) : all;
    // Resolve group ids → names for the Group export column.
    const meta = { '/drivers': ['driver', 'driver_group'], '/staff': ['staff', 'staff_group'], '/passengers': ['passenger', 'passenger_group'] }[url];
    if (meta) {
      try {
        const gr = await api.get(`/people-groups?group_type=${meta[0]}`);
        const gmap = Object.fromEntries((gr.data || []).map((g) => [g.id, g.name]));
        rows.forEach((p) => { p.person_group_name = gmap[p[meta[1]]] || ''; });
      } catch { /* export proceeds without group names */ }
    }
    return rows;
  };

  const exportPeopleCSV = (rows, headers, label, filePrefix) => {
    const csvContent = [headers.join(','), ...rows.map((p) => personExportRow(p, headers))].join('\n');
    downloadCSV(csvContent, `${filePrefix}_export_${getFileTimestamp()}.csv`);
    toast.success(`Exported ${rows.length} ${label}`);
  };

  const handleExportDriversCSV = async () => {
    const rows = await fetchPeopleExportRows('/drivers', drivers, selectedDriverIds, 'drivers');
    if (rows) exportPeopleCSV(rows, PEOPLE_EXPORT_HEADERS.drivers, 'driver(s)', 'drivers');
  };

  const handleExportStaffCSV = async () => {
    const rows = await fetchPeopleExportRows('/staff', staff, selectedStaffIds, 'staff');
    if (rows) exportPeopleCSV(rows, PEOPLE_EXPORT_HEADERS.staff, 'staff member(s)', 'staff');
  };

  const handleExportPassengersCSV = async () => {
    const rows = await fetchPeopleExportRows('/passengers', passengers, selectedPassengerIds, 'passengers');
    if (rows) exportPeopleCSV(rows, PEOPLE_EXPORT_HEADERS.passengers, 'passenger(s)', 'passengers');
  };

  const handleExportDriversXlsx = async () => {
    const rows = await fetchPeopleExportRows('/drivers', drivers, selectedDriverIds, 'drivers');
    if (!rows) return;
    buildPeopleXlsx(rows, PEOPLE_EXPORT_HEADERS.drivers, 'Drivers', `drivers_export_${getFileTimestamp()}.xlsx`);
    toast.success(`Exported ${rows.length} driver(s)`);
  };

  const handleExportStaffXlsx = async () => {
    const rows = await fetchPeopleExportRows('/staff', staff, selectedStaffIds, 'staff');
    if (!rows) return;
    buildPeopleXlsx(rows, PEOPLE_EXPORT_HEADERS.staff, 'Staff', `staff_export_${getFileTimestamp()}.xlsx`);
    toast.success(`Exported ${rows.length} staff member(s)`);
  };

  const handleExportPassengersXlsx = async () => {
    const rows = await fetchPeopleExportRows('/passengers', passengers, selectedPassengerIds, 'passengers');
    if (!rows) return;
    buildPeopleXlsx(rows, PEOPLE_EXPORT_HEADERS.passengers, 'Passengers', `passengers_export_${getFileTimestamp()}.xlsx`);
    toast.success(`Exported ${rows.length} passenger(s)`);
  };

  // Full Backup (.zip): people.xlsx + photos/<ID>.<ext> — true round trip incl. photos.
  const handleExportFullBackup = (type) => async () => {
    const cfg = {
      drivers: ['/drivers', drivers, selectedDriverIds, 'Drivers'],
      staff: ['/staff', staff, selectedStaffIds, 'Staff'],
      passengers: ['/passengers', passengers, selectedPassengerIds, 'Passengers'],
    }[type];
    const rows = await fetchPeopleExportRows(cfg[0], cfg[1], cfg[2], type);
    if (!rows) return;
    toast.info('Building backup zip…');
    try {
      const zip = new JSZip();
      const xlsxData = XLSX.write(buildPeopleWorkbook(rows, PEOPLE_EXPORT_HEADERS[type], cfg[3]), { bookType: 'xlsx', type: 'array' });
      zip.file(`${type}.xlsx`, xlsxData);
      let photoCount = 0, docCount = 0;
      // Unique per-person stems — duplicate names/IDs no longer overwrite each other in the zip.
      const usedStems = new Set();
      const sanitizeStem = (s) => String(s || '').trim().replace(/[\\/:*?"<>|]/g, '-');
      const manifest = { version: 2, type, people: [] };
      for (const p of rows) {
        const baseStem = sanitizeStem(p.employee_id || p.passenger_id || p.full_name || p.id) || 'person';
        let stem = baseStem, n = 2;
        while (usedStems.has(stem.toLowerCase())) stem = `${baseStem}-${n++}`;
        usedStems.add(stem.toLowerCase());
        const entry = { key: stem, id: p.id, full_name: p.full_name || '', attachments: [] };
        if (p.photo?.url) {
          try {
            const res = await api.get(p.photo.url.replace(/^\/api/, ''), { responseType: 'blob' });
            const ext = (p.photo.content_type || res.data.type || 'image/jpeg').split('/').pop().replace('jpeg', 'jpg');
            zip.file(`photos/${stem}.${ext}`, res.data);
            entry.photo = `photos/${stem}.${ext}`;
            photoCount++;
          } catch { /* skip unreadable photo */ }
        }
        for (const [idx, att] of (p.attachments || []).entries()) {
          if (!att?.url) continue;
          try {
            const res = await api.get(att.url.replace(/^\/api/, ''), { responseType: 'blob' });
            const fname = `attachments/${stem}/${idx + 1}_${sanitizeStem(att.name || 'document')}`;
            zip.file(fname, res.data);
            entry.attachments.push({ file: fname, name: att.name || 'document', content_type: att.content_type || null, doc_type: att.doc_type || 'other', expiry_date: att.expiry_date || null, note: att.note || null });
            docCount++;
          } catch { /* skip unreadable document */ }
        }
        if (entry.photo || entry.attachments.length) manifest.people.push(entry);
      }
      if (manifest.people.length) zip.file('manifest.json', JSON.stringify(manifest, null, 1));
      const blob = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${type}_full_backup_${getFileTimestamp()}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Backup ready — ${rows.length} ${type}, ${photoCount} photo(s), ${docCount} document(s)`);
    } catch (e) {
      toast.error('Failed to build backup zip');
    }
  };

  const downloadCSV = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Full-page spinner ONLY before the first load — later refetches (search
  // debounce, pagination, sorting) keep the page mounted so inputs never lose focus.
  if (loading && !hasLoadedOnce) {
    return (
      <div className="flex items-center justify-center h-[calc(100vh-10rem)]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2563eb]" />
      </div>
    );
  }

  // Search / pending-filter / sorting / pagination all happen SERVER-SIDE now
  // (GET /drivers|/staff|/passengers|/people/rfid-ibutton-cards|/people/personal-beacons
  // with search/pending_only/sort_key/sort_dir/skip/limit → X-Total-Count).
  const togglePeopleSort = (key) => setSortByTab((prev) => {
    const cur = prev[activeTab] || PEOPLE_DEFAULT_SORT;
    return { ...prev, [activeTab]: cur.key === key ? { key, dir: cur.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' } };
  });
  const toggleCardSort = (key) => setCardSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' });

  const visibleDrivers = drivers;
  const visibleStaff = staff;
  const visiblePassengers = passengers;
  const visibleRfidCards = rfidCards;
  const visiblePersonalBeacons = personalBeacons;

  const pagedDrivers = drivers;
  const pagedStaff = staff;
  const pagedPassengers = passengers;

  const PEOPLE_GROUP_FIELD = { drivers: 'driver_group', staff: 'staff_group', passengers: 'passenger_group' };

  // Grouped view: slice the (already group-sorted) page rows into sections and render
  // the canonical group header rows (groups-ui-pattern.md §2.7). Collapsed groups have
  // their rows excluded server-side, so their headers render with the true count only.
  // Shared "⋮" overflow actions menu used by the Drivers / Staff / Passengers rows.
  // Mobile sizing mirrors the Live Tracking device overflow menu (bigger touch targets).
  const renderPersonActions = (personType, prefix, person, onEdit, onDelete) => {
    const mob = isMobileOrWebview;
    const itemCls = mob ? 'min-h-[52px] text-sm px-3 py-3' : '';
    const iconCls = mob ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2';
    return (
    <div className="flex items-center gap-1 justify-end">
      <Button size="sm" variant="outline" className={mob ? 'h-9 w-9 p-0' : ''} onClick={() => openPersonLogbook(personType, person)} title="Person Log Book" data-testid={`${prefix}-logbook-button-${person.id}`}>
        <BookOpen className={mob ? '!w-5 !h-5' : 'w-4 h-4'} />
      </Button>
      <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" className={mob ? 'h-9 w-9 p-0' : ''} title="Actions" data-testid={`${prefix}-actions-menu-${person.id}`}>
          <MoreVertical className={mob ? '!w-5 !h-5' : 'w-4 h-4'} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className={mob ? 'w-64 p-1.5' : 'w-56'}>
        <DropdownMenuItem className={itemCls} onSelect={onEdit} data-testid={`edit-${prefix}-button-${person.id}`}>
          <Pencil className={iconCls} /> Edit
        </DropdownMenuItem>
        <DropdownMenuItem className={itemCls} disabled={!personCanLoginAs(person)} onSelect={() => handleLoginAsPerson(person)} data-testid={`login-as-${prefix}-button-${person.id}`}>
          <ExternalLink className={iconCls} /> Login as {person.full_name || person.name}
        </DropdownMenuItem>
        {person.personal_beacon_id && (
          <>
            <DropdownMenuItem className={itemCls} onSelect={() => openLastLocation(personType, person)} data-testid={`${prefix}-last-location-button-${person.id}`}>
              <MapPin className={iconCls} /> Last Observed Location
            </DropdownMenuItem>
            <DropdownMenuItem className={itemCls} onSelect={() => openObservedHistory(personType, person)} data-testid={`${prefix}-observed-history-button-${person.id}`}>
              <Clock className={iconCls} /> Observed Beacon History
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem className={`${itemCls} text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400`} onSelect={onDelete} data-testid={`delete-${prefix}-button-${person.id}`}>
          <Trash2 className={iconCls} /> Delete
        </DropdownMenuItem>
      </DropdownMenuContent>
      </DropdownMenu>
    </div>
    );
  };

  // Contact cell: hides empty email/phone lines; "—" when both are empty.
  const renderContactCell = (person, large) => {
    const txt = large ? 'text-sm' : 'text-xs';
    const icn = large ? 'w-4 h-4' : 'w-3 h-3';
    return (person.email || person.phone) ? (
      <div className={large ? 'space-y-1' : 'space-y-0.5'}>
        {person.email && (
          <div className={`flex items-center gap-1.5 ${txt}`}>
            <Mail className={`${icn} text-muted-foreground shrink-0`} />
            <span className="break-all">{person.email}</span>
          </div>
        )}
        {person.phone && (
          <div className={`flex items-center gap-1.5 ${txt}`}>
            <Phone className={`${icn} text-muted-foreground shrink-0`} />
            <span>{person.phone}</span>
          </div>
        )}
      </div>
    ) : (
      <span className={`${txt} text-muted-foreground`}>—</span>
    );
  };

  // Builds the grouped sections (shared by the table body and the mobile card list).
  const buildGroupSections = (tabKey, rows) => {
    const groupsList = peopleGroups[tabKey] || [];
    const collapsedSet = collapsedGroups[tabKey];
    const counts = groupCounts[tabKey] || {};
    const field = PEOPLE_GROUP_FIELD[tabKey];
    const known = new Map(groupsList.map((g) => [g.id, g]));
    const byGid = new Map();
    rows.forEach((r) => {
      let gid = r[field];
      if (!gid || !known.has(gid)) gid = 'ungrouped';
      if (!byGid.has(gid)) byGid.set(gid, []);
      byGid.get(gid).push(r);
    });
    const sections = [];
    groupsList.forEach((g) => {
      const isCollapsed = collapsedSet.has(g.id);
      const secRows = byGid.get(g.id) || [];
      if (isCollapsed) {
        if ((counts[g.id] || 0) > 0) sections.push({ id: g.id, name: g.name, color: g.color, collapsed: true, rows: [], count: counts[g.id] });
      } else if (secRows.length) {
        sections.push({ id: g.id, name: g.name, color: g.color, collapsed: false, rows: secRows, count: counts[g.id] ?? secRows.length });
      }
    });
    const ung = byGid.get('ungrouped') || [];
    if (collapsedSet.has('ungrouped')) {
      if ((counts.ungrouped || 0) > 0) sections.push({ id: 'ungrouped', name: 'Ungrouped', color: null, collapsed: true, rows: [], count: counts.ungrouped });
    } else if (ung.length) {
      sections.push({ id: 'ungrouped', name: 'Ungrouped', color: null, collapsed: false, rows: ung, count: counts.ungrouped ?? ung.length });
    }
    return sections;
  };

  const groupHeaderButton = (tabKey, sec, rounded) => (
    <button
      className={`w-full flex items-center gap-1.5 bg-slate-200 dark:bg-slate-700 px-2 py-1.5 text-xs font-semibold hover:bg-slate-300 dark:hover:bg-slate-600 ${rounded ? 'rounded-md' : ''}`}
      onClick={() => toggleCollapsedGroup(tabKey, sec.id)}
      data-testid={`${tabKey}-group-header-${sec.name}`}
      type="button"
    >
      {sec.collapsed
        ? <ChevronRight className="w-3 h-3 text-slate-700 dark:text-slate-200" />
        : <ChevronDown className="w-3 h-3 text-slate-700 dark:text-slate-200" />}
      <Layers className="w-3 h-3" style={sec.color ? { color: resolveGroupTextColor(sec.color) } : undefined} />
      <span className="truncate" style={sec.color ? { color: resolveGroupTextColor(sec.color) } : { color: 'inherit' }}>{sec.name}</span>
      <span className="ml-auto text-[10px] font-normal text-slate-700 dark:text-slate-200">{sec.count}</span>
    </button>
  );

  const renderGroupedBody = (tabKey, rows, colSpan, renderRow) => {
    if (!groupedView[tabKey]) return rows.map(renderRow);
    return buildGroupSections(tabKey, rows).map((sec) => (
      <React.Fragment key={`grp-${sec.id}`}>
        <TableRow className="hover:bg-transparent">
          <TableCell colSpan={colSpan} className="p-0">
            {groupHeaderButton(tabKey, sec, false)}
          </TableCell>
        </TableRow>
        {!sec.collapsed && sec.rows.map(renderRow)}
      </React.Fragment>
    ));
  };

  // Mobile card list equivalent of renderGroupedBody (group headers + cards).
  const renderGroupedCards = (tabKey, rows, renderCard) => {
    if (!groupedView[tabKey]) return rows.map(renderCard);
    return buildGroupSections(tabKey, rows).map((sec) => (
      <React.Fragment key={`grp-${sec.id}`}>
        {groupHeaderButton(tabKey, sec, true)}
        {!sec.collapsed && sec.rows.map(renderCard)}
      </React.Fragment>
    ));
  };

  // Stacked person card used on mobile/WebView instead of the wide table row.
  const renderPersonCard = (personType, prefix, person, { selected, onToggleSelect, onEdit, onDelete, middle }) => (
    <div key={person.id} className="rounded-lg border bg-card p-3 space-y-2.5" data-testid={`${prefix}-card-${person.id}`}>
      <div className="flex items-start gap-2.5">
        <Checkbox
          className="mt-2.5 shrink-0 h-5 w-5"
          checked={selected}
          onCheckedChange={onToggleSelect}
          data-testid={`${prefix}-card-select-${person.id}`}
        />
        <PersonAvatar person={person} fallbackClass={PERSON_AVATAR_CLS[personType]} testid={`${prefix}-avatar-${person.id}`} onClick={() => openProfile(personType, person)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <button type="button" className="font-semibold text-base text-left hover:underline hover:text-[#2563eb] break-words" onClick={() => openProfile(personType, person)} title="View profile" data-testid={`${prefix}-name-btn-${person.id}`}>{person.full_name || person.name}</button>
            <RowWarningIcon person={person} prefix={prefix} large />
          </div>
          <PersonIdentityTags person={person} prefix={prefix} compact large />
        </div>
        <div className="shrink-0">{renderPersonActions(personType, prefix, person, onEdit, onDelete)}</div>
      </div>
      {(person.email || person.phone) && <div className="pl-9">{renderContactCell(person, true)}</div>}
      {middle}
      <div className="flex items-center gap-2.5 pl-9">
        <Badge variant="outline" className={`text-sm capitalize ${statusColors[person.status] || statusColors.inactive}`}>
          {person.status || 'inactive'}
        </Badge>
        {person.linked_user_id ? <LinkedAccountIcon testid={`${prefix}-linked-badge-${person.id}`} large /> : <ChatStatusIcon status={person.chat_invite_status} testid={`${prefix}-chat-status-${person.id}`} large />}
        {personType === 'staff' && <DriverEligibleIcon person={person} large />}
        <DocumentsChip person={person} prefix={prefix} large onClick={() => setAttachmentsViewer({ personType, person })} />
        {hasGreenDriving && personType !== 'passenger' && <BehaviourScoreBadge person={person} map={behaviourMap} prefix={prefix} large />}
      </div>
    </div>
  );
  const pagedRfidCards = rfidCards;
  const pagedPersonalBeacons = personalBeacons;


  return (
    <div className="space-y-6" data-testid="people-page">
      {/* Page Header */}
      <div className="hidden lg:flex items-center gap-3">
        <div className="p-2 rounded-lg bg-[#2563eb]/10">
          <Users className="w-6 h-6 text-[#2563eb]" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">People Management</h1>
          <p className="text-sm text-muted-foreground">Manage drivers, staff, and passengers</p>
        </div>
      </div>

      {/* Sub-navigation Tabs */}
      <MobileSectionMenu
        items={[
          hasDrivers && { id: 'drivers', label: 'Drivers', icon: User },
          hasStaff && { id: 'staff', label: 'Staff', icon: Briefcase },
          hasPassengers && { id: 'passengers', label: 'Passengers', icon: UserCircle },
          hasPersonalBeacons && { id: 'personal-beacons', label: 'Personal Beacons', icon: Bluetooth },
          hasRfidCards && { id: 'rfid-cards', label: 'RFID / iButton Cards', icon: CreditCard },
          hasDriverCorrections && { id: 'driver-corrections', label: 'Driver Corrections', icon: Shield },
        ].filter(Boolean)}
        activeId={activeTab}
        onChange={changeTab}
        testIdPrefix="people-tabs"
      />
      <div className="hidden sm:flex gap-2 flex-wrap">
        {hasDrivers && (
          <Button
            onClick={() => changeTab('drivers')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'drivers' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-drivers"
          >
            <User className="w-4 h-4" />
            Drivers
          </Button>
        )}
        {hasStaff && (
          <Button
            onClick={() => changeTab('staff')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'staff' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-staff"
          >
            <Briefcase className="w-4 h-4" />
            Staff
          </Button>
        )}
        {hasPassengers && (
          <Button
            onClick={() => changeTab('passengers')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'passengers' 
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg' 
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-passengers"
          >
            <UserCircle className="w-4 h-4" />
            Passengers
          </Button>
        )}
        {hasPersonalBeacons && (
          <Button
            onClick={() => changeTab('personal-beacons')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'personal-beacons'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-personal-beacons"
          >
            <Bluetooth className="w-4 h-4" />
            Personal Beacons
          </Button>
        )}
        {hasRfidCards && (
          <Button
            onClick={() => changeTab('rfid-cards')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'rfid-cards'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-rfid-cards"
          >
            <CreditCard className="w-4 h-4" />
            RFID / iButton Cards
          </Button>
        )}
        {hasDriverCorrections && (
          <Button
            onClick={() => changeTab('driver-corrections')}
            className={`gap-2 min-w-[200px] justify-center ${activeTab === 'driver-corrections'
              ? 'bg-[#2563eb] hover:bg-[#1d4ed8] text-white shadow-lg'
              : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700'
            }`}
            data-testid="tab-driver-corrections"
          >
            <Shield className="w-4 h-4" />
            Driver Corrections
            {pendingReviewCount > 0 && (
              <Badge className="bg-red-500 text-white text-xs px-1.5 py-0" data-testid="driver-corrections-pending-badge">{pendingReviewCount}</Badge>
            )}
          </Button>
        )}
      </div>

      {/* No Access Message - SECURITY FIX */}
      {!activeTab && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Shield className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
            <p className="text-lg font-medium text-muted-foreground">No Access</p>
            <p className="text-sm text-muted-foreground mt-2">You do not have permission to access any sections of People Management.</p>
            <p className="text-xs text-muted-foreground mt-4">Contact your administrator to request access.</p>
          </CardContent>
        </Card>
      )}

      {activeTab === 'personal-beacons' && hasPersonalBeacons && (
        <div className="space-y-4" data-testid="personal-beacons-tab-content">
          {/* Title block — mirrors Driver Corrections: icon + title + description. */}
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Bluetooth className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Personal Beacons</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1" data-testid="personal-beacons-readonly-note">
                Personal ID Beacons are managed by your administrator. You can assign an available beacon to a driver, staff member or passenger.
              </p>
            </div>
          </div>
          {/* Filters / toolbar row */}
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search beacons..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="personal-beacons-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="personal-beacons-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button size="sm" variant="outline" onClick={handleExportBeacons} disabled={selectedBeaconIds.length === 0} data-testid="personal-beacon-export-button">
              <Upload className="w-4 h-4 mr-2" />
              {selectedBeaconIds.length > 0 ? `Export (${selectedBeaconIds.length})` : 'Export'}
            </Button>
            <Button size="sm" variant="outline" onClick={fetchData} data-testid="personal-beacon-refresh-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Count badge — Driver Corrections style, always visible. */}
          <div className="flex flex-wrap items-center gap-2" data-testid="personal-beacons-counts-row">
            <Badge variant="outline" data-testid="personal-beacons-total-badge">{peopleTotals['personal-beacons']} Total</Badge>
          </div>

          {/* Selection bar — its own row directly below the toolbar, matching
              the Driver Corrections layout. Only visible when rows are ticked. */}
          <PeopleSelectionBar
            count={selectedBeaconIds.length}
            total={peopleTotals['personal-beacons']}
            busy={selectAllBusyTab === 'personal-beacons'}
            onToggleAll={() => handleSelectAllMatching('personal-beacons')}
            onClear={() => setSelectedBeaconIds([])}
            prefix="personal-beacons"
          />

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle>All Personal Beacons</CardTitle>
                <Badge variant="secondary" data-testid="personal-beacons-count">{peopleTotals['personal-beacons']} {peopleTotals['personal-beacons'] === 1 ? 'beacon' : 'beacons'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={personalBeacons.length > 0 && personalBeacons.every((b) => b.id && selectedBeaconIds.includes(b.id))}
                        onCheckedChange={handleToggleAllBeacons}
                        data-testid="personal-beacons-select-all"
                      />
                    </TableHead>
                    <TableHead><SortHeader label="Name" sortKey="name" sort={beaconSort} onSort={toggleBeaconSort} testid="sort-beacon-name" /></TableHead>
                    <TableHead><SortHeader label="Type" sortKey="type" sort={beaconSort} onSort={toggleBeaconSort} testid="sort-beacon-type" /></TableHead>
                    <TableHead><SortHeader label="Battery" sortKey="battery" sort={beaconSort} onSort={toggleBeaconSort} testid="sort-beacon-battery" /></TableHead>
                    <TableHead><SortHeader label="Status" sortKey="status" sort={beaconSort} onSort={toggleBeaconSort} testid="sort-beacon-status" /></TableHead>
                    <TableHead><SortHeader label="Assigned To" sortKey="assigned" sort={beaconSort} onSort={toggleBeaconSort} testid="sort-beacon-assigned" /></TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedPersonalBeacons.map((beacon) => {
                    const assigned = beacon.personal_beacon_assigned_to;
                    return (
                      <TableRow key={beacon.id} className="hover:bg-muted/50" data-testid={`personal-beacon-row-${beacon.id}`}>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedBeaconIds.includes(beacon.id)}
                            onCheckedChange={() => handleToggleBeaconSelection(beacon.id)}
                            data-testid={`personal-beacon-select-${beacon.id}`}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Bluetooth className="w-4 h-4 text-teal-600" />
                            <div>
                              <p className="font-medium">{beaconDisplayName(beacon)}</p>
                              <p className="text-xs text-muted-foreground font-mono">{beacon.imei || '-'}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>{beacon.beacon_type || 'BLE'}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{formatBeaconBattery(beacon)}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={assigned ? 'border-teal-200 text-teal-700 bg-teal-50' : 'border-amber-200 text-amber-700 bg-amber-50'}>
                            {assigned ? 'Assigned' : 'Unassigned'}
                          </Badge>
                        </TableCell>
                        <TableCell>{assigned ? `${assigned.type}: ${assigned.name || '-'}` : 'Available'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {assigned ? (
                              <Button size="sm" variant="outline" onClick={() => handleUnassignBeacon(beacon)} title="Unassign from Person" data-testid={`unassign-personal-beacon-button-${beacon.id}`}>
                                <UserMinus className="w-4 h-4 text-amber-600" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="outline" onClick={() => openAssignBeaconDialog(beacon)} title="Assign to Person" data-testid={`assign-personal-beacon-button-${beacon.id}`}>
                                <UserPlus className="w-4 h-4 text-[#2563eb]" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {visiblePersonalBeacons.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        {searchQuery ? 'No beacons match your search.' : 'No Personal ID Beacons are allocated to this account yet.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <PaginationFooter page={beaconsPage} setPage={setBeaconsPage} totalItems={peopleTotals['personal-beacons']} itemsPerPage={peoplePageSize} setItemsPerPage={setPeoplePageSize} testIdPrefix="personal-beacons" alwaysShow />
            </CardContent>
          </Card>

          <Dialog open={assignBeaconModal.open} onOpenChange={(open) => { if (!open) setAssignBeaconModal({ open: false, beacon: null }); }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Assign Beacon to Person</DialogTitle>
                <DialogDescription>Bind {beaconDisplayName(assignBeaconModal.beacon)} to a driver, staff or passenger — no need to open their subpage.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Person Type</Label>
                  <Select value={assignBeaconForm.personType} onValueChange={(v) => setAssignBeaconForm({ personType: v, personId: '' })}>
                    <SelectTrigger data-testid="assign-beacon-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {hasDrivers && <SelectItem value="driver">Driver</SelectItem>}
                      {hasStaff && <SelectItem value="staff">Staff</SelectItem>}
                      {hasPassengers && <SelectItem value="passenger">Passenger</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Person</Label>
                  <PersonPicker
                    people={peopleForType(assignBeaconForm.personType).filter((p) => !p.personal_beacon_id)}
                    value={assignBeaconForm.personId}
                    onChange={(id) => setAssignBeaconForm((prev) => ({ ...prev, personId: id }))}
                    emptyLabel={`No unassigned ${assignBeaconForm.personType}s found`}
                    testidPrefix="assign-beacon-person"
                  />
                  <p className="text-[11px] text-muted-foreground">Only people without a beacon are shown.</p>
                </div>
              </div>
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => setAssignBeaconModal({ open: false, beacon: null })} data-testid="assign-beacon-cancel-button">Cancel</Button>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleAssignBeacon} data-testid="assign-beacon-confirm-button">Assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {activeTab === 'rfid-cards' && hasRfidCards && (
        <div className="space-y-4" data-testid="rfid-cards-tab-content">
          <UnmatchedScansModal open={unmatchedModalOpen} onOpenChange={(o) => { setUnmatchedModalOpen(o); if (!o) setAssignCardCredential(''); }} api={api} onChanged={fetchData} initialCredential={assignCardCredential} />
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">RFID / iButton Cards</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Assign and manage RFID or iButton cards for drivers, staff, and passengers.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search cards..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="rfid-cards-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="rfid-cards-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setUnmatchedModalOpen(true)} data-testid="rfid-unmatched-scans-button">
              <ScanLine className="w-4 h-4 mr-2" />
              Unmatched scans
              {unmatchedScans.length > 0 && (
                <Badge className="ml-2 bg-red-500 text-white text-xs px-1.5 py-0" data-testid="rfid-unmatched-scans-badge">{unmatchedScans.length}</Badge>
              )}
            </Button>
            <input ref={rfidImportRef} type="file" accept=".csv,text/csv" className="hidden" onChange={handleImportRfidCards} data-testid="rfid-card-import-input" />
            <Button variant="outline" size="sm" onClick={() => rfidImportRef.current?.click()} data-testid="rfid-card-import-button">
              <Download className="w-4 h-4 mr-2" />
              Import
            </Button>
            <Button size="sm" variant="outline" onClick={handleExportRfidCards} disabled={selectedRfidCardIds.length === 0} data-testid="rfid-card-export-button">
              <Upload className="w-4 h-4 mr-2" />
              {selectedRfidCardIds.length > 0 ? `Export (${selectedRfidCardIds.length})` : 'Export'}
            </Button>
            {selectedRfidCardIds.length > 0 && (
              <Button variant="destructive" size="sm" onClick={handleDeleteSelectedRfidCards} data-testid="rfid-card-delete-selected-button">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete ({selectedRfidCardIds.length})
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={fetchData} data-testid="rfid-card-refresh-button">
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Dialog open={isRfidDialogOpen} onOpenChange={(open) => {
              if (!open) { setIsRfidDialogOpen(false); setSelectedRfidCard(null); }
              else { setIsRfidDialogOpen(true); }
            }}>
              <DialogTrigger asChild>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" onClick={openAddRfidDialog} data-testid="rfid-card-add-button">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Card
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{selectedRfidCard ? 'Edit Card' : 'Add RFID / iButton Card'}</DialogTitle>
                  <DialogDescription>Cards are tenant isolated and can be assigned to drivers, staff or passengers.</DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label>Card Value *</Label>
                    <Input
                      value={rfidCardForm.card_value}
                      onChange={(e) => setRfidCardForm(prev => ({ ...prev, card_value: e.target.value }))}
                      placeholder="Parsed RFID / iButton value"
                      data-testid="rfid-card-value-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Label</Label>
                    <Input
                      value={rfidCardForm.label}
                      onChange={(e) => setRfidCardForm(prev => ({ ...prev, label: e.target.value }))}
                      placeholder="Friendly label (optional)"
                      data-testid="rfid-card-label-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Status</Label>
                    <Select
                      value={rfidCardForm.status}
                      onValueChange={(v) => setRfidCardForm(prev => ({ ...prev, status: v }))}
                      disabled={!!selectedRfidCard?.assigned_person_id}
                    >
                      <SelectTrigger data-testid="rfid-card-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="available">Available</SelectItem>
                        <SelectItem value="disabled">Disabled</SelectItem>
                        {selectedRfidCard?.assigned_person_id && <SelectItem value="assigned">Assigned</SelectItem>}
                      </SelectContent>
                    </Select>
                    {selectedRfidCard?.assigned_person_id && (
                      <p className="text-xs text-muted-foreground">Unassign this card from the person before changing its status.</p>
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label>Notes</Label>
                    <Textarea
                      value={rfidCardForm.notes}
                      onChange={(e) => setRfidCardForm(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Notes (optional)"
                      data-testid="rfid-card-notes-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button size="sm" variant="outline" onClick={() => { setIsRfidDialogOpen(false); setSelectedRfidCard(null); }} data-testid="rfid-card-cancel-button">Cancel</Button>
                  <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleSaveRfidCard} data-testid="rfid-card-save-button">{selectedRfidCard ? 'Save Changes' : 'Add Card'}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="rfid-cards-counts-row">
            <Badge variant="outline" data-testid="rfid-cards-total-badge">{peopleTotals['rfid-cards']} Total</Badge>
          </div>

          <PeopleSelectionBar
            count={selectedRfidCardIds.length}
            total={peopleTotals['rfid-cards']}
            busy={selectAllBusyTab === 'rfid-cards'}
            onToggleAll={() => handleSelectAllMatching('rfid-cards')}
            onClear={() => setSelectedRfidCardIds([])}
            prefix="rfid-cards"
          />

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle>All RFID / iButton Cards</CardTitle>
                <Badge variant="secondary" data-testid="rfid-cards-count">{peopleTotals['rfid-cards']} {peopleTotals['rfid-cards'] === 1 ? 'card' : 'cards'}</Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={rfidCards.length > 0 && rfidCards.every((c) => c.id && selectedRfidCardIds.includes(c.id))}
                        onCheckedChange={handleToggleAllRfid}
                        data-testid="rfid-cards-select-all"
                      />
                    </TableHead>
                    <TableHead><SortHeader label="Label" sortKey="label" sort={cardSort} onSort={toggleCardSort} testid="sort-card-label" /></TableHead>
                    <TableHead><SortHeader label="Value" sortKey="value" sort={cardSort} onSort={toggleCardSort} testid="sort-card-value" /></TableHead>
                    <TableHead><SortHeader label="Status" sortKey="status" sort={cardSort} onSort={toggleCardSort} testid="sort-card-status" /></TableHead>
                    <TableHead><SortHeader label="Assigned To" sortKey="assigned" sort={cardSort} onSort={toggleCardSort} testid="sort-card-assigned" /></TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pagedRfidCards.map((card) => (
                    <TableRow key={card.id} className="hover:bg-muted/50" data-testid={`rfid-card-row-${card.id}`}>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedRfidCardIds.includes(card.id)}
                          onCheckedChange={() => handleToggleRfidSelection(card.id)}
                          data-testid={`rfid-card-select-${card.id}`}
                        />
                      </TableCell>
                      <TableCell>{card.label || '-'}</TableCell>
                      <TableCell className="font-mono">{card.card_value}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`capitalize ${rfidStatusColors[card.status] || rfidStatusColors.available}`}>
                          {card.status || 'available'}
                        </Badge>
                      </TableCell>
                      <TableCell>{card.assigned_person_name ? `${card.assigned_person_type}: ${card.assigned_person_name}` : 'Available'}</TableCell>
                      <TableCell>
                        <div className="flex gap-1 items-center">
                          {card.assigned_person_id ? (
                            <Button size="sm" variant="outline" className={isMobileOrWebview ? 'h-9 w-9 p-0' : ''} onClick={() => handleUnassignCard(card)} title="Unassign from Person" data-testid={`unassign-rfid-card-button-${card.id}`}>
                              <UserMinus className={isMobileOrWebview ? '!w-5 !h-5 text-amber-600' : 'w-4 h-4 text-amber-600'} />
                            </Button>
                          ) : (
                            <Button size="sm" variant="outline" className={isMobileOrWebview ? 'h-9 w-9 p-0' : ''} onClick={() => openAssignCardDialog(card)} title="Assign to Person" data-testid={`assign-rfid-card-button-${card.id}`}>
                              <UserPlus className={isMobileOrWebview ? '!w-5 !h-5 text-[#2563eb]' : 'w-4 h-4 text-[#2563eb]'} />
                            </Button>
                          )}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="sm" variant="outline" className={isMobileOrWebview ? 'h-9 w-9 p-0' : ''} title="Actions" data-testid={`rfid-card-actions-menu-${card.id}`}>
                                <MoreVertical className={isMobileOrWebview ? '!w-5 !h-5' : 'w-4 h-4'} />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className={isMobileOrWebview ? 'w-64 p-1.5' : 'w-44'}>
                              <DropdownMenuItem className={isMobileOrWebview ? 'min-h-[52px] text-sm px-3 py-3' : ''} onSelect={() => openEditRfidDialog(card)} data-testid={`edit-rfid-card-button-${card.id}`}>
                                <Pencil className={isMobileOrWebview ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2'} /> Edit
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem className={`${isMobileOrWebview ? 'min-h-[52px] text-sm px-3 py-3' : ''} text-red-600 focus:text-red-600 dark:text-red-400 dark:focus:text-red-400`} disabled={!!card.assigned_person_id} onSelect={() => handleDeleteRfidCard(card)} data-testid={`rfid-card-delete-button-${card.id}`}>
                                <Trash2 className={isMobileOrWebview ? '!w-5 !h-5 mr-3' : 'w-4 h-4 mr-2'} /> Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleRfidCards.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {searchQuery ? 'No cards match your search.' : 'No RFID / iButton cards yet. Click "Add Card" to create one.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <PaginationFooter page={rfidPage} setPage={setRfidPage} totalItems={peopleTotals['rfid-cards']} itemsPerPage={peoplePageSize} setItemsPerPage={setPeoplePageSize} testIdPrefix="rfid-cards" alwaysShow />
            </CardContent>
          </Card>

          <Dialog open={assignCardModal.open} onOpenChange={(open) => { if (!open) setAssignCardModal({ open: false, card: null }); }}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Assign Card to Person</DialogTitle>
                <DialogDescription>Bind {assignCardModal.card?.label || assignCardModal.card?.card_value} to a driver, staff or passenger — no need to open their subpage.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Person Type</Label>
                  <Select value={assignForm.personType} onValueChange={(v) => setAssignForm({ personType: v, personId: '' })}>
                    <SelectTrigger data-testid="assign-card-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {hasDrivers && <SelectItem value="driver">Driver</SelectItem>}
                      {hasStaff && <SelectItem value="staff">Staff</SelectItem>}
                      {hasPassengers && <SelectItem value="passenger">Passenger</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Person</Label>
                  <PersonPicker
                    people={peopleForType(assignForm.personType).filter((p) => !p.rfid_ibutton_card_id)}
                    value={assignForm.personId}
                    onChange={(id) => setAssignForm((prev) => ({ ...prev, personId: id }))}
                    emptyLabel={`No unassigned ${assignForm.personType}s found`}
                    testidPrefix="assign-card-person"
                  />
                  <p className="text-[11px] text-muted-foreground">Only people without a card are shown.</p>
                </div>
              </div>
              <DialogFooter>
                <Button size="sm" variant="outline" onClick={() => setAssignCardModal({ open: false, card: null })} data-testid="assign-card-cancel-button">Cancel</Button>
                <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8]" onClick={handleAssignCard} data-testid="assign-card-confirm-button">Assign</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {/* ==================== DRIVER CORRECTIONS TAB ==================== */}
      {activeTab === 'driver-corrections' && hasDriverCorrections && (
        <DriverCorrectionsTab onPendingCountChange={setPendingReviewCount} />
      )}

      {/* ==================== DRIVERS TAB ==================== */}
      {activeTab === 'drivers' && hasDrivers && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Drivers</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Add, invite, and manage drivers with device assignments and role privileges.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search drivers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="drivers-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="drivers-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setGroupsModalOpen(true)} data-testid="driver-manage-groups-button">
              <Settings2 className="w-4 h-4 mr-2" /> Manage Groups
            </Button>
            <Button variant={groupedView.drivers ? 'default' : 'outline'} size="sm" onClick={() => toggleGroupedView('drivers')} title={groupedView.drivers ? 'Switch to flat view' : 'Group drivers by their group'} data-testid="driver-grouped-view-toggle">
              <Layers className="w-4 h-4 mr-2" /> Grouped
            </Button>
            <input
              type="file"
              accept=".csv"
              className="hidden"
              id="drivers-csv-import"
            />
            <PeopleImportDropdown type="drivers" onOpenWizard={() => setImportWizardType('drivers')} />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="export-drivers-btn" disabled={selectedDriverIds.length === 0}>
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedDriverIds.length > 0 ? `Export (${selectedDriverIds.length})` : 'Export'}
                    <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="text-xs">Export Drivers</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={handleExportDriversCSV} data-testid="export-drivers-csv">
                    <FileText className="w-4 h-4 mr-2" />
                    Drivers (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleExportDriversXlsx} data-testid="export-drivers-xlsx">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                    Drivers Excel-Safe (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleExportFullBackup('drivers')} data-testid="export-drivers-zip">
                    <Archive className="w-4 h-4 mr-2 text-[#2563eb]" />
                    Full Backup with photos (.zip)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {selectedDriverIds.length > 0 && (
                <>
                  <BulkActionsMenu
                    count={selectedDriverIds.length}
                    onStatus={(s) => handleBulkStatus('driver', selectedDriverIds, s)}
                    onResend={() => handleBulkResendInvites('driver', selectedDriverIds)}
                    groups={peopleGroups.drivers}
                    onMoveToGroup={(gid) => handleBulkMoveToGroup('driver', selectedDriverIds, gid)}
                    testPrefix="driver"
                  />
                  <Button variant="destructive" size="sm" onClick={handleDeleteSelectedDrivers}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedDriverIds.length})
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={isDriverDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  handlePeopleDialogClose(() => { setIsDriverDialogOpen(false); setSelectedDriver(null); resetDriverForm(); });
                } else {
                  setIsDriverDialogOpen(true);
                }
              }}>
                <span title={addBlocked.drivers ? addBlockedTitle('Drivers', peopleLimits.drivers) : undefined}>
                  <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" disabled={addBlocked.drivers} onClick={() => setIsDriverDialogOpen(true)} data-testid="add-driver-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Driver
                  </Button>
                </span>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedDriver ? 'Edit Driver' : 'Add New Driver'}</DialogTitle>
                    <DialogDescription>Enter driver information and details</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <LinkedAccountField options={linkableUsers} value={driverForm.linked_user_id} onChange={handleLinkChange(setDriverForm, !!selectedDriver)} testPrefix="driver" />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Employee ID</Label>
                        <Input
                          value={driverForm.employee_id}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, employee_id: e.target.value }))}
                          placeholder="EMP001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          value={driverForm.full_name}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, full_name: e.target.value }))}
                          placeholder="John Smith"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={driverForm.email}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="john@example.com"
                          data-testid="driver-email-input"
                        />
                        <EmailAvailabilityHint result={driverEmailAvail} testid="driver-email-availability" />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={driverForm.phone}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+61 412 345 678"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>License Number *</Label>
                        <Input
                          value={driverForm.license_number}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, license_number: e.target.value }))}
                          placeholder="NSW-12345"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>License Expiry *</Label>
                        <Input
                          type="date"
                          value={driverForm.license_expiry}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, license_expiry: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Textarea
                        value={driverForm.address}
                        onChange={(e) => setDriverForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Main St, Sydney NSW 2000"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={driverForm.date_of_birth}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date Started</Label>
                        <Input
                          type="date"
                          value={driverForm.date_started}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, date_started: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Emergency Contact Name</Label>
                        <Input
                          value={driverForm.emergency_contact_name}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                          placeholder="Jane Smith"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emergency Contact Phone</Label>
                        <Input
                          value={driverForm.emergency_contact_phone}
                          onChange={(e) => setDriverForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                          placeholder="+61 400 000 000"
                        />
                      </div>
                    </div>

                    <PersonPhotoField api={api} personType="driver" person={selectedDriver} onChange={(ph) => applyPersonPhoto('driver', selectedDriver?.id, ph)} />

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={driverForm.notes}
                        onChange={(e) => setDriverForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Additional notes..."
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <PersonStatusField value={driverForm.status} onChange={(v) => setDriverForm(prev => ({ ...prev, status: v }))} testPrefix="driver" />
                      <PersonGroupField options={peopleGroups.drivers} value={driverForm.person_group} onChange={(v) => setDriverForm(prev => ({ ...prev, person_group: v }))} testPrefix="driver" />
                    </div>

                    <PersonalBeaconField
                      value={driverForm.personal_beacon_id}
                      onChange={(v) => setDriverForm(prev => ({ ...prev, personal_beacon_id: v }))}
                      beacons={beaconInventory}
                      currentPersonId={selectedDriver?.id}
                      personType="driver"
                      testPrefix="driver"
                    />

                    <CredentialFields form={driverForm} setForm={setDriverForm} testPrefix="driver" cards={availableRfidCardsFor(driverForm)} />

                    {driverForm.linked_user_id ? (
                      <p className="text-xs text-muted-foreground border rounded-md p-2" data-testid="driver-linked-note">
                        This driver is linked to a real account and uses that login — no separate sign-up needed.
                      </p>
                    ) : (
                    <AccountAccessSection
                      form={driverForm}
                      prefix="driver"
                      personLabel="driver"
                      inviteStatus={selectedDriver?.chat_invite_status}
                      onAccountToggle={(v) => handleAccountToggle(v, setDriverForm, selectedDriver?.chat_invite_status, driverForm.full_name)}
                      onChatToggle={(v) => { setDriverForm(prev => ({ ...prev, messaging_enabled: v })); }}
                      onResend={selectedDriver?.chat_user_id ? () => handleResendInvite('driver', selectedDriver) : null}
                      onShowQr={selectedDriver?.chat_user_id ? () => setQrPerson({ person: selectedDriver, type: 'driver' }) : null}
                      resending={resendingInvite}
                      onActiveToggle={selectedDriver?.chat_user_id ? (v) => handleAccountActiveToggle('driver', selectedDriver, v, (s) => {
                        setSelectedDriver((p) => (p ? { ...p, chat_invite_status: s, account_enabled: true } : p));
                        setDriverForm((f) => ({ ...f, account_enabled: true, confirm_account_deactivation: false }));
                      }) : null}
                      statusBusy={accountStatusBusy}
                      pushEnabled={!!selectedDriver?.push_notifications_enabled}
                      onPushToggle={selectedDriver?.chat_user_id ? (v) => handleAccountPushToggle('driver', selectedDriver, v, (enabled) => {
                        setSelectedDriver((p) => (p ? { ...p, push_notifications_enabled: enabled } : p));
                      }) : null}
                      pushBusy={accountPushBusy}
                      isNotificationChannelActive={isNotificationChannelActive}
                      emailChecked={inviteEmail}
                      smsChecked={inviteSms}
                      onToggleEmail={setInviteEmail}
                      onToggleSms={setInviteSms}
                    />
                    )}
                    <div className="sm:col-span-2 border-t pt-3 mt-1">
                      <PersonAttachments personType="driver" person={selectedDriver} onChange={(atts) => applyPersonAttachments('driver', selectedDriver?.id, atts)} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button size="sm" variant="outline" onClick={() => handlePeopleDialogClose(() => { setIsDriverDialogOpen(false); setSelectedDriver(null); resetDriverForm(); })}>Cancel</Button>
                    <Button size="sm" 
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      onClick={selectedDriver ? handleEditDriver : handleCreateDriver}
                      data-testid={selectedDriver ? 'driver-update-submit-button' : 'driver-create-submit-button'}
                    >
                      {selectedDriver ? 'Update Driver' : 'Create Driver'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="drivers-counts-row">
            {peopleLimits.drivers !== -1 ? (
              <Badge
                variant={peopleLimits.drivers > 0 && peopleCounts.drivers >= peopleLimits.drivers ? 'destructive' : 'outline'}
                title={peopleLimits.drivers === 0 ? 'Upgrade required — contact your administrator to add Drivers.' : 'Driver Limit'}
                data-testid="drivers-limit-badge"
              >
                Driver Limit {peopleCounts.drivers} / {peopleLimits.drivers === 0 ? 'Upgrade Required' : peopleLimits.drivers}
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="drivers-total-badge">{peopleTotals.drivers} Total</Badge>
            )}
          </div>

          <PeopleSelectionBar
            count={selectedDriverIds.length}
            total={peopleTotals.drivers}
            busy={selectAllBusyTab === 'drivers'}
            onToggleAll={() => handleSelectAllMatching('drivers')}
            onClear={() => setSelectedDriverIds([])}
            prefix="drivers"
          />

          {/* Drivers Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle>All Drivers</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <CurrentlyInFilterBadge
                    count={currentlyInCounts.drivers}
                    active={driverInOnly}
                    onToggle={() => setDriverInOnly((v) => !v)}
                    testid="driver-currently-in-stat"
                  />
                  <LicenseExpiryFilterBadge
                    count={licenseAlertCounts.drivers}
                    active={driverLicenseOnly}
                    onToggle={() => setDriverLicenseOnly((v) => !v)}
                    testid="driver-license-alerts-stat"
                  />
                  <DocsExpiryFilterBadge
                    count={docsAlertCounts.drivers}
                    active={driverDocsOnly}
                    onToggle={() => setDriverDocsOnly((v) => !v)}
                    testid="driver-docs-alerts-stat"
                  />
                  <PendingInviteFilterBadge
                    count={pendingInviteCounts.drivers}
                    active={driverPendingOnly}
                    onToggle={() => setDriverPendingOnly((v) => !v)}
                    testid="driver-pending-invites-stat"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isMobileOrWebview ? (
              <div className="space-y-2" data-testid="driver-card-list">
                {renderGroupedCards('drivers', pagedDrivers, (driver) => renderPersonCard('driver', 'driver', driver, {
                  selected: selectedDriverIds.includes(driver.id),
                  onToggleSelect: () => handleToggleDriverSelection(driver.id),
                  onEdit: () => openEditDriverDialog(driver),
                  onDelete: () => handleDeleteDriver(driver.id),
                  middle: (driver.employee_id || driver.license_number) ? (
                    <p className="pl-9 text-sm text-muted-foreground">
                      {[driver.employee_id, driver.license_number ? `License ${driver.license_number}` : null].filter(Boolean).join(' · ')}
                    </p>
                  ) : null,
                }))}
                {visibleDrivers.length === 0 && !(groupedView.drivers && collapsedGroups.drivers.size > 0) && (
                  <p className="text-center py-8 text-sm text-muted-foreground" data-testid="driver-cards-empty">
                    {driverInOnly ? 'No one is currently in a vehicle.' : driverLicenseOnly ? 'No expired or expiring licenses. All clear.' : driverPendingOnly ? 'No pending sign-ups.' : 'No drivers found. Click "Add Driver" to create one.'}
                  </p>
                )}
              </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={drivers.length > 0 && drivers.every((d) => d.id && selectedDriverIds.includes(d.id))}
                        onCheckedChange={handleToggleAllDrivers}
                      />
                    </TableHead>
                    <TableHead><SortHeader label="Driver" sortKey="name" sort={peopleSort} onSort={togglePeopleSort} testid="sort-driver-name" /></TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead><SortHeader label="Employee ID" sortKey="employee_id" sort={peopleSort} onSort={togglePeopleSort} testid="sort-driver-employee-id" /></TableHead>
                    <TableHead><SortHeader label="License" sortKey="license_expiry" sort={peopleSort} onSort={togglePeopleSort} testid="sort-driver-license-expiry" /></TableHead>
                    <TableHead><SortHeader label="Status" sortKey="status" sort={peopleSort} onSort={togglePeopleSort} testid="sort-driver-status" /></TableHead>
                    {hasGreenDriving && <TableHead>Score</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderGroupedBody('drivers', pagedDrivers, 8, (driver) => (
                    <TableRow key={driver.id} className="hover:bg-muted/50">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedDriverIds.includes(driver.id)}
                          onCheckedChange={() => handleToggleDriverSelection(driver.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <PersonAvatar person={driver} fallbackClass="bg-[#2563eb] text-white" testid={`driver-avatar-${driver.id}`} onClick={() => openProfile('driver', driver)} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <button type="button" className="font-medium text-left hover:underline hover:text-[#2563eb]" onClick={() => openProfile('driver', driver)} title="View profile" data-testid={`driver-name-btn-${driver.id}`}>{driver.full_name || driver.name}</button>
                              <RowWarningIcon person={driver} prefix="driver" />
                            </div>
                            <PersonIdentityTags person={driver} prefix="driver" compact />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{renderContactCell(driver)}</TableCell>
                      <TableCell>{driver.employee_id || '-'}</TableCell>
                      <TableCell>
                        <span className="text-sm">{driver.license_number || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`capitalize ${statusColors[driver.status] || statusColors.inactive}`} title="Person status — set in the edit dialog.">
                            {driver.status || 'inactive'}
                          </Badge>
                          {driver.linked_user_id ? <LinkedAccountIcon testid={`driver-linked-badge-${driver.id}`} /> : <ChatStatusIcon status={driver.chat_invite_status} testid={`driver-chat-status-${driver.id}`} />}
                          <DocumentsChip person={driver} prefix="driver" onClick={() => setAttachmentsViewer({ personType: 'driver', person: driver })} />
                        </div>
                      </TableCell>
                      {hasGreenDriving && (
                        <TableCell>
                          <BehaviourScoreBadge person={driver} map={behaviourMap} prefix="driver" />
                        </TableCell>
                      )}
                      <TableCell>
                        {renderPersonActions('driver', 'driver', driver, () => openEditDriverDialog(driver), () => handleDeleteDriver(driver.id))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleDrivers.length === 0 && !(groupedView.drivers && collapsedGroups.drivers.size > 0) && (
                    <TableRow>
                      <TableCell colSpan={hasGreenDriving ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        {driverInOnly ? 'No one is currently in a vehicle.' : driverLicenseOnly ? 'No expired or expiring licenses. All clear.' : driverPendingOnly ? 'No pending sign-ups.' : 'No drivers found. Click "Add Driver" to create one.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              )}
              <PaginationFooter page={driversPage} setPage={setDriversPage} totalItems={peopleTotals.drivers} itemsPerPage={peoplePageSize} setItemsPerPage={setPeoplePageSize} testIdPrefix="drivers" alwaysShow />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== STAFF TAB ==================== */}
      {activeTab === 'staff' && hasStaff && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <Briefcase className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Staff</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Add, invite, and manage staff members with device assignments and privileges.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="staff-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="staff-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setGroupsModalOpen(true)} data-testid="staff-manage-groups-button">
              <Settings2 className="w-4 h-4 mr-2" /> Manage Groups
            </Button>
            <Button variant={groupedView.staff ? 'default' : 'outline'} size="sm" onClick={() => toggleGroupedView('staff')} title={groupedView.staff ? 'Switch to flat view' : 'Group staff by their group'} data-testid="staff-grouped-view-toggle">
              <Layers className="w-4 h-4 mr-2" /> Grouped
            </Button>
            <PeopleImportDropdown type="staff" onOpenWizard={() => setImportWizardType('staff')} />
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="export-staff-btn" disabled={selectedStaffIds.length === 0}>
                    <Upload className="w-4 h-4 mr-2" />
                    {selectedStaffIds.length > 0 ? `Export (${selectedStaffIds.length})` : 'Export'}
                    <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <DropdownMenuLabel className="text-xs">Export Staff</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={handleExportStaffCSV} data-testid="export-staff-csv">
                    <FileText className="w-4 h-4 mr-2" />
                    Staff (.csv)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleExportStaffXlsx} data-testid="export-staff-xlsx">
                    <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                    Staff Excel-Safe (.xlsx)
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={handleExportFullBackup('staff')} data-testid="export-staff-zip">
                    <Archive className="w-4 h-4 mr-2 text-[#2563eb]" />
                    Full Backup with photos (.zip)
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {selectedStaffIds.length > 0 && (
                <>
                  <BulkActionsMenu
                    count={selectedStaffIds.length}
                    onStatus={(s) => handleBulkStatus('staff', selectedStaffIds, s)}
                    onResend={() => handleBulkResendInvites('staff', selectedStaffIds)}
                    groups={peopleGroups.staff}
                    onMoveToGroup={(gid) => handleBulkMoveToGroup('staff', selectedStaffIds, gid)}
                    testPrefix="staff"
                  />
                  <Button variant="destructive" size="sm" onClick={handleDeleteSelectedStaff}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedStaffIds.length})
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={isStaffDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  handlePeopleDialogClose(() => { setIsStaffDialogOpen(false); setSelectedStaffMember(null); resetStaffForm(); });
                } else {
                  setIsStaffDialogOpen(true);
                }
              }}>
                <span title={addBlocked.staff ? addBlockedTitle('Staff', peopleLimits.staff) : undefined}>
                  <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" disabled={addBlocked.staff} onClick={() => setIsStaffDialogOpen(true)} data-testid="add-staff-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Staff
                  </Button>
                </span>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedStaffMember ? 'Edit Staff Member' : 'Add New Staff Member'}</DialogTitle>
                    <DialogDescription>Enter staff information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <LinkedAccountField options={linkableUsers} value={staffForm.linked_user_id} onChange={handleLinkChange(setStaffForm, !!selectedStaffMember)} testPrefix="staff" />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Employee ID</Label>
                        <Input
                          value={staffForm.employee_id}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, employee_id: e.target.value }))}
                          placeholder="EMP001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          value={staffForm.full_name}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, full_name: e.target.value }))}
                          placeholder="Emma Wilson"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={staffForm.email}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="emma@example.com"
                          data-testid="staff-email-input"
                        />
                        <EmailAvailabilityHint result={staffEmailAvail} testid="staff-email-availability" />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={staffForm.phone}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+61 445 678 901"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Department *</Label>
                        <Select
                          value={staffForm.department}
                          onValueChange={(v) => setStaffForm(prev => ({ ...prev, department: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select department" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="operations">Operations</SelectItem>
                            <SelectItem value="sales">Sales</SelectItem>
                            <SelectItem value="service">Service</SelectItem>
                            <SelectItem value="safety & compliance">Safety & Compliance</SelectItem>
                            <SelectItem value="finance">Finance</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            <SelectItem value="management">Management</SelectItem>
                            <SelectItem value="technical">Technical</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Role *</Label>
                        <Select
                          value={staffForm.role}
                          onValueChange={(v) => setStaffForm(prev => ({ ...prev, role: v }))}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="supervisor">Supervisor</SelectItem>
                            <SelectItem value="coordinator">Coordinator</SelectItem>
                            <SelectItem value="fleet manager">Fleet Manager</SelectItem>
                            <SelectItem value="driver">Driver</SelectItem>
                            <SelectItem value="sales person">Sales Person</SelectItem>
                            <SelectItem value="safety officer">Safety Officer</SelectItem>
                            <SelectItem value="finance officer">Finance Officer</SelectItem>
                            <SelectItem value="fitter / installer">Fitter / Installer</SelectItem>
                            <SelectItem value="dispatcher">Dispatcher</SelectItem>
                            <SelectItem value="workshop manager">Workshop Manager</SelectItem>
                            <SelectItem value="auditor">Auditor</SelectItem>
                            <SelectItem value="analyst">Analyst</SelectItem>
                            <SelectItem value="technician">Technician</SelectItem>
                            <SelectItem value="other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Textarea
                        value={staffForm.address}
                        onChange={(e) => setStaffForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Main St, Sydney NSW 2000"
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Date of Birth</Label>
                        <Input
                          type="date"
                          value={staffForm.date_of_birth}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Date Started</Label>
                        <Input
                          type="date"
                          value={staffForm.date_started}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, date_started: e.target.value }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Emergency Contact Name</Label>
                        <Input
                          value={staffForm.emergency_contact_name}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                          placeholder="Jane Wilson"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emergency Contact Phone</Label>
                        <Input
                          value={staffForm.emergency_contact_phone}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                          placeholder="+61 400 000 000"
                        />
                      </div>
                    </div>

                    <PersonPhotoField api={api} personType="staff" person={selectedStaffMember} onChange={(ph) => applyPersonPhoto('staff', selectedStaffMember?.id, ph)} />

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={staffForm.notes}
                        onChange={(e) => setStaffForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Additional notes..."
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <PersonStatusField value={staffForm.status} onChange={(v) => setStaffForm(prev => ({ ...prev, status: v }))} testPrefix="staff" />
                      <PersonGroupField options={peopleGroups.staff} value={staffForm.person_group} onChange={(v) => setStaffForm(prev => ({ ...prev, person_group: v }))} testPrefix="staff" />
                    </div>

                    <PersonalBeaconField
                      value={staffForm.personal_beacon_id}
                      onChange={(v) => setStaffForm(prev => ({ ...prev, personal_beacon_id: v }))}
                      beacons={beaconInventory}
                      currentPersonId={selectedStaffMember?.id}
                      personType="staff"
                      testPrefix="staff"
                    />

                    <CredentialFields form={staffForm} setForm={setStaffForm} testPrefix="staff" cards={availableRfidCardsFor(staffForm)} />

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>License Number{staffForm.driver_eligible ? ' *' : ''}</Label>
                        <Input
                          value={staffForm.license_number}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, license_number: e.target.value }))}
                          placeholder="NSW-12345"
                          data-testid="staff-license-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>License Expiry{staffForm.driver_eligible ? ' *' : ''}</Label>
                        <Input
                          type="date"
                          value={staffForm.license_expiry}
                          onChange={(e) => setStaffForm(prev => ({ ...prev, license_expiry: e.target.value }))}
                          data-testid="staff-license-expiry-input"
                        />
                      </div>
                    </div>

                    <div className="flex items-start gap-3 rounded-lg border p-3 bg-muted/30" data-testid="staff-driver-eligible-row">
                      <Checkbox
                        id="staff-driver-eligible"
                        checked={!!staffForm.driver_eligible}
                        onCheckedChange={(v) => setStaffForm(prev => ({ ...prev, driver_eligible: !!v }))}
                        className="mt-0.5"
                        data-testid="staff-driver-eligible-checkbox"
                      />
                      <div className="space-y-1">
                        <Label htmlFor="staff-driver-eligible" className="cursor-pointer">Eligible to drive</Label>
                        <p className="text-xs text-muted-foreground">
                          Allow this staff member to be considered as a driver by the BLE Driver Candidate Engine (they carry a personal beacon and may drive vehicles).
                        </p>
                      </div>
                    </div>

                    {staffForm.linked_user_id ? (
                      <p className="text-xs text-muted-foreground border rounded-md p-2" data-testid="staff-linked-note">
                        This staff member is linked to a real account and uses that login — no separate sign-up needed.
                      </p>
                    ) : (
                    <AccountAccessSection
                      form={staffForm}
                      prefix="staff"
                      personLabel="staff member"
                      inviteStatus={selectedStaffMember?.chat_invite_status}
                      onAccountToggle={(v) => handleAccountToggle(v, setStaffForm, selectedStaffMember?.chat_invite_status, staffForm.full_name)}
                      onChatToggle={(v) => { setStaffForm(prev => ({ ...prev, messaging_enabled: v })); }}
                      onResend={selectedStaffMember?.chat_user_id ? () => handleResendInvite('staff', selectedStaffMember) : null}
                      onShowQr={selectedStaffMember?.chat_user_id ? () => setQrPerson({ person: selectedStaffMember, type: 'staff' }) : null}
                      resending={resendingInvite}
                      onActiveToggle={selectedStaffMember?.chat_user_id ? (v) => handleAccountActiveToggle('staff', selectedStaffMember, v, (s) => {
                        setSelectedStaffMember((p) => (p ? { ...p, chat_invite_status: s, account_enabled: true } : p));
                        setStaffForm((f) => ({ ...f, account_enabled: true, confirm_account_deactivation: false }));
                      }) : null}
                      statusBusy={accountStatusBusy}
                      pushEnabled={!!selectedStaffMember?.push_notifications_enabled}
                      onPushToggle={selectedStaffMember?.chat_user_id ? (v) => handleAccountPushToggle('staff', selectedStaffMember, v, (enabled) => {
                        setSelectedStaffMember((p) => (p ? { ...p, push_notifications_enabled: enabled } : p));
                      }) : null}
                      pushBusy={accountPushBusy}
                      isNotificationChannelActive={isNotificationChannelActive}
                      emailChecked={inviteEmail}
                      smsChecked={inviteSms}
                      onToggleEmail={setInviteEmail}
                      onToggleSms={setInviteSms}
                    />
                    )}
                    <div className="sm:col-span-2 border-t pt-3 mt-1">
                      <PersonAttachments personType="staff" person={selectedStaffMember} onChange={(atts) => applyPersonAttachments('staff', selectedStaffMember?.id, atts)} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button size="sm" variant="outline" onClick={() => handlePeopleDialogClose(() => { setIsStaffDialogOpen(false); setSelectedStaffMember(null); resetStaffForm(); })}>Cancel</Button>
                    <Button size="sm" 
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      onClick={selectedStaffMember ? handleEditStaff : handleCreateStaff}
                      data-testid={selectedStaffMember ? 'staff-update-submit-button' : 'staff-create-submit-button'}
                    >
                      {selectedStaffMember ? 'Update Staff' : 'Create Staff'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="staff-counts-row">
            {peopleLimits.staff !== -1 ? (
              <Badge
                variant={peopleLimits.staff > 0 && peopleCounts.staff >= peopleLimits.staff ? 'destructive' : 'outline'}
                title={peopleLimits.staff === 0 ? 'Upgrade required — contact your administrator to add Staff.' : 'Staff Limit'}
                data-testid="staff-limit-badge"
              >
                Staff Limit {peopleCounts.staff} / {peopleLimits.staff === 0 ? 'Upgrade Required' : peopleLimits.staff}
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="staff-total-badge">{peopleTotals.staff} Total</Badge>
            )}
          </div>

          <PeopleSelectionBar
            count={selectedStaffIds.length}
            total={peopleTotals.staff}
            busy={selectAllBusyTab === 'staff'}
            onToggleAll={() => handleSelectAllMatching('staff')}
            onClear={() => setSelectedStaffIds([])}
            prefix="staff"
          />

          {/* Staff Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle>All Staff Members</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <CurrentlyInFilterBadge
                    count={currentlyInCounts.staff}
                    active={staffInOnly}
                    onToggle={() => setStaffInOnly((v) => !v)}
                    testid="staff-currently-in-stat"
                  />
                  <LicenseExpiryFilterBadge
                    count={licenseAlertCounts.staff}
                    active={staffLicenseOnly}
                    onToggle={() => setStaffLicenseOnly((v) => !v)}
                    testid="staff-license-alerts-stat"
                  />
                  <DocsExpiryFilterBadge
                    count={docsAlertCounts.staff}
                    active={staffDocsOnly}
                    onToggle={() => setStaffDocsOnly((v) => !v)}
                    testid="staff-docs-alerts-stat"
                  />
                  <PendingInviteFilterBadge
                    count={pendingInviteCounts.staff}
                    active={staffPendingOnly}
                    onToggle={() => setStaffPendingOnly((v) => !v)}
                    testid="staff-pending-invites-stat"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isMobileOrWebview ? (
              <div className="space-y-2" data-testid="staff-card-list">
                {renderGroupedCards('staff', pagedStaff, (member) => renderPersonCard('staff', 'staff', member, {
                  selected: selectedStaffIds.includes(member.id),
                  onToggleSelect: () => handleToggleStaffSelection(member.id),
                  onEdit: () => openEditStaffDialog(member),
                  onDelete: () => handleDeleteStaff(member.id),
                  middle: (
                    <div className="pl-9 space-y-1.5">
                      <StaffRoleBadges member={member} large />
                      {(member.department || member.employee_id) && (
                        <p className="text-sm text-muted-foreground" data-testid={`staff-card-meta-${member.id}`}>
                          {member.department && <span className="capitalize">{member.department}</span>}
                          {member.department && member.employee_id && ' · '}
                          {member.employee_id}
                        </p>
                      )}
                    </div>
                  ),
                }))}
                {visibleStaff.length === 0 && !(groupedView.staff && collapsedGroups.staff.size > 0) && (
                  <p className="text-center py-8 text-sm text-muted-foreground" data-testid="staff-cards-empty">
                    {staffInOnly ? 'No one is currently in a vehicle.' : staffLicenseOnly ? 'No expired or expiring licenses. All clear.' : staffPendingOnly ? 'No pending sign-ups.' : 'No staff members found. Click "Add Staff" to create one.'}
                  </p>
                )}
              </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={staff.length > 0 && staff.every((m) => m.id && selectedStaffIds.includes(m.id))}
                        onCheckedChange={handleToggleAllStaff}
                      />
                    </TableHead>
                    <TableHead><SortHeader label="Staff Member" sortKey="name" sort={peopleSort} onSort={togglePeopleSort} testid="sort-staff-name" /></TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead><SortHeader label="Employee ID" sortKey="employee_id" sort={peopleSort} onSort={togglePeopleSort} testid="sort-staff-employee-id" /></TableHead>
                    <TableHead><SortHeader label="Status" sortKey="status" sort={peopleSort} onSort={togglePeopleSort} testid="sort-staff-status" /></TableHead>
                    {hasGreenDriving && <TableHead>Score</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderGroupedBody('staff', pagedStaff, 8, (member) => (
                    <TableRow key={member.id} className="hover:bg-muted/50">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedStaffIds.includes(member.id)}
                          onCheckedChange={() => handleToggleStaffSelection(member.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <PersonAvatar person={member} fallbackClass="bg-emerald-500 text-white" testid={`staff-avatar-${member.id}`} onClick={() => openProfile('staff', member)} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <button type="button" className="font-medium text-left hover:underline hover:text-[#2563eb]" onClick={() => openProfile('staff', member)} title="View profile" data-testid={`staff-name-btn-${member.id}`}>{member.full_name || member.name}</button>
                              <RowWarningIcon person={member} prefix="staff" />
                            </div>
                            <PersonIdentityTags person={member} prefix="staff" compact />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StaffRoleBadges member={member} />
                      </TableCell>
                      <TableCell>
                        {(member.email || member.phone) ? (
                          <div className="space-y-0.5">
                            {member.email && (
                              <div className="flex items-center gap-1 text-xs">
                                <Mail className="w-3 h-3 text-muted-foreground shrink-0" />
                                <span>{member.email}</span>
                              </div>
                            )}
                            {member.phone && (
                              <div className="flex items-center gap-1 text-xs">
                                <Phone className="w-3 h-3 text-muted-foreground shrink-0" />
                                <span>{member.phone}</span>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{member.employee_id || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`capitalize ${statusColors[member.status] || statusColors.inactive}`} title="Person status — set in the edit dialog.">
                            {member.status || 'inactive'}
                          </Badge>
                          {member.linked_user_id ? <LinkedAccountIcon testid={`staff-linked-badge-${member.id}`} /> : <ChatStatusIcon status={member.chat_invite_status} testid={`staff-chat-status-${member.id}`} />}
                          <DriverEligibleIcon person={member} />
                          <DocumentsChip person={member} prefix="staff" onClick={() => setAttachmentsViewer({ personType: 'staff', person: member })} />
                        </div>
                      </TableCell>
                      {hasGreenDriving && (
                        <TableCell>
                          <BehaviourScoreBadge person={member} map={behaviourMap} prefix="staff" />
                        </TableCell>
                      )}
                      <TableCell>
                        {renderPersonActions('staff', 'staff', member, () => openEditStaffDialog(member), () => handleDeleteStaff(member.id))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visibleStaff.length === 0 && !(groupedView.staff && collapsedGroups.staff.size > 0) && (
                    <TableRow>
                      <TableCell colSpan={hasGreenDriving ? 8 : 7} className="text-center py-8 text-muted-foreground">
                        {staffInOnly ? 'No one is currently in a vehicle.' : staffLicenseOnly ? 'No expired or expiring licenses. All clear.' : staffPendingOnly ? 'No pending sign-ups.' : 'No staff members found. Click "Add Staff" to create one.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              )}
              <PaginationFooter page={staffPage} setPage={setStaffPage} totalItems={peopleTotals.staff} itemsPerPage={peoplePageSize} setItemsPerPage={setPeoplePageSize} testIdPrefix="staff" alwaysShow />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ==================== PASSENGERS TAB ==================== */}
      {activeTab === 'passengers' && hasPassengers && (
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <UserCircle className="w-5 h-5 text-foreground" />
                <h2 className="text-xl font-semibold">Passengers</h2>
              </div>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Add, invite, and manage passenger accounts with device and beacon assignments.
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search passengers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-8 pl-10 pr-9"
                data-testid="passengers-search"
              />
              {searchQuery && (
                <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setSearchQuery('')} data-testid="passengers-clear-search-button"><X className="w-4 h-4" /></Button>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setGroupsModalOpen(true)} data-testid="passenger-manage-groups-button">
              <Settings2 className="w-4 h-4 mr-2" /> Manage Groups
            </Button>
            <Button variant={groupedView.passengers ? 'default' : 'outline'} size="sm" onClick={() => toggleGroupedView('passengers')} title={groupedView.passengers ? 'Switch to flat view' : 'Group passengers by their group'} data-testid="passenger-grouped-view-toggle">
              <Layers className="w-4 h-4 mr-2" /> Grouped
            </Button>
            <PeopleImportDropdown type="passengers" onOpenWizard={() => setImportWizardType('passengers')} />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" data-testid="export-passengers-btn" disabled={selectedPassengerIds.length === 0}>
                  <Upload className="w-4 h-4 mr-2" />
                  {selectedPassengerIds.length > 0 ? `Export (${selectedPassengerIds.length})` : 'Export'}
                  <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-60">
                <DropdownMenuLabel className="text-xs">Export Passengers</DropdownMenuLabel>
                <DropdownMenuItem onSelect={handleExportPassengersCSV} data-testid="export-passengers-csv">
                  <FileText className="w-4 h-4 mr-2" />
                  Passengers (.csv)
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleExportPassengersXlsx} data-testid="export-passengers-xlsx">
                  <FileSpreadsheet className="w-4 h-4 mr-2 text-[#2563eb]" />
                  Passengers Excel-Safe (.xlsx)
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={handleExportFullBackup('passengers')} data-testid="export-passengers-zip">
                  <Archive className="w-4 h-4 mr-2 text-[#2563eb]" />
                  Full Backup with photos (.zip)
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
              {selectedPassengerIds.length > 0 && (
                <>
                  <BulkActionsMenu
                    count={selectedPassengerIds.length}
                    onStatus={(s) => handleBulkStatus('passenger', selectedPassengerIds, s)}
                    onResend={() => handleBulkResendInvites('passenger', selectedPassengerIds)}
                    groups={peopleGroups.passengers}
                    onMoveToGroup={(gid) => handleBulkMoveToGroup('passenger', selectedPassengerIds, gid)}
                    testPrefix="passenger"
                  />
                  <Button variant="destructive" size="sm" onClick={handleDeleteSelectedPassengers}>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete ({selectedPassengerIds.length})
                  </Button>
                </>
              )}
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Dialog open={isPassengerDialogOpen} onOpenChange={(open) => {
                if (!open) {
                  handlePeopleDialogClose(() => { setIsPassengerDialogOpen(false); setSelectedPassengerRecord(null); resetPassengerForm(); });
                } else {
                  setIsPassengerDialogOpen(true);
                }
              }}>
                <span title={addBlocked.passengers ? addBlockedTitle('Passengers', peopleLimits.passengers) : undefined}>
                  <Button size="sm" className="bg-[#2563eb] hover:bg-[#1d4ed8] min-w-[170px] justify-center" disabled={addBlocked.passengers} onClick={() => setIsPassengerDialogOpen(true)} data-testid="add-passenger-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Passenger
                  </Button>
                </span>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{selectedPassengerRecord ? 'Edit Passenger' : 'Add New Passenger'}</DialogTitle>
                    <DialogDescription>Enter passenger information</DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Passenger ID</Label>
                        <Input
                          value={passengerForm.passenger_id}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, passenger_id: e.target.value }))}
                          placeholder="PAX001"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Full Name *</Label>
                        <Input
                          value={passengerForm.full_name}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, full_name: e.target.value }))}
                          placeholder="Robert Taylor"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          value={passengerForm.email}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="robert@example.com"
                          data-testid="passenger-email-input"
                        />
                        <EmailAvailabilityHint result={passengerEmailAvail} testid="passenger-email-availability" />
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input
                          value={passengerForm.phone}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, phone: e.target.value }))}
                          placeholder="+61 467 890 123"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Address</Label>
                      <Textarea
                        value={passengerForm.address}
                        onChange={(e) => setPassengerForm(prev => ({ ...prev, address: e.target.value }))}
                        placeholder="123 Oak Street, Sydney NSW 2000"
                        rows={2}
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Date of Birth</Label>
                      <Input
                        type="date"
                        value={passengerForm.date_of_birth}
                        onChange={(e) => setPassengerForm(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Emergency Contact Name</Label>
                        <Input
                          value={passengerForm.emergency_contact_name}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                          placeholder="Jane Taylor"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Emergency Contact Phone</Label>
                        <Input
                          value={passengerForm.emergency_contact_phone}
                          onChange={(e) => setPassengerForm(prev => ({ ...prev, emergency_contact_phone: e.target.value }))}
                          placeholder="+61 400 000 000"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Special Requirements</Label>
                      <Textarea
                        value={passengerForm.special_requirements}
                        onChange={(e) => setPassengerForm(prev => ({ ...prev, special_requirements: e.target.value }))}
                        placeholder="Wheelchair access, assistance needed, etc."
                        rows={2}
                      />
                    </div>

                    <PersonPhotoField api={api} personType="passenger" person={selectedPassengerRecord} onChange={(ph) => applyPersonPhoto('passenger', selectedPassengerRecord?.id, ph)} />

                    <div className="space-y-2">
                      <Label>Notes</Label>
                      <Textarea
                        value={passengerForm.notes}
                        onChange={(e) => setPassengerForm(prev => ({ ...prev, notes: e.target.value }))}
                        placeholder="Additional notes..."
                        rows={2}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <PersonStatusField value={passengerForm.status} onChange={(v) => setPassengerForm(prev => ({ ...prev, status: v }))} testPrefix="passenger" />
                      <PersonGroupField options={peopleGroups.passengers} value={passengerForm.person_group} onChange={(v) => setPassengerForm(prev => ({ ...prev, person_group: v }))} testPrefix="passenger" />
                    </div>

                    <PersonalBeaconField
                      value={passengerForm.personal_beacon_id}
                      onChange={(v) => setPassengerForm(prev => ({ ...prev, personal_beacon_id: v }))}
                      beacons={beaconInventory}
                      currentPersonId={selectedPassengerRecord?.id}
                      personType="passenger"
                      testPrefix="passenger"
                    />

                    <CredentialFields form={passengerForm} setForm={setPassengerForm} testPrefix="passenger" cards={availableRfidCardsFor(passengerForm)} />

                    <AccountAccessSection
                      form={passengerForm}
                      prefix="passenger"
                      personLabel="passenger"
                      inviteStatus={selectedPassengerRecord?.chat_invite_status}
                      onAccountToggle={(v) => handleAccountToggle(v, setPassengerForm, selectedPassengerRecord?.chat_invite_status, passengerForm.full_name)}
                      onChatToggle={(v) => { setPassengerForm(prev => ({ ...prev, messaging_enabled: v })); }}
                      onResend={selectedPassengerRecord?.chat_user_id ? () => handleResendInvite('passenger', selectedPassengerRecord) : null}
                      onShowQr={selectedPassengerRecord?.chat_user_id ? () => setQrPerson({ person: selectedPassengerRecord, type: 'passenger' }) : null}
                      onActiveToggle={selectedPassengerRecord?.chat_user_id ? (v) => handleAccountActiveToggle('passenger', selectedPassengerRecord, v, (s) => {
                        setSelectedPassengerRecord((p) => (p ? { ...p, chat_invite_status: s, account_enabled: true } : p));
                        setPassengerForm((f) => ({ ...f, account_enabled: true, confirm_account_deactivation: false }));
                      }) : null}
                      statusBusy={accountStatusBusy}
                      pushEnabled={!!selectedPassengerRecord?.push_notifications_enabled}
                      onPushToggle={selectedPassengerRecord?.chat_user_id ? (v) => handleAccountPushToggle('passenger', selectedPassengerRecord, v, (enabled) => {
                        setSelectedPassengerRecord((p) => (p ? { ...p, push_notifications_enabled: enabled } : p));
                      }) : null}
                      pushBusy={accountPushBusy}
                      resending={resendingInvite}
                      isNotificationChannelActive={isNotificationChannelActive}
                      emailChecked={inviteEmail}
                      smsChecked={inviteSms}
                      onToggleEmail={setInviteEmail}
                      onToggleSms={setInviteSms}
                    />
                    <div className="sm:col-span-2 border-t pt-3 mt-1">
                      <PersonAttachments personType="passenger" person={selectedPassengerRecord} onChange={(atts) => applyPersonAttachments('passenger', selectedPassengerRecord?.id, atts)} />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button size="sm" variant="outline" onClick={() => handlePeopleDialogClose(() => { setIsPassengerDialogOpen(false); setSelectedPassengerRecord(null); resetPassengerForm(); })}>Cancel</Button>
                    <Button size="sm" 
                      className="bg-[#2563eb] hover:bg-[#1d4ed8]"
                      onClick={selectedPassengerRecord ? handleEditPassenger : handleCreatePassenger}
                      data-testid={selectedPassengerRecord ? 'passenger-update-submit-button' : 'passenger-create-submit-button'}
                    >
                      {selectedPassengerRecord ? 'Update Passenger' : 'Create Passenger'}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-testid="passengers-counts-row">
            {peopleLimits.passengers !== -1 ? (
              <Badge
                variant={peopleLimits.passengers > 0 && peopleCounts.passengers >= peopleLimits.passengers ? 'destructive' : 'outline'}
                title={peopleLimits.passengers === 0 ? 'Upgrade required — contact your administrator to add Passengers.' : 'Passenger Limit'}
                data-testid="passengers-limit-badge"
              >
                Passenger Limit {peopleCounts.passengers} / {peopleLimits.passengers === 0 ? 'Upgrade Required' : peopleLimits.passengers}
              </Badge>
            ) : (
              <Badge variant="outline" data-testid="passengers-total-badge">{peopleTotals.passengers} Total</Badge>
            )}
          </div>

          <PeopleSelectionBar
            count={selectedPassengerIds.length}
            total={peopleTotals.passengers}
            busy={selectAllBusyTab === 'passengers'}
            onToggleAll={() => handleSelectAllMatching('passengers')}
            onClear={() => setSelectedPassengerIds([])}
            prefix="passengers"
          />

          {/* Passengers Table */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle>All Passengers</CardTitle>
                <div className="flex items-center gap-2 flex-wrap">
                  <CurrentlyInFilterBadge
                    count={currentlyInCounts.passengers}
                    active={passengerInOnly}
                    onToggle={() => setPassengerInOnly((v) => !v)}
                    testid="passenger-currently-in-stat"
                  />
                  <DocsExpiryFilterBadge
                    count={docsAlertCounts.passengers}
                    active={passengerDocsOnly}
                    onToggle={() => setPassengerDocsOnly((v) => !v)}
                    testid="passenger-docs-alerts-stat"
                  />
                  <PendingInviteFilterBadge
                    count={pendingInviteCounts.passengers}
                    active={passengerPendingOnly}
                    onToggle={() => setPassengerPendingOnly((v) => !v)}
                    testid="passenger-pending-invites-stat"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isMobileOrWebview ? (
              <div className="space-y-2" data-testid="passenger-card-list">
                {renderGroupedCards('passengers', pagedPassengers, (passenger) => renderPersonCard('passenger', 'passenger', passenger, {
                  selected: selectedPassengerIds.includes(passenger.id),
                  onToggleSelect: () => handleTogglePassengerSelection(passenger.id),
                  onEdit: () => openEditPassengerDialog(passenger),
                  onDelete: () => handleDeletePassenger(passenger.id),
                  middle: (passenger.address || passenger.passenger_id) ? (
                    <div className="pl-9 space-y-1">
                      {passenger.address && (
                        <p className="text-sm flex items-center gap-1.5">
                          <MapPin className="w-4 h-4 text-muted-foreground shrink-0" />
                          <span className="break-words">{passenger.address}</span>
                        </p>
                      )}
                      {passenger.passenger_id && <p className="text-sm text-muted-foreground">ID: {passenger.passenger_id}</p>}
                    </div>
                  ) : null,
                }))}
                {visiblePassengers.length === 0 && !(groupedView.passengers && collapsedGroups.passengers.size > 0) && (
                  <p className="text-center py-8 text-sm text-muted-foreground" data-testid="passenger-cards-empty">
                    {passengerInOnly ? 'No one is currently in a vehicle.' : passengerPendingOnly ? 'No pending sign-ups.' : 'No passengers found. Click "Add Passenger" to create one.'}
                  </p>
                )}
              </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={passengers.length > 0 && passengers.every((pp) => pp.id && selectedPassengerIds.includes(pp.id))}
                        onCheckedChange={handleToggleAllPassengers}
                      />
                    </TableHead>
                    <TableHead><SortHeader label="Passenger" sortKey="name" sort={peopleSort} onSort={togglePeopleSort} testid="sort-passenger-name" /></TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead><SortHeader label="Passenger ID" sortKey="passenger_id" sort={peopleSort} onSort={togglePeopleSort} testid="sort-passenger-id" /></TableHead>
                    <TableHead><SortHeader label="Status" sortKey="status" sort={peopleSort} onSort={togglePeopleSort} testid="sort-passenger-status" /></TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {renderGroupedBody('passengers', pagedPassengers, 6, (passenger) => (
                    <TableRow key={passenger.id} className="hover:bg-muted/50">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedPassengerIds.includes(passenger.id)}
                          onCheckedChange={() => handleTogglePassengerSelection(passenger.id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <PersonAvatar person={passenger} fallbackClass="bg-purple-500 text-white" testid={`passenger-avatar-${passenger.id}`} onClick={() => openProfile('passenger', passenger)} />
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <button type="button" className="font-medium text-left hover:underline hover:text-[#2563eb]" onClick={() => openProfile('passenger', passenger)} title="View profile" data-testid={`passenger-name-btn-${passenger.id}`}>{passenger.full_name || passenger.name}</button>
                              <RowWarningIcon person={passenger} prefix="passenger" />
                            </div>
                            <PersonIdentityTags person={passenger} prefix="passenger" compact />
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{renderContactCell(passenger)}</TableCell>
                      <TableCell>
                        <span className="text-sm">{passenger.passenger_id || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5">
                          <Badge variant="outline" className={`capitalize ${statusColors[passenger.status] || statusColors.inactive}`} title="Person status — set in the edit dialog.">
                            {passenger.status || 'inactive'}
                          </Badge>
                          {passenger.linked_user_id ? <LinkedAccountIcon testid={`passenger-linked-badge-${passenger.id}`} /> : <ChatStatusIcon status={passenger.chat_invite_status} testid={`passenger-chat-status-${passenger.id}`} />}
                          <DocumentsChip person={passenger} prefix="passenger" onClick={() => setAttachmentsViewer({ personType: 'passenger', person: passenger })} />
                        </div>
                      </TableCell>
                      <TableCell>
                        {renderPersonActions('passenger', 'passenger', passenger, () => openEditPassengerDialog(passenger), () => handleDeletePassenger(passenger.id))}
                      </TableCell>
                    </TableRow>
                  ))}
                  {visiblePassengers.length === 0 && !(groupedView.passengers && collapsedGroups.passengers.size > 0) && (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        {passengerInOnly ? 'No one is currently in a vehicle.' : passengerPendingOnly ? 'No pending sign-ups.' : 'No passengers found. Click "Add Passenger" to create one.'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              )}
              <PaginationFooter page={passengersPage} setPage={setPassengersPage} totalItems={peopleTotals.passengers} itemsPerPage={peoplePageSize} setItemsPerPage={setPeoplePageSize} testIdPrefix="passengers" alwaysShow />
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!pendingPeopleAction} onOpenChange={(open) => { if (!open) { setPendingPeopleAction(null); setPeopleDeleteConfirmText(''); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className={`w-5 h-5 ${pendingPeopleAction?.tone === 'warning' ? 'text-amber-500' : 'text-red-500'}`} />{pendingPeopleAction?.title}</DialogTitle>
            <DialogDescription>{pendingPeopleAction?.message}</DialogDescription>
          </DialogHeader>
          {pendingPeopleAction?.requireTyped && (
            <div className="space-y-3">
              <p className="text-sm">To confirm, type the following exactly:</p>
              <code className="block p-2 bg-muted rounded text-sm font-mono font-bold">DELETE</code>
              <Input
                value={peopleDeleteConfirmText}
                onChange={(e) => setPeopleDeleteConfirmText(e.target.value)}
                placeholder="Type confirmation text..."
                className="font-mono"
                autoFocus
                data-testid="people-delete-confirm-input"
              />
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" data-testid="people-confirm-cancel-button" onClick={() => { setPendingPeopleAction(null); setPeopleDeleteConfirmText(''); }}>Cancel</Button>
            <Button
              size="sm"
              variant={pendingPeopleAction?.tone === 'warning' ? 'default' : 'destructive'}
              className={pendingPeopleAction?.tone === 'warning' ? 'bg-amber-600 hover:bg-amber-700 text-white' : ''}
              data-testid="people-confirm-action-button"
              disabled={pendingPeopleAction?.requireTyped && peopleDeleteConfirmText !== 'DELETE'}
              onClick={async () => { await pendingPeopleAction?.action(); setPendingPeopleAction(null); setPeopleDeleteConfirmText(''); }}
            >{pendingPeopleAction?.confirmLabel || 'Delete'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unsaved Changes Warning */}
      <Dialog open={showPeopleUnsavedWarning} onOpenChange={setShowPeopleUnsavedWarning}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Unsaved Changes</DialogTitle>
            <DialogDescription>You have unsaved changes. Are you sure you want to leave? Your changes will be lost.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowPeopleUnsavedWarning(false)}>Stay</Button>
            <Button size="sm" variant="destructive" onClick={() => { if (unsavedCloseTarget) unsavedCloseTarget(); setShowPeopleUnsavedWarning(false); }}>Discard Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Account activation invite result (shown once after enabling Create Account) */}
      <Dialog open={!!chatInvite} onOpenChange={(open) => { if (!open) setChatInvite(null); }}>
        <DialogContent className="max-w-md" data-testid="chat-invite-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-brand-blue" />Account invitation sent
            </DialogTitle>
            <DialogDescription>
              We&apos;ve invited <span className="font-medium">{chatInvite?.person_name || 'this contact'}</span> to set up
              their own login. They&apos;ll choose a password and accept the Terms &amp; Privacy, then they can sign in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg border p-3 bg-muted/30 space-y-2" data-testid="chat-invite-delivery">
              {(() => {
                const d = chatInvite?.delivery || {};
                // Show only the channels that were actually attempted (present in
                // the delivery result), so an Email-only send doesn't list an SMS row.
                const targets = { email: chatInvite?.person_email, sms: chatInvite?.person_phone };
                const rows = Object.keys(d).map((ch) => [ch, ch === 'email' ? 'Email' : 'SMS', targets[ch], d[ch]]);
                if (!rows.length) return <p className="text-xs text-muted-foreground">No channel was sent — share the activation link below.</p>;
                const statusLabel = (s) => s === 'sent' ? 'Sent' : s === 'not_allowed' ? 'Channel off' : s === 'not_configured' ? 'Not configured' : s === 'no_recipient' ? 'No recipient' : (s === 'queued' || s === 'sending') ? 'Sending…' : s ? 'Failed' : 'Not sent';
                const statusColor = (s) => (s === 'sent' ? 'text-green-600' : (s === 'queued' || s === 'sending') ? 'text-blue-600' : 'text-amber-600');
                return rows.map(([key, label, target, status]) => (
                  <div key={key} className="flex items-center justify-between gap-2 text-sm">
                    <span className="flex items-center gap-1.5 min-w-0">
                      {key === 'email' ? <Mail className="w-3.5 h-3.5 text-muted-foreground shrink-0" /> : <MessageSquare className="w-3.5 h-3.5 text-muted-foreground shrink-0" />}
                      <span className="text-muted-foreground">{label}</span>
                      <span className="truncate text-xs">({target})</span>
                    </span>
                    <span className={`text-xs font-medium ${statusColor(status)}`} data-testid={`chat-invite-status-${key}`}>{statusLabel(status)}</span>
                  </div>
                ));
              })()}
            </div>
            {chatInvite?.invite_url && (
              <div className="rounded-lg border p-3 space-y-2">
                <p className="text-xs font-medium text-foreground flex items-center gap-1"><KeyRound className="w-3 h-3" />Activation link (valid 48h)</p>
                <div className="flex items-center gap-2">
                  <Input readOnly value={chatInvite.invite_url} className="h-8 text-xs font-mono" data-testid="chat-invite-link" />
                  <Button size="sm" variant="outline" className="h-8 shrink-0"
                    onClick={() => { navigator.clipboard.writeText(chatInvite.invite_url || ''); toast.success('Invitation link copied'); }}
                    data-testid="chat-invite-copy-link">
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground">Share this only if email/SMS didn&apos;t reach them. Resending generates a new link and invalidates this one.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button size="sm" onClick={() => setChatInvite(null)} data-testid="chat-invite-done-btn">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={lastLocationModal.open} onOpenChange={(open) => {
        setLastLocationModal(prev => ({ ...prev, open }));
        if (!open && returnToProfileRef.current) {
          const back = returnToProfileRef.current;
          returnToProfileRef.current = null;
          setProfileDrawer({ open: true, ...back });
        }
      }}>
        <DialogContent className="sm:max-w-xl" data-testid="personal-beacon-last-location-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5 text-teal-600" /> Last Observed Location
            </DialogTitle>
            <DialogDescription>Location is observed from BLE Beacon detections by GPS tracking devices.</DialogDescription>
          </DialogHeader>
          {lastLocationModal.loading ? (
            <div className="py-8 flex items-center justify-center" data-testid="personal-beacon-last-location-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          ) : lastLocationModal.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="personal-beacon-last-location-error">
              {lastLocationModal.error}
            </div>
          ) : lastLocationModal.data ? (
            <div className="space-y-4" data-testid="personal-beacon-last-location-content">
              <div className="rounded-lg border p-4">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs text-muted-foreground">Person</p>
                    <p className="font-semibold" data-testid="personal-beacon-last-location-person">{lastLocationModal.data.person?.full_name}</p>
                  </div>
                  <Badge variant="outline" className="capitalize">{lastLocationModal.data.person?.type}</Badge>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2 text-sm">
                  <div data-testid="personal-beacon-last-location-beacon">
                    <p className="text-xs text-muted-foreground">Beacon</p>
                    <p>{beaconDisplayName(lastLocationModal.data.beacon)}</p>
                  </div>
                  <div data-testid="personal-beacon-last-location-time">
                    <p className="text-xs text-muted-foreground">Last Seen</p>
                    <p>{formatDT(lastLocationModal.data.last_observed?.last_seen_at, { seconds: true })}</p>
                  </div>
                  <div data-testid="personal-beacon-last-location-detector">
                    <p className="text-xs text-muted-foreground">Last Seen By GPS Device</p>
                    <p>{lastLocationModal.data.last_observed?.last_seen_by?.device_name || '-'}</p>
                  </div>
                  <div data-testid="personal-beacon-last-location-rssi">
                    <p className="text-xs text-muted-foreground">RSSI / Confidence</p>
                    <p>{lastLocationModal.data.last_observed?.rssi_dbm != null ? `${lastLocationModal.data.last_observed.rssi_dbm} dBm` : '-'} · {lastLocationModal.data.last_observed?.confidence || 'low'}</p>
                  </div>
                  <div data-testid="personal-beacon-last-location-battery">
                    <p className="text-xs text-muted-foreground">Battery</p>
                    <p>{formatBeaconBattery(lastLocationModal.data.last_observed || {})}</p>
                  </div>
                </div>
              </div>
              <div className="rounded-lg border p-4" data-testid="personal-beacon-last-location-coordinates">
                <p className="text-xs text-muted-foreground">Observed Location</p>
                {lastLocationModal.data.last_observed?.last_location ? (
                  <div className="mt-1 flex items-center justify-between gap-3">
                    <p className="font-mono text-sm">
                      {lastLocationModal.data.last_observed.last_location.latitude}, {lastLocationModal.data.last_observed.last_location.longitude}
                    </p>
                    <a
                      href={`https://www.google.com/maps?q=${lastLocationModal.data.last_observed.last_location.latitude},${lastLocationModal.data.last_observed.last_location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-[#2563eb] hover:underline"
                      data-testid="personal-beacon-last-location-map-link"
                    >
                      <ExternalLink className="w-4 h-4" /> Map
                    </a>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">No receiver GPS location available yet.</p>
                )}
                <p className="mt-2 text-xs text-muted-foreground">Observed from detecting GPS device, not continuous personal GPS.</p>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={observedHistoryModal.open} onOpenChange={(open) => setObservedHistoryModal(prev => ({ ...prev, open }))}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="personal-beacon-observed-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-teal-600" /> Observed Beacon History
            </DialogTitle>
            <DialogDescription>
              BLE-observed presence history for this person. This is not continuous GPS tracking.
            </DialogDescription>
          </DialogHeader>
          {observedHistoryModal.loading ? (
            <div className="py-8 flex items-center justify-center" data-testid="personal-beacon-observed-history-loading">
              <Loader2 className="w-6 h-6 animate-spin text-[#2563eb]" />
            </div>
          ) : observedHistoryModal.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="personal-beacon-observed-history-error">
              {observedHistoryModal.error}
            </div>
          ) : observedHistoryModal.data ? (
            <div className="space-y-4" data-testid="personal-beacon-observed-history-content">
              <div className="rounded-lg border p-4 bg-background" data-testid="personal-beacon-observed-history-filters">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1"><Label>From</Label>
                    <div className="relative">
                    <Popover open={ohStartOpen} onOpenChange={setOhStartOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="observed-history-start-filter">
                          <span className="truncate text-left min-w-0 flex-1">{observedHistoryFilters.start_time ? formatDT(observedHistoryFilters.start_time, { seconds: false }) : 'Start date'}</span>
                          {!observedHistoryFilters.start_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={observedHistoryFilters.start_time || undefined} onSelect={(date) => { if (!date) return; const prev = observedHistoryFilters.start_time; date.setHours(prev?.getHours() || 0, prev?.getMinutes() || 0, 0, 0); setObservedHistoryFilters((p) => ({ ...p, start_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={observedHistoryFilters.start_time ? `${String(observedHistoryFilters.start_time.getHours()).padStart(2, '0')}:${String(observedHistoryFilters.start_time.getMinutes()).padStart(2, '0')}` : '00:00'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setObservedHistoryFilters((p) => { const d = new Date(p.start_time || new Date()); d.setHours(h || 0, m || 0, 0, 0); return { ...p, start_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="observed-history-start-time" />
                          {observedHistoryFilters.start_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setObservedHistoryFilters((p) => ({ ...p, start_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setOhStartOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {observedHistoryFilters.start_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setObservedHistoryFilters((p) => ({ ...p, start_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="observed-history-clear-start"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                  <div className="space-y-1"><Label>To</Label>
                    <div className="relative">
                    <Popover open={ohEndOpen} onOpenChange={setOhEndOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="observed-history-end-filter">
                          <span className="truncate text-left min-w-0 flex-1">{observedHistoryFilters.end_time ? formatDT(observedHistoryFilters.end_time, { seconds: false }) : 'End date'}</span>
                          {!observedHistoryFilters.end_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={observedHistoryFilters.end_time || undefined} onSelect={(date) => { if (!date) return; const prev = observedHistoryFilters.end_time; date.setHours(prev?.getHours() || 23, prev?.getMinutes() || 59, 59, 999); setObservedHistoryFilters((p) => ({ ...p, end_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={observedHistoryFilters.end_time ? `${String(observedHistoryFilters.end_time.getHours()).padStart(2, '0')}:${String(observedHistoryFilters.end_time.getMinutes()).padStart(2, '0')}` : '23:59'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setObservedHistoryFilters((p) => { const d = new Date(p.end_time || new Date()); d.setHours(h || 0, m || 0, 59, 999); return { ...p, end_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="observed-history-end-time" />
                          {observedHistoryFilters.end_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setObservedHistoryFilters((p) => ({ ...p, end_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setOhEndOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {observedHistoryFilters.end_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setObservedHistoryFilters((p) => ({ ...p, end_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="observed-history-clear-end"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-4">
                  <div className="space-y-1"><Label>Confidence</Label>
                    <Select value={observedHistoryFilters.confidence} onValueChange={(v) => setObservedHistoryFilters(prev => ({ ...prev, confidence: v }))}>
                      <SelectTrigger data-testid="observed-history-confidence-filter"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Min RSSI</Label>
                    <Input type="number" value={observedHistoryFilters.min_rssi} onChange={(e) => setObservedHistoryFilters(prev => ({ ...prev, min_rssi: e.target.value }))} placeholder="e.g. -75" className="h-9" data-testid="observed-history-min-rssi-filter" />
                  </div>
                  <div className="space-y-1"><Label>Max RSSI</Label>
                    <Input type="number" value={observedHistoryFilters.max_rssi} onChange={(e) => setObservedHistoryFilters(prev => ({ ...prev, max_rssi: e.target.value }))} placeholder="e.g. -45" className="h-9" data-testid="observed-history-max-rssi-filter" />
                  </div>
                  <div className="space-y-1"><Label>Limit</Label>
                    <Select value={String(observedHistoryFilters.limit)} onValueChange={(v) => setObservedHistoryFilters(prev => ({ ...prev, limit: Number(v) }))}>
                      <SelectTrigger data-testid="observed-history-limit-filter"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="50">50</SelectItem><SelectItem value="200">200</SelectItem><SelectItem value="500">500</SelectItem><SelectItem value="1000">1000</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_auto]">
                  <div className="relative">
                    <Input value={observedHistoryFilters.search} onChange={(e) => setObservedHistoryFilters(prev => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyObservedHistoryFilters(); } }} placeholder="Person, Beacon, vehicle or device" className="pr-9" data-testid="observed-history-search-filter" />
                    {observedHistoryFilters.search && <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setObservedHistoryFilters(prev => ({ ...prev, search: '' }))} data-testid="observed-history-clear-search-button"><X className="w-4 h-4" /></Button>}
                  </div>
                  <Button onClick={applyObservedHistoryFilters} data-testid="observed-history-apply-filters-button"><Search className="w-4 h-4 mr-1" /> Search</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="observed-history-export-button"><Upload className="w-4 h-4 mr-1" /> Export{selectedObsPoints.size ? ` (${selectedObsPoints.size})` : ''}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportAllObs('csv')} data-testid="observed-history-export-csv">CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAllObs('pdf')} data-testid="observed-history-export-pdf">PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEmailObs()} disabled={!isNotificationChannelActive('notification_email')} data-testid="observed-history-export-email"><Mail className="w-3.5 h-3.5 mr-2" /> Email PDF{!isNotificationChannelActive('notification_email') && <span className="ml-auto text-[10px] text-destructive">Restricted</span>}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="rounded-lg border p-4 bg-muted/20">
                <div className="grid gap-3 md:grid-cols-3 text-sm">
                  <div>
                    <p className="text-xs text-muted-foreground">Person</p>
                    <p className="font-semibold">{observedHistoryModal.data.person?.full_name}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Beacon</p>
                    <p>{beaconDisplayName(observedHistoryModal.data.beacon)}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Observations</p>
                    <p className="font-semibold">{observedHistoryModal.data.count || 0}</p>
                  </div>
                </div>
              </div>
              {(observedHistoryModal.data.hints || []).length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="personal-beacon-observed-history-hints">
                  {observedHistoryModal.data.hints.map((hint, idx) => <p key={idx}>{hint}</p>)}
                </div>
              )}
              {(observedHistoryModal.data.points || []).length > 0 && (() => {
                const pagePoints = observedHistoryModal.data.points || [];
                const obsStart = (obsPage - 1) * obsPageSize;
                const pageKeys = pagePoints.map((p, i) => obsPointKey(p, obsStart + i));
                const pageAllOn = pageKeys.length > 0 && pageKeys.every((k) => selectedObsPoints.has(k));
                return (
                <div ref={obsListTopRef} className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground pl-[15px]">
                    <Checkbox
                      checked={pageAllOn}
                      onCheckedChange={(checked) => setSelectedObsPoints((prev) => {
                        const next = new Set(prev);
                        pageKeys.forEach((k) => { if (checked) next.add(k); else next.delete(k); });
                        return next;
                      })}
                      data-testid="observed-history-select-all"
                    />
                    <span data-testid="observed-history-selection-count">{selectedObsPoints.size} selected</span>
                    {selectedObsPoints.size > 0 && (<>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={async () => {
                        if (selectedObsPoints.size >= obsTotal) { setSelectedObsPoints(new Set()); return; }
                        try { const all = await fetchAllObsPoints(); setSelectedObsPoints(new Set(all.map((p, i) => obsPointKey(p, i)))); }
                        catch { toast.error('Could not select all observations'); }
                      }} data-testid="observed-history-select-all-toggle">
                        {selectedObsPoints.size >= obsTotal ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedObsPoints(new Set())} data-testid="observed-history-clear-selection">Clear</Button>
                    </>)}
                  </div>
                  <Button variant="outline" size="sm" onClick={applyObservedHistoryFilters} data-testid="observed-history-refresh-button"><RefreshCw className={`w-4 h-4 mr-2 ${observedHistoryModal.loading ? 'animate-spin' : ''}`} /> Refresh</Button>
                </div>
                );
              })()}
              {(observedHistoryModal.data.points || []).map((point, i) => {
                const idx = (obsPage - 1) * obsPageSize + i;
                const key = obsPointKey(point, idx);
                const hasLoc = point.latitude != null && point.longitude != null;
                return (
                  <div key={key} className={`rounded-xl border p-3.5 space-y-3 bg-white dark:bg-slate-800/50 shadow-[0_2px_8px_rgba(15,23,42,0.08)] dark:shadow-none transition-shadow ${selectedObsPoints.has(key) ? 'border-[#2563eb] ring-2 ring-[#2563eb]/40' : 'border-slate-300 dark:border-slate-700 hover:shadow-md'}`} data-testid={`personal-beacon-observed-history-point-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Checkbox checked={selectedObsPoints.has(key)} onCheckedChange={() => toggleObsSelection(key)} className="mt-1" data-testid={`observed-history-point-select-${idx}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Bluetooth className="w-4 h-4 text-[#2563eb] shrink-0" />
                            <p className="font-semibold text-sm" data-testid={`observed-history-point-title-${idx}`}>{formatDT(point.timestamp, { seconds: true })}</p>
                            <Badge variant="outline" className={`capitalize ${confidenceBadgeClass(point.confidence)}`}>{point.confidence || 'low'}</Badge>
                            <Badge variant="secondary" data-testid={`personal-beacon-observed-history-freshness-${idx}`}>{freshnessLabel(point.timestamp)}</Badge>
                          </div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{point.seen_near_label || `Seen by ${point.detected_by?.device_name || point.detected_by?.device_imei || '-'}`}</p>
                          {hasLoc && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-[11px]">
                              <a href={`https://www.google.com/maps?q=${point.latitude},${point.longitude}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open in Google Maps" data-testid={`personal-beacon-observed-history-map-link-${idx}`}><MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">Location:</span> {Number(point.latitude).toFixed(5)}, {Number(point.longitude).toFixed(5)}</a>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                        <p className="font-semibold text-orange-600 dark:text-orange-400" data-testid={`observed-history-point-rssi-${idx}`}>{point.rssi_dbm != null ? `${point.rssi_dbm} dBm` : '\u2014'}</p>
                        <div className="flex items-center justify-end gap-1 pt-0.5">
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logObsExport('csv', 1); exportObsPointCsv(point, observedHistoryModal.data.person?.full_name, beaconDisplayName(observedHistoryModal.data.beacon), idx); }} data-testid={`observed-history-point-csv-${idx}`}><Upload className="w-3 h-3 mr-1" /> CSV</Button>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logObsExport('pdf', 1); exportObsPointPdf(point, observedHistoryModal.data.person?.full_name, beaconDisplayName(observedHistoryModal.data.beacon), idx).catch(() => toast.error('Could not generate PDF')); }} data-testid={`observed-history-point-pdf-${idx}`}><Upload className="w-3 h-3 mr-1" /> PDF</Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2.5 py-2 text-[11px] text-muted-foreground">
                      <span>Battery: {formatBeaconBattery(point)}</span>
                      <span>Source: {point.location_source || 'detection only'}</span>
                    </div>
                  </div>
                );
              })}
              {(observedHistoryModal.data.points || []).length === 0 && (
                <div className="rounded-lg border border-dashed p-5 text-center text-sm text-muted-foreground" data-testid="personal-beacon-observed-history-empty">No observed Beacon history is available for this person yet.</div>
              )}
              {(() => {
                if (obsTotal === 0) return null;
                return (
                  <PaginationFooter
                    page={obsPage}
                    setPage={setObsPage}
                    totalItems={obsTotal}
                    itemsPerPage={obsPageSize}
                    setItemsPerPage={setObsPageSize}
                    testIdPrefix="observed-history"
                    scrollTargetRef={obsListTopRef}
                    alwaysShow
                  />
                );
              })()}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={logbookModal.open} onOpenChange={(open) => {
        setLogbookModal(prev => ({ ...prev, open }));
        if (!open && returnToProfileRef.current) {
          const back = returnToProfileRef.current;
          returnToProfileRef.current = null;
          setProfileDrawer({ open: true, ...back });
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="person-logbook-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><BookOpen className="w-5 h-5 text-teal-600" /> Person Log Book</DialogTitle>
            <DialogDescription>Vehicles this person was bound to as Driver, Staff, or Passenger.</DialogDescription>
          </DialogHeader>
          {logbookModal.loading ? (
            <div className="py-8 flex justify-center" data-testid="person-logbook-loading"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>
          ) : logbookModal.error ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800" data-testid="person-logbook-error">{logbookModal.error}</div>
          ) : logbookModal.data ? (
            <div className="space-y-4" data-testid="person-logbook-content">
              <div className="rounded-lg border p-4 bg-background" data-testid="person-logbook-filters">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-1"><Label>From</Label>
                    <div className="relative">
                    <Popover open={lbStartOpen} onOpenChange={setLbStartOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="person-logbook-start-filter">
                          <span className="truncate text-left min-w-0 flex-1">{logbookFilters.start_time ? formatDT(logbookFilters.start_time, { seconds: false }) : 'Start date'}</span>
                          {!logbookFilters.start_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={logbookFilters.start_time || undefined} onSelect={(date) => { if (!date) return; const prev = logbookFilters.start_time; date.setHours(prev?.getHours() || 0, prev?.getMinutes() || 0, 0, 0); setLogbookFilters((p) => ({ ...p, start_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={logbookFilters.start_time ? `${String(logbookFilters.start_time.getHours()).padStart(2, '0')}:${String(logbookFilters.start_time.getMinutes()).padStart(2, '0')}` : '00:00'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setLogbookFilters((p) => { const d = new Date(p.start_time || new Date()); d.setHours(h || 0, m || 0, 0, 0); return { ...p, start_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="person-logbook-start-time" />
                          {logbookFilters.start_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setLogbookFilters((p) => ({ ...p, start_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setLbStartOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {logbookFilters.start_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setLogbookFilters((p) => ({ ...p, start_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="person-logbook-clear-start"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                  <div className="space-y-1"><Label>To</Label>
                    <div className="relative">
                    <Popover open={lbEndOpen} onOpenChange={setLbEndOpen}>
                      <PopoverTrigger asChild>
                        <Button variant="outline" className="w-full h-8 text-xs font-normal justify-between pr-8" data-testid="person-logbook-end-filter">
                          <span className="truncate text-left min-w-0 flex-1">{logbookFilters.end_time ? formatDT(logbookFilters.end_time, { seconds: false }) : 'End date'}</span>
                          {!logbookFilters.end_time && <CalendarIcon className="w-3.5 h-3.5 ml-2 opacity-50 shrink-0" />}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[10000]" align="start">
                        <Calendar mode="single" selected={logbookFilters.end_time || undefined} onSelect={(date) => { if (!date) return; const prev = logbookFilters.end_time; date.setHours(prev?.getHours() || 23, prev?.getMinutes() || 59, 59, 999); setLogbookFilters((p) => ({ ...p, end_time: date })); }} />
                        <div className="border-t p-3 flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 text-muted-foreground" />
                          <Input type="time" value={logbookFilters.end_time ? `${String(logbookFilters.end_time.getHours()).padStart(2, '0')}:${String(logbookFilters.end_time.getMinutes()).padStart(2, '0')}` : '23:59'} onChange={(e) => { const [h, m] = e.target.value.split(':').map(Number); setLogbookFilters((p) => { const d = new Date(p.end_time || new Date()); d.setHours(h || 0, m || 0, 59, 999); return { ...p, end_time: d }; }); }} className="h-8 text-sm flex-1" data-testid="person-logbook-end-time" />
                          {logbookFilters.end_time && <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setLogbookFilters((p) => ({ ...p, end_time: null }))}>Clear</Button>}
                          <Button size="sm" className="h-8 text-xs" onClick={() => setLbEndOpen(false)}>Done</Button>
                        </div>
                      </PopoverContent>
                    </Popover>
                    {logbookFilters.end_time && (
                      <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.stopPropagation(); setLogbookFilters((p) => ({ ...p, end_time: null })); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="person-logbook-clear-end"><X className="w-3.5 h-3.5" /></button>
                    )}
                    </div>
                  </div>
                  <LogbookQuickRange
                    className="md:col-span-2"
                    startTime={logbookFilters.start_time}
                    endTime={logbookFilters.end_time}
                    onSelect={(start) => {
                      const next = { ...logbookFilters, start_time: start, end_time: null };
                      setLogbookFilters(next);
                      if (logbookTarget) openPersonLogbook(logbookTarget.personType, logbookTarget.person, next);
                    }}
                    testIdPrefix="person-logbook"
                  />
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-3">
                  <div className="space-y-1"><Label>Source</Label>
                    <Select value={logbookFilters.source} onValueChange={(v) => setLogbookFilters(prev => ({ ...prev, source: v }))}>
                      <SelectTrigger data-testid="person-logbook-source-filter"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all" data-testid="person-logbook-source-all">All</SelectItem><SelectItem value="rfid_ibutton" data-testid="person-logbook-source-rfid">RFID / iButton</SelectItem><SelectItem value="ble" data-testid="person-logbook-source-ble">BLE Beacon</SelectItem><SelectItem value="manager_assignment" data-testid="person-logbook-source-manager">Manager assignment</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Event</Label>
                    <Select value={logbookFilters.event_type} onValueChange={(v) => setLogbookFilters(prev => ({ ...prev, event_type: v }))}>
                      <SelectTrigger data-testid="person-logbook-event-filter"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="Trip">Bound trips</SelectItem><SelectItem value="Mid-trip Driver Scan Ignored">Ignored Mid-trip Scan</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1"><Label>Signal</Label>
                    <Select value={logbookFilters.signal} onValueChange={(v) => setLogbookFilters(prev => ({ ...prev, signal: v }))}>
                      <SelectTrigger data-testid="person-logbook-signal-filter"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="all">All signals</SelectItem><SelectItem value="strong">Strong</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-[1fr_auto_auto]">
                  <div className="relative">
                    <Input value={logbookFilters.search} onChange={(e) => setLogbookFilters(prev => ({ ...prev, search: e.target.value }))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyPersonLogbookFilters(); } }} placeholder="Search vehicle, credential, event" className="h-8 pr-9" data-testid="person-logbook-search-filter" />
                    {logbookFilters.search && <Button type="button" variant="ghost" size="sm" className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0" onClick={() => setLogbookFilters(prev => ({ ...prev, search: '' }))} data-testid="person-logbook-clear-search-button"><X className="w-4 h-4" /></Button>}
                  </div>
                  <Button onClick={applyPersonLogbookFilters} data-testid="person-logbook-apply-filters-button"><Search className="w-4 h-4 mr-1" /> Search</Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" data-testid="person-logbook-export-button"><Upload className="w-4 h-4 mr-1" /> Export{selectedLogEntries.size ? ` (${selectedLogEntries.size})` : ''}</Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => exportAllPersonEntries('csv')} data-testid="person-logbook-export-csv">CSV</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => exportAllPersonEntries('pdf')} data-testid="person-logbook-export-pdf">PDF</DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEmailPersonEntries()} disabled={!isNotificationChannelActive('notification_email')} data-testid="person-logbook-export-email"><Mail className="w-3.5 h-3.5 mr-2" /> Email PDF{!isNotificationChannelActive('notification_email') && <span className="ml-auto text-[10px] text-destructive">Restricted</span>}</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
              <div className="rounded-lg border p-4 bg-muted/20 grid gap-3 md:grid-cols-3 text-sm">
                <div><p className="text-xs text-muted-foreground">Person</p><p className="font-semibold">{logbookModal.data.person?.full_name}</p></div>
                <div><p className="text-xs text-muted-foreground">Entries</p><p className="font-semibold" data-testid="person-logbook-entry-count">{logbookTotal}</p></div>
                <div><p className="text-xs text-muted-foreground">Sources</p><p data-testid="person-logbook-sources">{Array.from(new Set((logbookModal.data.entries || []).map(e => e.source_label).filter(Boolean))).join(', ') || '—'}</p></div>
              </div>
              <div className="flex flex-wrap justify-end gap-2" data-testid="person-logbook-shortcuts">
                <Button variant="outline" size="sm" onClick={openObservedHistoryFromLogbook} data-testid="person-logbook-open-observed-history">
                  <Bluetooth className="w-4 h-4 mr-1" /> Observed History
                </Button>
              </div>
              {(logbookModal.data.hints || []).map((hint, idx) => <div key={idx} className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-200/90">{hint}</div>)}
              {(logbookModal.data.entries || []).length > 0 && (() => {
                const pageEntriesLb = logbookModal.data.entries || [];
                const lbStart = (logEntryPage - 1) * logEntryPageSize;
                const pageKeys = pageEntriesLb.map((e, i) => logEntryKey(e, lbStart + i));
                const pageAllOn = pageKeys.length > 0 && pageKeys.every((k) => selectedLogEntries.has(k));
                return (
                <div ref={logbookListTopRef} className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground pl-[15px]">
                    <Checkbox
                      checked={pageAllOn}
                      onCheckedChange={(checked) => setSelectedLogEntries((prev) => {
                        const next = new Set(prev);
                        pageKeys.forEach((k) => { if (checked) next.add(k); else next.delete(k); });
                        return next;
                      })}
                      data-testid="person-logbook-select-all"
                    />
                    <span data-testid="person-logbook-selection-count">{selectedLogEntries.size} selected</span>
                    {selectedLogEntries.size > 0 && (<>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={async () => {
                        if (selectedLogEntries.size >= logbookTotal) { setSelectedLogEntries(new Set()); return; }
                        try { const ids = await fetchAllPersonLogEntryIds(); setSelectedLogEntries(new Set(ids.map((id, i) => id || `entry-${i}`))); }
                        catch { toast.error('Could not select all entries'); }
                      }} data-testid="person-logbook-select-all-toggle">
                        {selectedLogEntries.size >= logbookTotal ? 'Deselect All' : 'Select All'}
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setSelectedLogEntries(new Set())} data-testid="person-logbook-clear-selection">Clear</Button>
                    </>)}
                  </div>
                  <Button variant="outline" size="sm" onClick={applyPersonLogbookFilters} data-testid="person-logbook-refresh-button"><RefreshCw className={`w-4 h-4 mr-2 ${logbookModal.loading ? 'animate-spin' : ''}`} /> Refresh</Button>
                </div>
                );
              })()}
              {(logbookModal.data.entries || []).map((entry, i) => {
                const idx = (logEntryPage - 1) * logEntryPageSize + i;
                const key = logEntryKey(entry, idx);
                const isTrip = entry.event_type === 'Trip';
                const roleK = String(entry.role || entry.person_type || '').toLowerCase();
                const RoleIcon = isTrip ? Route : (entry.event_type === 'observed' ? Bluetooth : Ban);
                const startUrl = tripLocMapsUrl(entry.start_location);
                const endUrl = tripLocMapsUrl(entry.end_location);
                return (
                  <div key={key} className={`rounded-xl border p-3.5 space-y-3 bg-white dark:bg-slate-800/50 shadow-[0_2px_8px_rgba(15,23,42,0.08)] dark:shadow-none transition-shadow ${selectedLogEntries.has(key) ? 'border-[#2563eb] ring-2 ring-[#2563eb]/40' : 'border-slate-300 dark:border-slate-700 hover:shadow-md'}`} data-testid={`person-logbook-entry-${idx}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start gap-2 min-w-0 flex-1">
                        <Checkbox checked={selectedLogEntries.has(key)} onCheckedChange={() => toggleLogEntrySelection(key)} className="mt-1" data-testid={`person-logbook-entry-select-${idx}`} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <RoleIcon className="w-4 h-4 text-[#2563eb] shrink-0" />
                            {isTrip ? (
                              <p className="font-semibold text-sm" data-testid={`person-logbook-entry-title-${idx}`}>{entry.vehicle_name || 'Vehicle'} <span className="text-muted-foreground font-normal">· Bound {formatDT(entry.started_at, { seconds: true })} {'\u2192'} {entry.is_active ? 'Now' : formatDT(entry.ended_at, { seconds: true })}</span></p>
                            ) : (
                              <p className="font-semibold text-sm" data-testid={`person-logbook-entry-title-${idx}`}>{entry.summary}</p>
                            )}
                            {roleK && (<span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ${OCCUPANCY_BADGE_CLS[roleK] || ''}`}>{roleLabel(entry.role || entry.person_type)}</span>)}
                            {isTrip && entry.is_active ? (<Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300" data-testid={`person-logbook-entry-active-${idx}`}>Ongoing</Badge>) : null}
                          </div>
                          {!isTrip && (<p className="text-[11px] text-muted-foreground mt-0.5">{formatDT(entry.timestamp, { seconds: true })}</p>)}
                          {isTrip && entry.allocation_note && (
                            <p className="text-[11px] text-[#2563eb] dark:text-blue-300 mt-0.5" data-testid={`person-logbook-allocation-note-${idx}`}>{entry.allocation_note}</p>
                          )}
                          {(startUrl || endUrl) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-0.5 mt-1 text-[11px]" data-testid={`person-logbook-entry-locations-${idx}`}>
                              {startUrl && (<a href={startUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open in Google Maps" data-testid={`person-logbook-entry-start-loc-${idx}`}><MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">{isTrip ? 'Start:' : 'Location:'}</span> {fmtCoords(entry.start_location)}</a>)}
                              {endUrl && (<a href={endUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-[#2563eb] hover:underline" title="Open in Google Maps" data-testid={`person-logbook-entry-end-loc-${idx}`}><MapPin className="w-3 h-3 shrink-0" /> <span className="text-muted-foreground">End:</span> {fmtCoords(entry.end_location)}</a>)}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right text-xs text-muted-foreground shrink-0 space-y-1">
                        {isTrip && entry.duration_seconds != null && (<p className="flex items-center justify-end gap-1" data-testid={`person-logbook-entry-duration-${idx}`}><Clock className="w-3 h-3" /> {formatDuration(entry.duration_seconds)}</p>)}
                        {isTrip && entry.distance_km != null && (<p className="flex items-center justify-end gap-1" data-testid={`person-logbook-entry-distance-${idx}`}><Route className="w-3 h-3" /> {formatDistance(entry.distance_km, { decimals: 1 })}</p>)}
                        {isTrip && entry.vehicle_device_id && (
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => openVehicleTripFromPersonLogbook(entry)} data-testid={`person-logbook-open-vehicle-trip-${idx}`}>
                            <Route className="w-3 h-3 mr-1" /> Vehicle trip
                          </Button>
                        )}
                        <div className="flex items-center justify-end gap-1 pt-0.5">
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logPersonExport('csv', 1); exportEntryCsv(entry, logbookModal.data.person?.full_name, idx); }} data-testid={`person-logbook-entry-csv-${idx}`}><Upload className="w-3 h-3 mr-1" /> CSV</Button>
                          <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={() => { logPersonExport('pdf', 1); exportEntryPdf(entry, logbookModal.data.person?.full_name, idx).catch(() => toast.error('Could not generate PDF')); }} data-testid={`person-logbook-entry-pdf-${idx}`}><Upload className="w-3 h-3 mr-1" /> PDF</Button>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 px-2.5 py-2">
                      <div className="flex items-center gap-2 text-xs text-muted-foreground min-w-0">
                        <Badge variant="outline" className="flex items-center gap-1 shrink-0">{entry.is_ble ? <Bluetooth className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}{entry.source_label || entry.credential_source}</Badge>
                        {entry.signal_level && (<Badge className={`shrink-0 ${SIGNAL_BADGE_CLS[entry.signal_level] || ''}`} data-testid={`person-logbook-entry-signal-${idx}`}>{signalLabel(entry.signal_level)}</Badge>)}
                        <span className="font-mono break-all">{entry.credential_identifier || '-'}</span>
                      </div>
                      {isTrip ? (
                        <span className="text-[11px] text-muted-foreground">Boarded {formatDT(entry.started_at, { seconds: true })}{entry.is_active ? ' · still onboard' : <> {'\u2192'} Left {formatDT(entry.ended_at, { seconds: true })}</>}</span>
                      ) : entry.is_ble ? (
                        <span className="text-[11px] text-muted-foreground">{entry.confidence ? `Confidence: ${entry.confidence}` : ''}{entry.rssi_dbm != null ? ` · RSSI ${entry.rssi_dbm} dBm` : ''}{entry.battery_voltage_mv != null ? ` · ${entry.battery_voltage_mv} mV` : ''}</span>
                      ) : (
                        <span className="text-[11px] text-muted-foreground">{entry.vehicle_name ? `Vehicle: ${entry.vehicle_name}` : ''}</span>
                      )}
                    </div>
                  </div>
                );
              })}
              {(logbookModal.data.entries || []).length === 0 && (
                <div className="rounded-lg border border-dashed p-5 text-center text-sm text-muted-foreground" data-testid="person-logbook-empty">No log book entries are available for this person yet.</div>
              )}
              {(() => {
                if (logbookTotal === 0) return null;
                return (
                  <PaginationFooter
                    page={logEntryPage}
                    setPage={setLogEntryPage}
                    totalItems={logbookTotal}
                    itemsPerPage={logEntryPageSize}
                    setItemsPerPage={setLogEntryPageSize}
                    testIdPrefix="person-logbook"
                    scrollTargetRef={logbookListTopRef}
                    alwaysShow
                  />
                );
              })()}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      <EmailReportModal open={emailModal.open} onOpenChange={(open) => setEmailModal((p) => ({ ...p, open }))} api={api} ctx={emailModal.ctx} />
      <AttachmentsViewerDialog
        open={!!attachmentsViewer}
        onOpenChange={(o) => { if (!o) setAttachmentsViewer(null); }}
        title="Documents"
        subtitle={attachmentsViewer ? `${attachmentsViewer.person.full_name || ''} (${attachmentsViewer.personType})` : ''}
        attachments={attachmentsViewer?.person?.attachments || []}
        testPrefix="person-attachments"
      />
      <ManagePeopleGroupsModal
        open={groupsModalOpen}
        onOpenChange={setGroupsModalOpen}
        api={api}
        tab={activeTab}
        onSaved={fetchData}
      />

      <PeopleImportWizard
        open={!!importWizardType}
        personType={importWizardType || 'drivers'}
        api={api}
        existingPeople={importWizardType === 'staff' ? staff : importWizardType === 'passengers' ? passengers : drivers}
        groups={peopleGroups[importWizardType] || []}
        onClose={() => setImportWizardType(null)}
        onImported={() => fetchData()}
      />
      <PersonInviteQrModal
        open={!!qrPerson}
        onClose={() => setQrPerson(null)}
        api={api}
        person={qrPerson?.person}
        personType={qrPerson?.type || 'driver'}
      />
      <PersonProfileDrawer
        open={profileDrawer.open}
        onOpenChange={(o) => setProfileDrawer((p) => ({ ...p, open: o }))}
        person={profileDrawer.person}
        personType={profileDrawer.personType}
        groups={peopleGroups[{ driver: 'drivers', staff: 'staff', passenger: 'passengers' }[profileDrawer.personType]] || []}
        api={api}
        onChanged={fetchData}
        onEdit={() => {
          const fn = { driver: openEditDriverDialog, staff: openEditStaffDialog, passenger: openEditPassengerDialog }[profileDrawer.personType];
          setProfileDrawer((p) => ({ ...p, open: false }));
          fn?.(profileDrawer.person);
        }}
        onLogBook={() => {
          returnToProfileRef.current = { person: profileDrawer.person, personType: profileDrawer.personType };
          setProfileDrawer((p) => ({ ...p, open: false }));
          openPersonLogbook(profileDrawer.personType, profileDrawer.person);
        }}
        onLocation={() => {
          returnToProfileRef.current = { person: profileDrawer.person, personType: profileDrawer.personType };
          setProfileDrawer((p) => ({ ...p, open: false }));
          openLastLocation(profileDrawer.personType, profileDrawer.person);
        }}
        onObservedHistory={() => {
          returnToProfileRef.current = { person: profileDrawer.person, personType: profileDrawer.personType };
          setProfileDrawer((p) => ({ ...p, open: false }));
          openObservedHistory(profileDrawer.personType, profileDrawer.person);
        }}
      />
    </div>
  );
}

export default People;
